import { useTranslation, type Translate } from '@client-app/i18n';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
    ArrowRight,
    CaretDown,
    CaretLeft,
    CaretRight,
    Check,
    Clock,
    CircleNotch,
    DownloadSimple,
    HandPalm,
    ListChecks,
    MagnifyingGlass,
    PencilSimpleLine,
    Power,
    Question,
    SquaresFour,
    Target,
    Users,
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { isWorkspaceSubscriptionExpired } from '@/contexts/AuthContext/workspaceSubscriptionUtils';
import metaAdsIcon from '@client-app/assets/integration-icons/meta-ads.png';
import shopifyIcon from '@client-app/assets/integration-icons/shopify.svg';
import googleAdsIcon from '@client-app/assets/integration-icons/google-ads.png';
import LibraryIcon from '@common/components/Icon';
import Tooltip from '@common/components/Tooltip';
import { snapdom } from '@zumer/snapdom';
import jsPDF from 'jspdf';
import { valueLedgerService, type ValueLedgerApiLedger } from '@common/api/valueLedgerService';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Modal from '@/components/Settings/components/Modal';
import PermissionDeniedNotice from '@/components/PermissionDeniedNotice/PermissionDeniedNotice';
import {
    canManageOutcomeSummary,
    canViewOutcomeSummary,
} from './outcomeSummaryAccess';
import { formatOutcomeSummaryRefreshDisplayTime } from './outcomeSummaryRefreshDisplay';
import { useOutcomeSummaryRealtimeInvalidation } from './useOutcomeSummaryRealtimeInvalidation';
import { useOutcomeSummarySettings } from './useOutcomeSummarySettings';

import {
    adaptValueLedgerApiOutcome,
    adaptValueLedgerApiResponse,
    type ValueLedgerDesignData,
    type ValueLedgerEvidenceSystem,
    type ValueLedgerOverviewInsights,
    type ValueLedgerOverviewTaskScenario,
    type ValueLedgerOutcome,
    type ValueLedgerTask,
} from './valueLedgerDataAdapter';

type LedgerDetailTab = 'overview' | 'evidence' | 'source-task';
type TraceConversationMessage = NonNullable<ValueLedgerOutcome['conversationExcerpts']>[number];
type LaborLaneKey = 'execute' | 'judge' | 'watch' | 'unblock';
type OutcomePaginationItem = number | 'ellipsis-start' | 'ellipsis-end';
type EvidenceSystemVisual = {
    key: string;
    label: string;
    iconName: string;
    isBrandIcon: boolean;
    imageUrl?: string;
    operationCounts?: EvidenceSystemOperationCounts;
};
type EvidenceSystemOperationCounts = {
    read?: number;
    write?: number;
    check?: number;
    other?: number;
    total?: number;
};
type EstablishmentConditionStep = {
    key: 'business-object' | 'system-evidence' | 'completion-result';
    label: string;
    value: React.ReactNode;
    iconName: string;
};
type LaborLane = {
    key: LaborLaneKey;
    zhTitle: string;
    enTitle: string;
    definition: Array<{ key: 'definition' | 'conditions' | 'excluded' | 'dispute'; zh: string; en: string }>;
};
type ScrollContainerSnapshot = { element: HTMLElement; scrollTop: number; scrollLeft: number };
type ListScrollSnapshot = {
    windowScrollX: number;
    windowScrollY: number;
    containers: ScrollContainerSnapshot[];
};
type OutcomeOverviewMetricTone = 'blue' | 'green' | 'amber' | 'neutral';
type OutcomeOverviewMetricCard = {
    key: string;
    label: string;
    value: string;
    tone: OutcomeOverviewMetricTone;
    icon: React.ReactNode;
};
type OutcomeOverviewTaskAreaRow = {
    key: string;
    scenarioLabel: string | null;
    taskCount: number | null;
    activeMemberCount: number | null;
    representativeWork: string | null;
};
type OutcomeInsightCardItem = {
    key: string;
    title: string | null;
    description: string | null;
};
type OutcomeChampionInsightItem = {
    key: string;
    name: string | null;
    taskCount: number | null;
    focus: string | null;
};

const scrollableOverflowPattern = /(auto|scroll|overlay)/;
const OUTCOME_SUMMARY_ITEMS_PER_PAGE = 20;
const OUTCOME_SUMMARY_GENERATION_POLL_INTERVAL_MS = 15000;
const OUTCOME_SUMMARY_INSIGHT_PLACEHOLDER_COUNT = 5;
const OUTCOME_SUMMARY_CHAMPION_PLACEHOLDER_COUNT = 4;
const OUTCOME_SUMMARY_NEXT_FOCUS_PLACEHOLDER_COUNT = 3;
const MAX_VISIBLE_OUTCOME_PAGE_BUTTONS = 5;
const PDF_EXPORT_WIDTH_PX = 794;
const PDF_EXPORT_PAGE_WIDTH_MM = 210;
const PDF_EXPORT_PAGE_HEIGHT_MM = 297;
const PDF_EXPORT_SAFE_BREAK_SELECTOR = '[data-pdf-page-break="after"]';
const PDF_EXPORT_MIN_SAFE_SLICE_RATIO = 0.36;
const pdfExportShellStyle: React.CSSProperties = {
    position: 'fixed',
    left: 'calc(100vw + 24px)',
    top: 0,
    width: `${PDF_EXPORT_WIDTH_PX}px`,
    pointerEvents: 'none',
    zIndex: 2147483647,
    overflow: 'visible',
};
const getOutcomeSummaryPdfFileName = (workspaceName?: string | null) => {
    const cleanWorkspaceName = (workspaceName ?? '')
        .trim()
        .replace(/[^a-zA-Z0-9_\u4e00-\u9fa5]+/g, '_')
        .replace(/^_+|_+$/g, '');

    return `${cleanWorkspaceName || 'Bustly'}_outcome_summary.pdf`;
};
const waitForPdfExportFrame = () => new Promise<void>((resolve) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        resolve();
        return;
    }
    window.requestAnimationFrame(() => resolve());
});
const getPdfExportSafeBreakPositions = (root: HTMLElement) => {
    const rootRect = root.getBoundingClientRect();
    const rootHeight = Math.max(root.scrollHeight, rootRect.height);
    const breakPositions = Array.from(root.querySelectorAll<HTMLElement>(PDF_EXPORT_SAFE_BREAK_SELECTOR))
        .map((item) => item.getBoundingClientRect().bottom - rootRect.top)
        .filter((position) => Number.isFinite(position) && position > 0 && position < rootHeight);

    return Array.from(new Set([...breakPositions, rootHeight].map((position) => Math.round(position))))
        .sort((a, b) => a - b);
};

const getNextPdfExportSliceHeight = ({
    currentOffsetY,
    imageHeight,
    pageHeight,
    safeBreakPositions,
}: {
    currentOffsetY: number;
    imageHeight: number;
    pageHeight: number;
    safeBreakPositions: number[];
}) => {
    const targetOffsetY = Math.min(currentOffsetY + pageHeight, imageHeight);
    if (targetOffsetY >= imageHeight) return imageHeight - currentOffsetY;

    const minimumUsefulSliceHeight = pageHeight * PDF_EXPORT_MIN_SAFE_SLICE_RATIO;
    const bestSafeBreak = safeBreakPositions
        .filter((position) => (
            position > currentOffsetY + minimumUsefulSliceHeight
            && position <= targetOffsetY
        ))
        .at(-1);

    return Math.max(1, Math.round((bestSafeBreak ?? targetOffsetY) - currentOffsetY));
};
const brandFocusClass = 'focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#1A162F]/20 active:outline-none active:ring-0 active:shadow-none [-webkit-tap-highlight-color:transparent]';
const rangePickerTriggerClass = `h-9 rounded-lg border border-[#D2DAE8] bg-white px-3 text-left text-[12px] font-medium text-[#1A162F] shadow-none transition-colors hover:border-[#AEB8CC] ${brandFocusClass}`;
const compactFilterButtonBaseClass = `h-[34px] whitespace-nowrap rounded-[7px] border px-2.5 text-[12px] font-medium leading-[18px] transition-colors duration-150 ${brandFocusClass}`;
const compactFilterButtonActiveClass = 'border-[#1A162F] bg-white text-[#1A162F] shadow-none';
const compactFilterButtonInactiveClass = 'border-[#DDE3EA] bg-white text-[#667085] hover:border-[#BFC6D4] hover:bg-white hover:text-[#1A162F]';
const isOutcomeLedgerGenerationStatusInFlight = (status: unknown) => (
    typeof status === 'string'
    && ['pending', 'queued', 'running', 'in_progress'].includes(status.toLowerCase())
);
const platformEvidenceSystemRules: Array<{
    matches: string[];
    iconName: string;
    isBrandIcon: boolean;
    label: string;
    imageUrl?: string;
}> = [
    {
        matches: ['shopify pos'],
        iconName: 'shopify',
        isBrandIcon: true,
        label: 'Shopify POS',
    },
    {
        matches: ['shopify'],
        iconName: 'shopify',
        isBrandIcon: true,
        label: 'Shopify',
    },
    {
        matches: ['google analytics 4', 'google analytics', 'ga4'],
        iconName: 'google',
        isBrandIcon: true,
        label: 'Google Analytics',
        imageUrl: '/imgs/Google_Analytics.png',
    },
    {
        matches: ['google search console', 'search console', 'gsc'],
        iconName: 'google',
        isBrandIcon: true,
        label: 'Google Search Console',
    },
    {
        matches: ['google docs', 'google document', 'google drive', 'google sheets', 'google slides'],
        iconName: 'google',
        isBrandIcon: true,
        label: 'Google Docs',
    },
    {
        matches: ['google ads', 'adwords'],
        iconName: 'google-ads',
        isBrandIcon: true,
        label: 'Google Ads',
        imageUrl: '/imgs/Google_Ads.jpeg',
    },
    {
        matches: ['facebook page'],
        iconName: 'facebook_ads',
        isBrandIcon: true,
        label: 'Facebook Page',
    },
    {
        matches: ['instagram'],
        iconName: 'meta-ads',
        isBrandIcon: true,
        label: 'Instagram',
    },
    {
        matches: ['meta ads', 'facebook ads', 'meta', 'facebook'],
        iconName: 'meta-ads',
        isBrandIcon: true,
        label: 'Meta Ads',
        imageUrl: metaAdsIcon,
    },
    {
        matches: ['tiktok shop', 'tik tok shop'],
        iconName: 'TikTok',
        isBrandIcon: true,
        label: 'TikTok Shop',
    },
    {
        matches: ['tiktok ads', 'tik tok ads', 'tiktokads', 'tiktok', 'tik tok'],
        iconName: 'TikTok',
        isBrandIcon: true,
        label: 'TikTok Ads',
    },
    {
        matches: ['pinterest ads', 'pinterestads', 'pinterest'],
        iconName: 'pinterest',
        isBrandIcon: true,
        label: 'Pinterest Ads',
    },
    {
        matches: ['amazon'],
        iconName: 'amazon',
        isBrandIcon: true,
        label: 'Amazon',
    },
    {
        matches: ['bigcommerce'],
        iconName: 'BigCommerce',
        isBrandIcon: true,
        label: 'BigCommerce',
    },
    {
        matches: ['woocommerce'],
        iconName: 'WooCommerce',
        isBrandIcon: true,
        label: 'WooCommerce',
    },
    {
        matches: ['magento'],
        iconName: 'magento',
        isBrandIcon: true,
        label: 'Magento',
    },
    {
        matches: ['prestashop'],
        iconName: 'store',
        isBrandIcon: false,
        label: 'PrestaShop',
    },
    {
        matches: ['wix'],
        iconName: 'store',
        isBrandIcon: false,
        label: 'Wix',
    },
    {
        matches: ['salesforce commerce'],
        iconName: 'Salesforce',
        isBrandIcon: true,
        label: 'Salesforce Commerce',
    },
    {
        matches: ['salesforce'],
        iconName: 'Salesforce',
        isBrandIcon: true,
        label: 'Salesforce',
    },
    {
        matches: ['klaviyo'],
        iconName: 'store',
        isBrandIcon: false,
        label: 'Klaviyo',
    },
    {
        matches: ['mailchimp'],
        iconName: 'store',
        isBrandIcon: false,
        label: 'Mailchimp',
    },
    {
        matches: ['feishu', 'lark'],
        iconName: 'store',
        isBrandIcon: false,
        label: 'Feishu',
    },
    {
        matches: ['dingtalk', 'dingding'],
        iconName: 'store',
        isBrandIcon: false,
        label: 'DingTalk',
    },
    {
        matches: ['slack'],
        iconName: 'slack',
        isBrandIcon: true,
        label: 'Slack',
    },
];

const getOutcomeKey = (outcome: { outcomeId: string; outcomeNo: string }) => `${outcome.outcomeId}:${outcome.outcomeNo}`;

const getOutcomePaginationItems = (currentPage: number, totalPages: number): OutcomePaginationItem[] => {
    if (totalPages <= MAX_VISIBLE_OUTCOME_PAGE_BUTTONS + 2) {
        return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const middleStart = currentPage <= 3
        ? 2
        : currentPage >= totalPages - 2
            ? totalPages - 3
            : currentPage - 1;
    const middleEnd = currentPage <= 3
        ? 4
        : currentPage >= totalPages - 2
            ? totalPages - 1
            : currentPage + 1;
    const items: OutcomePaginationItem[] = [1];

    if (middleStart > 2) {
        items.push('ellipsis-start');
    }

    for (let page = middleStart; page <= middleEnd; page += 1) {
        items.push(page);
    }

    if (middleEnd < totalPages - 1) {
        items.push('ellipsis-end');
    }

    items.push(totalPages);

    return items;
};

const normalizeEvidenceSystemName = (value: string) => (
    value.trim().toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ')
);

const hiddenEvidenceSystemNames = new Set([
    'bustly trace',
    'workspace actions',
    'social channels',
    'chrome browser',
    'chrome browser plugin',
]);

const isHiddenEvidenceSystem = (system: string) => hiddenEvidenceSystemNames.has(normalizeEvidenceSystemName(system));

const resolveEvidenceSystemVisual = (system: string): EvidenceSystemVisual | null => {
    const rawLabel = formatSourceTaskText(system);
    const normalized = normalizeEvidenceSystemName(rawLabel);
    const match = platformEvidenceSystemRules.find((rule) => (
        rule.matches.some((candidate) => normalized.includes(candidate))
    ));

    if (!match) return null;

    const label = match.label;

    return {
        key: normalizeEvidenceSystemName(label) || normalized || rawLabel || 'unknown-system',
        label,
        iconName: match?.iconName ?? 'database',
        isBrandIcon: Boolean(match?.isBrandIcon),
        imageUrl: match?.imageUrl,
    };
};

const getEvidenceSystemVisuals = (systems: string[]) => {
    const sourceSystems = systems.filter((system) => !isHiddenEvidenceSystem(system));
    const seen = new Set<string>();

    return sourceSystems.reduce<EvidenceSystemVisual[]>((items, system) => {
        const visual = resolveEvidenceSystemVisual(system);
        if (!visual) return items;
        if (seen.has(visual.key)) return items;
        seen.add(visual.key);
        items.push(visual);
        return items;
    }, []);
};

const formatVisibleEvidenceSystemsText = (systems: string[]) => {
    const visibleSystems = getEvidenceSystemVisuals(systems).map((system) => system.label);
    return formatSourceTaskText(visibleSystems.join(' / '));
};

const mergeOperationCounts = (
    first: EvidenceSystemOperationCounts | undefined,
    second: EvidenceSystemOperationCounts | undefined,
): EvidenceSystemOperationCounts | undefined => {
    if (!first) return second;
    if (!second) return first;

    return {
        read: (first.read ?? 0) + (second.read ?? 0),
        write: (first.write ?? 0) + (second.write ?? 0),
        check: (first.check ?? 0) + (second.check ?? 0),
        other: (first.other ?? 0) + (second.other ?? 0),
        total: (first.total ?? 0) + (second.total ?? 0),
    };
};

const getOutcomeEvidenceSystemVisuals = (outcome: ValueLedgerOutcome) => {
    const productSystems = outcome.detail?.systemEvidence?.systems ?? outcome.whereBustlyWorked;
    if (!productSystems || productSystems.length === 0) {
        return getEvidenceSystemVisuals(outcome.evidenceSystems);
    }

    const seen = new Map<string, EvidenceSystemVisual>();

    productSystems.forEach((system) => {
        const rawLabel = system.label || system.code;
        if (!rawLabel || isHiddenEvidenceSystem(rawLabel)) return;

        const visual = resolveEvidenceSystemVisual(rawLabel);
        if (!visual) return;

        const existing = seen.get(visual.key);
        if (existing) {
            existing.operationCounts = mergeOperationCounts(existing.operationCounts, system.operationCounts);
            return;
        }

        seen.set(visual.key, {
            ...visual,
            operationCounts: system.operationCounts,
        });
    });

    return Array.from(seen.values());
};

const toOverviewTaskAreaRow = (
    item: ValueLedgerOverviewTaskScenario,
    index: number,
): OutcomeOverviewTaskAreaRow => ({
    key: `${item.scenarioLabel || 'overview-task-area'}-${index}`,
    scenarioLabel: item.scenarioLabel,
    taskCount: item.taskCount,
    activeMemberCount: item.activeMemberCount,
    representativeWork: item.representativeWork,
});

const deriveOverviewTaskAreasFromOutcomes = (outcomes: ValueLedgerOutcome[]): OutcomeOverviewTaskAreaRow[] => {
    const groups = new Map<string, {
        scenarioLabel: string | null;
        sourceTaskIds: Set<string>;
        userKeys: Set<string>;
    }>();

    outcomes.forEach((outcome) => {
        const scenarioLabel = formatSourceTaskText(outcome.businessModule) === '-' ? null : outcome.businessModule;
        const groupKey = scenarioLabel || 'unclassified';
        const existing = groups.get(groupKey) ?? {
            scenarioLabel,
            sourceTaskIds: new Set<string>(),
            userKeys: new Set<string>(),
        };
        const taskKey = formatSourceTaskText(outcome.sourceTaskId || outcome.sourceTaskNo || outcome.outcomeId);
        const userKey = formatSourceTaskText(outcome.sourceUserEmail || outcome.sourceUserName);

        if (taskKey !== '-') existing.sourceTaskIds.add(taskKey);
        if (userKey !== '-') existing.userKeys.add(userKey);
        groups.set(groupKey, existing);
    });

    return Array.from(groups.entries())
        .map(([key, group]) => ({
            key,
            scenarioLabel: group.scenarioLabel,
            taskCount: group.sourceTaskIds.size > 0 ? group.sourceTaskIds.size : null,
            activeMemberCount: group.userKeys.size > 0 ? group.userKeys.size : null,
            representativeWork: null,
        }))
        .sort((first, second) => (second.taskCount ?? -1) - (first.taskCount ?? -1));
};

const getPrimaryEvidenceSystemLabel = (outcomes: ValueLedgerOutcome[]) => {
    const systemCounts = new Map<string, { label: string; count: number }>();

    outcomes.forEach((outcome) => {
        getOutcomeEvidenceSystemVisuals(outcome).forEach((system) => {
            const current = systemCounts.get(system.key) ?? { label: system.label, count: 0 };
            systemCounts.set(system.key, { ...current, count: current.count + 1 });
        });
    });

    const [primarySystem] = Array.from(systemCounts.values())
        .sort((first, second) => second.count - first.count);

    return primarySystem?.label ?? null;
};

const getScrollableAncestors = (element: HTMLElement | null): HTMLElement[] => {
    if (typeof window === 'undefined') return [];

    const ancestors: HTMLElement[] = [];
    let current = element?.parentElement ?? null;

    while (current && current !== document.body && current !== document.documentElement) {
        const style = window.getComputedStyle(current);
        const canScrollY = scrollableOverflowPattern.test(style.overflowY) && current.scrollHeight > current.clientHeight;
        const canScrollX = scrollableOverflowPattern.test(style.overflowX) && current.scrollWidth > current.clientWidth;

        if (canScrollY || canScrollX) {
            ancestors.push(current);
        }

        current = current.parentElement;
    }

    return ancestors;
};

const isChineseLocale = (locale: string) => locale.toLowerCase().startsWith('zh');

const normalizeChineseMixedTextSpacing = (value: string) => (
    value
        .replace(/([\p{Script=Han}])([A-Za-z0-9][A-Za-z0-9+.#&/-]*)/gu, '$1 $2')
        .replace(/([A-Za-z0-9][A-Za-z0-9+.#&/-]*)([\p{Script=Han}])/gu, '$1 $2')
        .replace(/\s{2,}/gu, ' ')
        .trim()
);

const formatLocalizedRawText = (value: string, locale: string) => (
    isChineseLocale(locale) ? normalizeChineseMixedTextSpacing(value) : value
);

const normalizeChineseOutcomeMemberTerms = (value: string, locale: string) => {
    if (!isChineseLocale(locale)) return value;

    return value
        .replace(/(\{\{userCount\}\}|[\d,，]+|-)\s*位用户参与/gu, '$1 位成员参与')
        .replace(/全部用户发起任务/gu, '全部成员发起任务')
        .replace(/用户发起的任务/gu, '成员发起的任务')
        .replace(/用户发起任务/gu, '成员发起任务')
        .replace(/参与用户/gu, '参与成员')
        .replace(/用户消息/gu, '成员消息')
        .replace(/未命名用户/gu, '未命名成员')
        .replace(/工作区用户/gu, '工作区成员')
        .replace(/待用户/gu, '待成员')
        .replace(/需用户/gu, '需成员')
        .replace(/用户权限缺失/gu, '成员权限缺失')
        .replace(/用户信息缺口/gu, '成员信息缺口')
        .replace(/用户待确认事项/gu, '成员待确认事项')
        .replace(/用户可以/gu, '成员可以')
        .replace(/用户可按/gu, '成员可按')
        .replace(/交给用户处理/gu, '交给成员处理')
        .replace(/交接给用户/gu, '交接给成员');
};

const formatOutcomeGeneratedCopyText = (value: string | null | undefined, locale: string) => {
    const formatted = formatLocalizedSourceTaskText(value, locale);
    return formatted === '-' ? formatted : normalizeChineseOutcomeMemberTerms(formatted, locale);
};

const ledgerLabelCopy: Record<string, { en: string; zh: string }> = {
    'Trace 派生成果账本': { en: 'Outcome summary generated from workspace tasks', zh: '成果总览' },
    'Trace 快照': { en: 'Outcome snapshot', zh: '成果快照' },
    '成果账本': { en: 'Outcome summary generated from workspace tasks', zh: '成果总览' },
    '成果总览': { en: 'Outcome summary generated from workspace tasks', zh: '成果总览' },
    '当前页面快照': { en: 'Current page snapshot', zh: '当前页面快照' },
    'Subscription cycle 1': { en: 'Subscription cycle 1', zh: '第 1 个订阅周期' },
    '执行型成果': { en: 'Execution outcomes', zh: '执行型成果' },
    '核验型成果': { en: 'Verification outcomes', zh: '核验型成果' },
    '整理型成果': { en: 'Organization outcomes', zh: '整理型成果' },
    '判断型成果': { en: 'Judgment outcomes', zh: '判断型成果' },
    '看护型成果': { en: 'Stewardship outcomes', zh: '看护型成果' },
    '卡点推进成果': { en: 'Blocker resolution', zh: '卡点推进成果' },
    '其他成果': { en: 'Other outcomes', zh: '其他成果' },
    '社媒、KOL 与品牌声量': { en: 'Social, KOL, and brand voice', zh: '社媒、KOL 与品牌声量' },
    '内容、SEO 与页面资产': { en: 'Content, SEO, and page assets', zh: '内容、SEO 与页面资产' },
    '商品、库存与货架运营': { en: 'Product, inventory, and merchandising', zh: '商品、库存与货架运营' },
    '广告投放与获客增长': { en: 'Ads and acquisition', zh: '广告投放与获客增长' },
    '系统连接、权限与数据源': { en: 'Systems, permissions, and data sources', zh: '系统连接、权限与数据源' },
    '经营数据与管理汇报': { en: 'Operating data and reporting', zh: '经营数据与管理汇报' },
    '客户、会员与 CRM': { en: 'Customer, membership, and CRM', zh: '客户、会员与 CRM' },
    '站点流量、转化与 CRO': { en: 'Site traffic, conversion, and CRO', zh: '站点流量、转化与 CRO' },
    '订单、履约与售后': { en: 'Orders, fulfillment, and after-sales', zh: '订单、履约与售后' },
    '社媒 KOL 与舆情雷达': { en: 'Social, KOL, and sentiment monitoring', zh: '社媒 KOL 与舆情雷达' },
    '内容 SEO 与页面资产': { en: 'Content, SEO, and page assets', zh: '内容 SEO 与页面资产' },
    '店铺迁移与 Shopify 执行': { en: 'Store migration and Shopify execution', zh: '店铺迁移与 Shopify 执行' },
    '广告投放与获客分析': { en: 'Ads and acquisition analysis', zh: '广告投放与获客分析' },
    '系统连接与数据源治理': { en: 'System connections and data-source governance', zh: '系统连接与数据源治理' },
    '客户与会员运营': { en: 'Customer and membership operations', zh: '客户与会员运营' },
    '持续巡检': { en: 'Ongoing monitoring', zh: '持续巡检' },
    '待用户复核': { en: 'Pending user review', zh: '待成员复核' },
    '待用户验收': { en: 'Pending user acceptance', zh: '待成员验收' },
    '需用户复核': { en: 'Needs user review', zh: '需成员复核' },
    '需用户处理': { en: 'Needs user action', zh: '需成员处理' },
    '待 Member 复核': { en: 'Pending user review', zh: '待成员复核' },
    '待 Member 验收': { en: 'Pending user acceptance', zh: '待成员验收' },
    '需 Member 复核': { en: 'Needs user review', zh: '需成员复核' },
    '需 Member 处理': { en: 'Needs user action', zh: '需成员处理' },
    '内容/页面范围': { en: 'Content/page scope', zh: '内容/页面范围' },
    '商品/店铺范围': { en: 'Product/store scope', zh: '商品/店铺范围' },
    '投放/获客范围': { en: 'Ads/acquisition scope', zh: '投放/获客范围' },
    '数据范围': { en: 'Data scope', zh: '数据范围' },
    '渠道/话题范围': { en: 'Channel/topic scope', zh: '渠道/话题范围' },
    '系统/权限范围': { en: 'System/permission scope', zh: '系统/权限范围' },
    'Product/store': { en: 'Product/store', zh: '商品/店铺' },
    'Content/page': { en: 'Content/page', zh: '内容/页面' },
    'Campaign/acquisition': { en: 'Campaign/acquisition', zh: '投放/获客' },
    'Data/report': { en: 'Data/report', zh: '经营数据' },
    'Channel/topic': { en: 'Channel/topic', zh: '渠道/话题' },
    'System/permission': { en: 'System/permission', zh: '系统/权限' },
    'Customer/CRM': { en: 'Customer/CRM', zh: '客户/CRM' },
    '运营对象': { en: 'Operating object', zh: '运营对象' },
};

const localizeLedgerLabel = (value: string | null | undefined, locale: string) => {
    if (!value) return '--';
    const normalizedValue = value.trim();
    const subscriptionCycleMatch = normalizedValue.match(/^(?:Subscription cycle|订阅周期)\s*(\d+)$/u);
    if (subscriptionCycleMatch) {
        return isChineseLocale(locale)
            ? `订阅周期 ${subscriptionCycleMatch[1]}`
            : `Subscription cycle ${subscriptionCycleMatch[1]}`;
    }
    if (normalizedValue === '当前订阅周期') {
        return isChineseLocale(locale) ? '当前订阅周期' : 'Current subscription cycle';
    }
    const copy = ledgerLabelCopy[normalizedValue];
    if (!copy) return formatLocalizedRawText(normalizedValue, locale);
    return isChineseLocale(locale) ? copy.zh : copy.en;
};

const isCurrentSubscriptionCycleLabel = (label: string | null | undefined, rangeId: string | null | undefined) => {
    const normalizedLabel = String(label || '').trim().toLowerCase();
    const normalizedRangeId = String(rangeId || '').trim().toLowerCase();

    return normalizedLabel.includes('当前')
        || normalizedLabel.includes('current')
        || normalizedRangeId === 'current'
        || normalizedRangeId === 'current-cycle'
        || normalizedRangeId === 'current_subscription_cycle';
};

const getRangeOptionContextLabel = (
    label: string | null | undefined,
    locale: string,
    t: Translate,
) => {
    const normalizedLabel = String(label || '').trim().toLowerCase();

    if (!normalizedLabel) return t('website.valueLedger.customCycle');
    if (normalizedLabel.includes('自定义') || normalizedLabel.includes('custom')) {
        return t('website.valueLedger.customCycle');
    }
    if (normalizedLabel.includes('当前') || normalizedLabel.includes('current')) {
        return t('website.valueLedger.currentCycle');
    }
    if (normalizedLabel.includes('上一') || normalizedLabel.includes('previous')) {
        return t('website.valueLedger.previousCycle');
    }
    if (normalizedLabel.includes('最近 3') || normalizedLabel.includes('recent 3')) {
        return t('website.valueLedger.recentThreeCycles');
    }
    if (normalizedLabel.includes('subscription cycle') || normalizedLabel.includes('订阅周期')) {
        return t('website.valueLedger.billingCycle');
    }

    return localizeLedgerLabel(label, locale);
};

const localizeDerivedLedgerText = (value: string | null | undefined, locale: string) => {
    if (!value) return value;
    const isChinese = isChineseLocale(locale);

    const classificationMatch = value.match(/^基于 Trace 任务名「(.+)」、(\d+) 条\s*(?:Member|用户)\s*消息、(\d+) 次工具调用和 (\d+) 条(?:计费|处理)记录自动归类。$/u);
    if (classificationMatch) {
        const [, taskName, memberMessages, toolCalls] = classificationMatch;
        return isChinese
            ? `基于来源任务「${taskName}」、${memberMessages} 条成员消息和 ${toolCalls} 次执行动作整理。`
            : `Classified from source task "${taskName}", ${memberMessages} user messages, and ${toolCalls} actions.`;
    }

    const billingMatch = value.match(/^Trace 证据包含 (\d+) 条\s*(?:Member|用户)\s*消息、(\d+) 条 Bustly 回复、(\d+) 次工具调用、(\d+) 条计费记录；可回到来源任务核验。$/u);
    if (billingMatch) {
        const [, memberMessages, bustlyReplies, toolCalls] = billingMatch;
        return isChinese
            ? `来源任务包含 ${memberMessages} 条成员消息、${bustlyReplies} 条 Bustly 回复和 ${toolCalls} 次执行动作，可用于复核。`
            : `Source task evidence includes ${memberMessages} user messages, ${bustlyReplies} Bustly replies, and ${toolCalls} actions. Review the source task to verify.`;
    }

    const generatedCopy = [
        {
            pattern: /^该成果由真实 Trace\s+session 的任务名、消息、工具调用和计费记录自动派生。$/u,
            zh: '该成果由来源任务标题、消息和执行动作整理而成。',
            en: 'This outcome is derived from the source task name, messages, and actions.',
        },
        {
            pattern: /^请基于 Trace\s+对话和证据确认该结果是否已满足当前任务目标；如需继续处理，可从来源任务继续追问。$/u,
            zh: '请基于来源任务的对话和证据确认该结果是否满足任务目标；如需继续处理，可从来源任务继续追问。',
            en: 'Review the source task conversation and evidence to confirm this outcome meets the task goal. Continue from the source task if more work is needed.',
        },
        {
            pattern: /^请按业务结果核验；确认后可作为该周期成果记录保留。$/u,
            zh: '请按业务结果核验；确认后可作为该周期成果记录保留。',
            en: 'Review the business result. Once confirmed, keep it as an outcome record for this period.',
        },
        {
            pattern: /^该条目是 Trace 派生记录；如要用于结算、对外发布或客户承诺，需要由(?:用户|成员)复核。$/u,
            zh: '该条目来自来源任务；如要对外发布或承诺客户，需要由成员复核。',
            en: 'Ask a user to review this source-task record before external sharing or customer commitments.',
        },
        {
            pattern: /^未形成可展示回复、纯打招呼或无业务标题的 session 已从列表中过滤。$/u,
            zh: '未形成可展示回复、纯打招呼或无业务标题的任务已从列表中过滤。',
            en: 'Tasks without a usable reply, business title, or meaningful work are filtered out.',
        },
        {
            pattern: /^以 Trace\s+session id、对话记录、toolCall 记录和 billing events 作为复核依据。$/u,
            zh: '以来源任务对话和执行动作作为复核依据。',
            en: 'Use the source task conversation and actions as the review basis.',
        },
    ];
    const copy = generatedCopy.find((item) => item.pattern.test(value));
    if (copy) return isChinese ? copy.zh : copy.en;

    const sanitizedValue = value
        .replace(/Trace\s+session id/giu, isChinese ? '来源任务' : 'source task')
        .replace(/Trace\s+session/giu, isChinese ? '来源任务' : 'source task')
        .replace(/Trace\s+对话/gu, isChinese ? '来源任务对话' : 'source task conversation')
        .replace(/Trace 证据/gu, isChinese ? '来源任务证据' : 'source task evidence')
        .replace(/Trace 任务名/gu, isChinese ? '来源任务标题' : 'source task title')
        .replace(/Trace 派生记录/gu, isChinese ? '来源任务记录' : 'source task record')
        .replace(/Trace/gu, isChinese ? '来源任务' : 'source task')
        .replace(/billing\s+events/giu, isChinese ? '处理记录' : 'supporting records')
        .replace(/billing\s+records/giu, isChinese ? '处理记录' : 'supporting records')
        .replace(/计费记录/gu, '处理记录');

    if (isChinese) return sanitizedValue;
    return sanitizedValue
        .replace(/^来源任务标题抽取记录显示：/u, 'Source task extract: ')
        .replace(/\s*追溯记录$/u, ' source record');
};

const getLocalizedFieldValue = (
    field: { value?: string | null; localizedValue?: { en?: string | null; zh?: string | null } } | null | undefined,
    locale: string,
) => {
    if (!field) return undefined;
    const localized = isChineseLocale(locale) ? field.localizedValue?.zh : field.localizedValue?.en;
    return localized ?? field.value;
};

const formatScopeValue = (label: string, value: string, locale: string) => (
    isChineseLocale(locale)
        ? `${localizeLedgerLabel(label, locale).replace('范围', '')}：${formatLocalizedRawText(value, locale)}`
        : `${localizeLedgerLabel(label, locale).replace(/ scope$/u, '')}: ${value}`
);

const getDerivedFieldText = (
    field: { value?: string | null; localizedValue?: { en?: string | null; zh?: string | null } } | null | undefined,
    locale: string,
) => formatLocalizedSourceTaskText(localizeDerivedLedgerText(getLocalizedFieldValue(field, locale), locale), locale);

const getDerivedTextOrFallback = (
    field: { value?: string | null } | null | undefined,
    locale: string,
    fallback: string,
) => {
    const value = getDerivedFieldText(field, locale);
    return value === '-' ? formatLocalizedSourceTaskText(fallback, locale) : value;
};

const getOutcomeEvidenceSystemValues = (outcome: ValueLedgerOutcome) => {
    const productSystems = outcome.detail?.systemEvidence?.systems ?? outcome.whereBustlyWorked;
    if (productSystems && productSystems.length > 0) {
        return productSystems.map(getOutcomeEvidenceSystemLookupValue).filter(Boolean);
    }
    return outcome.evidenceSystems;
};

const getOutcomeEvidenceSystemLookupValue = (system: ValueLedgerEvidenceSystem) => {
    const code = system.code?.trim();
    if (code && isHiddenEvidenceSystem(code)) return code;
    if (code && resolveEvidenceSystemVisual(code)) return code;
    return system.label || code || '';
};

const getOutcomeScopeValue = (outcome: ValueLedgerOutcome, locale: string) => {
    const productScope = outcome.detail?.scope;
    if (productScope?.value) {
        const label = localizeLedgerLabel(productScope.label, locale);
        return isChineseLocale(locale)
            ? `${label}：${formatLocalizedRawText(productScope.value, locale)}`
            : `${label}: ${productScope.value}`;
    }
    return formatScopeValue(outcome.scopeLabel, outcome.scopeValue, locale);
};

const getOutcomeScopeDisplayValue = (outcome: ValueLedgerOutcome) => (
    formatSourceTaskText(outcome.detail?.scope?.value ?? outcome.scopeValue)
);

const getOutcomeDetailTypeLabel = (outcome: ValueLedgerOutcome, locale: string) => (
    localizeLedgerLabel(outcome.detail?.detailOutcomeType?.label ?? outcome.outcomeTypeLabel, locale)
);

const getOutcomeDetailBusinessAreaLabel = (outcome: ValueLedgerOutcome, locale: string) => (
    localizeLedgerLabel(outcome.detail?.detailBusinessArea?.label ?? outcome.businessModule, locale)
);

const formatInlineFieldLabel = (label: string, locale: string) => (
    isChineseLocale(locale) ? `${label}：` : `${label}:`
);

const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '--';
    return new Intl.NumberFormat('en-US').format(value);
};

const formatOutcomeSummaryCount = (value: number | null | undefined, isPendingRefresh: boolean) => {
    if (isPendingRefresh) return '-';
    return formatNumber(value);
};

const formatOverviewMetricNumber = (value: number | null | undefined) => (
    value === null || value === undefined ? '-' : formatNumber(value)
);

const formatOverviewMetricPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    const normalized = Math.abs(value) <= 1 ? value * 100 : value;
    return `${Math.round(normalized)}%`;
};

const getOverviewShare = (
    part: number | null | undefined,
    total: number | null | undefined,
) => {
    if (part === null || part === undefined || total === null || total === undefined || total <= 0) return null;
    return part / total;
};

const formatDateTime = (value: string | null | undefined, locale: string, timezone: string) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';

    return new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date);
};

const formatUtcOffset = (date: Date = new Date()) => {
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const minutes = String(absOffset % 60).padStart(2, '0');
    return `UTC${sign}${hours}:${minutes}`;
};

const formatTimezoneOffsetLabel = (timezone: string | null | undefined) => {
    if (!timezone) return formatUtcOffset();
    if (/^UTC[+-]\d{2}:\d{2}$/u.test(timezone)) return timezone;

    try {
        const timezoneName = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'longOffset',
        }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;

        if (!timezoneName) return timezone;
        if (timezoneName === 'GMT') return 'UTC+00:00';
        return timezoneName.replace('GMT', 'UTC');
    } catch {
        return timezone;
    }
};

const formatSourceTaskText = (value: string | null | undefined) => (
    value && value.trim() ? value : '-'
);

type LedgerFilterCountItem = {
    label: string;
    count: number;
};

const getOutcomeFilterLabel = (value: string | null | undefined) => {
    const label = formatSourceTaskText(value);
    return label === '-' ? null : label;
};

const getOrderedOutcomeFilterLabels = (
    counts: LedgerFilterCountItem[],
    fallbackLabels: string[],
) => {
    const labels: string[] = [];

    const addLabel = (value: string | null | undefined) => {
        const label = getOutcomeFilterLabel(value);
        if (!label || labels.includes(label)) return;
        labels.push(label);
    };

    counts.forEach((item) => addLabel(item.label));
    fallbackLabels.forEach(addLabel);

    return labels;
};

const getOutcomeCountsByLabel = (
    labels: string[],
    outcomes: ValueLedgerOutcome[],
    getLabel: (outcome: ValueLedgerOutcome) => string | null | undefined,
): LedgerFilterCountItem[] => {
    const orderedLabels = [...labels];
    const counts = new Map<string, number>(orderedLabels.map((label) => [label, 0]));

    outcomes.forEach((outcome) => {
        const label = getOutcomeFilterLabel(getLabel(outcome));
        if (!label) return;

        if (!counts.has(label)) {
            orderedLabels.push(label);
            counts.set(label, 0);
        }

        counts.set(label, (counts.get(label) ?? 0) + 1);
    });

    return orderedLabels.map((label) => ({
        label,
        count: counts.get(label) ?? 0,
    }));
};

const sumOutcomeFilterCounts = (items: LedgerFilterCountItem[]) => (
    items.reduce((total, item) => total + item.count, 0)
);

const formatLocalizedSourceTaskText = (value: string | null | undefined, locale: string) => {
    const formatted = formatSourceTaskText(value);
    return formatted === '-' ? formatted : formatLocalizedRawText(formatted, locale);
};

const formatSourceTaskNumber = (value: number | null | undefined) => (
    value === null || value === undefined ? '-' : formatNumber(value)
);

const formatSourceTaskDateTime = (value: string | null | undefined, locale: string, timezone: string) => {
    const formatted = formatDateTime(value, locale, timezone);
    return formatted === '--' ? '-' : formatted;
};

const formatSourceTaskDateRange = (
    start: string | null | undefined,
    end: string | null | undefined,
    locale: string,
    timezone: string,
) => {
    const formattedStart = formatSourceTaskDateTime(start, locale, timezone);
    const formattedEnd = formatSourceTaskDateTime(end, locale, timezone);

    if (formattedStart === '-' && formattedEnd === '-') return '-';
    if (formattedStart === '-') return formattedEnd;
    if (formattedEnd === '-') return formattedStart;
    return `${formattedStart} - ${formattedEnd}`;
};

const toSubscriptionCycleDisplayEnd = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return end;
    if (endDate.getTime() <= startDate.getTime()) return end;
    return new Date(endDate.getTime() - 1).toISOString();
};

const formatSubscriptionCycleDateRange = (start: string, end: string, locale: string, timezone: string) => {
    const startDate = new Date(start);
    const endDate = new Date(toSubscriptionCycleDisplayEnd(start, end));
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '--';

    const formatter = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });

    const separator = isChineseLocale(locale) ? ' 至 ' : ' - ';
    return `${formatter.format(startDate)}${separator}${formatter.format(endDate)}`;
};

const deriveBillingCycleStart = (periodEndAt: string | null | undefined, billingCycle: string | null | undefined) => {
    if (!periodEndAt) return '';
    const endDate = new Date(periodEndAt);
    if (Number.isNaN(endDate.getTime())) return '';

    const startDate = new Date(endDate);
    const normalizedCycle = String(billingCycle || '').toLowerCase();
    if (normalizedCycle.includes('year') || normalizedCycle.includes('annual')) {
        startDate.setFullYear(startDate.getFullYear() - 1);
    } else {
        startDate.setMonth(startDate.getMonth() - 1);
    }

    return startDate.toISOString();
};

/* ─── Detail sub-components for the drilldown modal ─── */

const DetailTextBlock = ({
    label,
    value,
}: {
    label: string;
    value: React.ReactNode;
}) => (
    <div className="rounded-lg border border-[#E8EBF3] bg-white p-3">
        <div className="text-[11px] font-semibold leading-4 text-[#1A162F]">
            {label}
        </div>
        <div className="mt-1 text-[12px] font-normal leading-5 text-[#1A162F]">
            {value || '--'}
        </div>
    </div>
);

const DetailSection = ({
    id,
    title,
    children,
}: {
    id?: string;
    title: string;
    children: React.ReactNode;
}) => (
    <section id={id} className="scroll-mt-4">
        <h3 className="text-[13px] font-semibold leading-5 text-[#1A162F]">{title}</h3>
        <div className="mt-2">{children}</div>
    </section>
);

const DetailInfoCard = ({
    label,
    value,
    className,
}: {
    label: string;
    value: React.ReactNode;
    className?: string;
}) => (
    <div className={clsx('flex h-full min-w-0 flex-col rounded-lg border border-[#E8EBF3] bg-white p-3', className)}>
        <div className="text-[11px] font-semibold leading-4 text-[#1A162F]">
            {label}
        </div>
        <div className="mt-1.5 text-[13px] font-medium leading-relaxed text-[#1A162F] [overflow-wrap:anywhere]">
            {value || '--'}
        </div>
    </div>
);

const EvidenceSystemChip = ({
    system,
    compact = false,
    singleLine = false,
    keepLabelFull = false,
}: {
    system: EvidenceSystemVisual;
    compact?: boolean;
    singleLine?: boolean;
    keepLabelFull?: boolean;
}) => (
    <span
        className={clsx(
            'inline-flex max-w-full min-w-0 items-center rounded-lg border border-[#E1E6EF] bg-white font-medium text-[#1A162F]',
            singleLine ? (keepLabelFull ? 'h-7 shrink-0' : 'h-7 shrink overflow-hidden') : 'shrink-0',
            compact ? 'gap-1.5 px-2 py-1 text-[11px]' : 'gap-2 px-2.5 py-1.5 text-[12px]',
        )}
        title={system.label}
    >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#F8F9FC]">
            {system.imageUrl ? (
                <img
                    src={system.imageUrl}
                    alt=""
                    aria-hidden="true"
                    className={clsx('object-contain', compact ? 'h-[13px] w-[13px]' : 'h-[14px] w-[14px]')}
                />
            ) : (
                <LibraryIcon
                    name={system.iconName}
                    size={compact ? 13 : 14}
                    color={system.isBrandIcon ? undefined : '#666F8D'}
                />
            )}
        </span>
        <span
            className={clsx(
                'min-w-0 truncate',
                compact && !keepLabelFull && (singleLine ? 'max-w-[112px]' : 'max-w-[150px]'),
            )}
        >
            {system.label}
        </span>
    </span>
);

const EvidenceSystemList = ({
    systems,
    compact = false,
}: {
    systems: EvidenceSystemVisual[];
    compact?: boolean;
}) => {
    if (systems.length === 0) {
        return <span className="text-[12px] font-medium text-[#8A93B2]">--</span>;
    }

    return (
        <div className={clsx('flex max-w-full min-w-0 flex-wrap', compact ? 'gap-1.5' : 'gap-2')}>
            {systems.map((system) => (
                <EvidenceSystemChip key={system.key} system={system} compact={compact} />
            ))}
        </div>
    );
};

const SystemEvidenceSummary = ({
    systems,
    meta,
}: {
    systems: EvidenceSystemVisual[];
    meta: string;
}) => {
    return (
        <div className="space-y-2">
            <EvidenceSystemList systems={systems} compact />
            <div className="text-[12px] font-medium leading-5 text-[#4E586A] [overflow-wrap:anywhere]">
                {meta}
            </div>
        </div>
    );
};

const EvidenceChainSteps = ({ steps }: { steps: EstablishmentConditionStep[] }) => (
    <div className="relative space-y-3">
        {steps.map((step, index) => (
            <div key={step.key} className="relative grid min-w-0 grid-cols-[36px_minmax(0,1fr)] gap-3">
                {index < steps.length - 1 && (
                    <div className="absolute bottom-[-30px] left-[17px] top-[18px] w-px bg-[#DDE3EA]" aria-hidden="true" />
                )}
                <div className="relative z-[1] flex h-9 w-9 items-center justify-center rounded-full border border-[#DDE3EA] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] text-[#4E586A]">
                    {step.iconName === 'store' ? (
                        <Target size={16} weight="bold" />
                    ) : step.iconName === 'connect' ? (
                        <HandPalm size={16} weight="bold" />
                    ) : (
                        <LibraryIcon name={step.iconName} size={15} color="#4E586A" />
                    )}
                </div>
                <div className="min-w-0 rounded-lg border border-[#E8EBF3] bg-white px-3 py-2.5">
                    <div className="text-[11px] font-semibold leading-4 text-[#1A162F]">
                        {step.label}
                    </div>
                    <div className="mt-1 text-[12px] font-medium leading-5 text-[#273044] [overflow-wrap:anywhere]">
                        {step.value || '-'}
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const laborLanes: LaborLane[] = [
    {
        key: 'execute',
        zhTitle: '执行型成果',
        enTitle: 'Execution outcomes',
        definition: [
            {
                key: 'definition',
                zh: 'Bustly 在授权范围内完成具体运营动作，或交付可直接使用的运营资产。',
                en: 'Bustly completed a concrete operating action within the authorized scope, or delivered an operating asset that can be used directly.',
            },
            {
                key: 'conditions',
                zh: '产出必须有明确业务对象、可核对证据和完成结果，例如创建、更新、迁移、上传、导出、生成、整理到可用状态。',
                en: 'The output must have a clear business object, checkable evidence, and completion result, such as creating, updating, migrating, uploading, exporting, generating, or organizing something into a usable state.',
            },
            {
                key: 'excluded',
                zh: '只给建议、只读数据、重复记录同一产出、未形成可检查结果，或因 Bustly 自身错误导致未完成。',
                en: 'Only giving advice, only reading data, duplicating the same output, failing to create a checkable result, or leaving work incomplete because of a Bustly error.',
            },
            {
                key: 'dispute',
                zh: '以执行动作、产出链接、变更前后状态、截图或文件作为证据；无法证明产出存在时，不应纳入成果摘要。',
                en: 'Use actions, output links, before-and-after state, screenshots, or files as evidence. If the output cannot be proven, it should not be included in Outcome Summary.',
            },
        ],
    },
    {
        key: 'verify',
        zhTitle: '核验型成果',
        enTitle: 'Verification outcomes',
        definition: [
            {
                key: 'definition',
                zh: 'Bustly 查明某个账号、配置、连接、页面、商品、文件或数据状态。',
                en: 'Bustly checked the state of an account, configuration, connection, page, product, file, or data object.',
            },
            {
                key: 'conditions',
                zh: '必须有明确核验对象，并返回可确认结果，例如已连接/未连接、存在/不存在、可用/不可用、已开启/未开启、已找到/未找到。',
                en: 'There must be a clear verification target and a confirmable result, such as connected/not connected, exists/not found, available/unavailable, enabled/disabled, or found/not found.',
            },
            {
                key: 'excluded',
                zh: '只是泛泛询问、没有明确核验对象、没有返回核验结果，或结果无法被成员复核。',
                en: 'Generic questions, no clear target, no verification result, or a result that cannot be reviewed by the user.',
            },
            {
                key: 'dispute',
                zh: '以账号、连接、页面、商品、文件或配置状态的检查记录作为证据；没有状态结果时，不应纳入核验型成果。',
                en: 'Use the checked account, connection, page, product, file, or configuration state as evidence. Without a state result, it should not be treated as a verification outcome.',
            },
        ],
    },
    {
        key: 'organize',
        zhTitle: '整理型成果',
        enTitle: 'Organization outcomes',
        definition: [
            {
                key: 'definition',
                zh: 'Bustly 把分散信息整理成成员可查看、可继续处理的结构化内容。',
                en: 'Bustly organized scattered information into structured content the user can review and continue working with.',
            },
            {
                key: 'conditions',
                zh: '必须有明确来源和整理结果，例如清单、摘要、字段表、素材列表、页面内容、文档整理、数据汇总、任务拆解。',
                en: 'There must be a clear source and organized result, such as a list, summary, field table, asset list, page content, document organization, data aggregation, or task breakdown.',
            },
            {
                key: 'excluded',
                zh: '只是复制原文、没有结构化结果、没有明确来源，或整理结果无法帮助成员继续处理。',
                en: 'Only copying original text, no structured result, no clear source, or an organized result that cannot help the user continue.',
            },
            {
                key: 'dispute',
                zh: '以来源材料、整理后的结构和可继续处理的字段/清单作为证据；没有明确结构时，不应归为整理型成果。',
                en: 'Use source materials, the organized structure, and actionable fields or lists as evidence. Without a clear structure, it should not be classified as an organization outcome.',
            },
        ],
    },
    {
        key: 'judge',
        zhTitle: '判断型成果',
        enTitle: 'Judgment outcomes',
        definition: [
            {
                key: 'definition',
                zh: 'Bustly 基于店铺数据、广告数据、页面状态或外部信号，完成诊断、归因、优先级判断或行动建议。',
                en: 'Bustly used store data, ad data, page state, or external signals to produce a diagnosis, attribution, priority judgment, or action recommendation.',
            },
            {
                key: 'conditions',
                zh: '判断必须引用明确数据/事实来源，并输出可执行结论，例如问题原因、机会排序、预算建议、页面优化方向。',
                en: 'The judgment must cite clear data or facts and produce an actionable conclusion, such as root cause, opportunity ranking, budget guidance, or page optimization direction.',
            },
            {
                key: 'excluded',
                zh: '泛泛而谈、无法追溯数据来源、没有业务结论、只是复述原始数据，或结论与客户问题无关。',
                en: 'Generic advice, no traceable data source, no business conclusion, merely repeating raw data, or conclusions unrelated to the user question.',
            },
            {
                key: 'dispute',
                zh: '以数据来源、分析口径、结论摘要和可执行建议作为证据；成员可按合同约定口径发起复核。',
                en: 'Use data sources, analysis scope, conclusion summary, and actionable recommendations as evidence. The user can request review under the agreed contract scope.',
            },
        ],
    },
    {
        key: 'watch',
        zhTitle: '看护型成果',
        enTitle: 'Stewardship outcomes',
        definition: [
            {
                key: 'definition',
                zh: 'Bustly 按约定范围、频率或触发条件，持续检查业务状态、外部信号或经营风险。',
                en: 'Bustly continuously checked business state, external signals, or operating risk by agreed scope, cadence, or trigger.',
            },
            {
                key: 'conditions',
                zh: '必须说明检查范围、时间窗口、数据/渠道来源和巡检结果，包括发现问题、发现机会或确认无异常。',
                en: 'The record must state the check scope, time window, data or channel sources, and monitoring result, including problems found, opportunities found, or confirmation that nothing unusual was found.',
            },
            {
                key: 'excluded',
                zh: '没有实际检查记录、范围不清、频率未达到约定、只保留空任务，或没有任何可追溯结果。',
                en: 'No actual check record, unclear scope, missed cadence, an empty task only, or no traceable result.',
            },
            {
                key: 'dispute',
                zh: '以巡检时间线、检查对象、发现记录、通知记录和后续升级成果作为证据；未按频率执行可扣减。',
                en: 'Use the monitoring timeline, checked objects, findings, notifications, and follow-up escalations as evidence. Missed cadence should trigger review.',
            },
        ],
    },
    {
        key: 'unblock',
        zhTitle: '卡点推进成果',
        enTitle: 'Blocker resolution',
        definition: [
            {
                key: 'definition',
                zh: '当任务无法直接完成时，Bustly 定位阻塞原因、尝试可行路径，并把问题推进到成员可以处理的下一步。',
                en: 'When a task could not be completed directly, Bustly identified the blocker, tried viable paths, and moved the issue to a next step the user can act on.',
            },
            {
                key: 'conditions',
                zh: '卡点必须来自成员权限缺失、第三方系统限制、成员信息缺口或外部平台异常，并且 Bustly 已完成定位、尝试和交接。',
                en: 'The blocker must come from missing user permission, third-party system limits, missing user input, or an external platform issue, and Bustly must have identified, tried, and handed off the next step.',
            },
            {
                key: 'excluded',
                zh: 'Bustly 自身能力缺陷、系统 bug、未按约定执行、没有尝试替代路径，或只是说失败但没有下一步。',
                en: 'Bustly capability gaps, system bugs, work not performed as agreed, no alternate path attempted, or simply saying it failed without a next step.',
            },
            {
                key: 'dispute',
                zh: '以错误记录、权限状态、尝试路径、替代方案和成员待确认事项作为证据；若证据显示是 Bustly 责任，则不应纳入成果摘要。',
                en: 'Use error records, permission state, attempted paths, alternatives, and user confirmation items as evidence. If evidence shows Bustly is responsible, it should not be included in Outcome Summary.',
            },
        ],
    },
    {
        key: 'other',
        zhTitle: '其他成果',
        enTitle: 'Other outcomes',
        definition: [
            {
                key: 'definition',
                zh: 'Bustly 完成了可追溯、可复核、对任务推进有帮助的轻量支持工作，但不属于执行型、核验型、整理型、判断型、看护型或卡点推进成果。',
                en: 'Bustly completed lightweight support work that is traceable, reviewable, and useful for task progress, but does not fit execution, verification, organization, judgment, stewardship, or blocker resolution.',
            },
            {
                key: 'conditions',
                zh: '必须有明确来源任务、明确业务对象和可追溯记录，并形成轻量推进结果，例如信息查询、资料收集、前置准备、对象识别、范围确认、上下文补全。',
                en: 'There must be a clear source task, business object, traceable record, and lightweight progress result, such as information lookup, material collection, preparation, object identification, scope confirmation, or context completion.',
            },
            {
                key: 'excluded',
                zh: '纯聊天、寒暄、重复确认、没有业务对象、没有可追溯记录，或没有任何推进结果。',
                en: 'Pure chat, small talk, repeated confirmation, no business object, no traceable record, or no progress result.',
            },
            {
                key: 'dispute',
                zh: '其他成果是正式成果类型，不是待归类；需要能说明它为什么对任务推进有帮助。',
                en: 'Other outcomes are an official outcome type, not an uncategorized bucket. The record must explain why it helped task progress.',
            },
        ],
    },
];

const fallbackBusinessModuleFilterLabels = [
    '社媒、KOL 与品牌声量',
    '商品、库存与货架运营',
    '内容、SEO 与页面资产',
    '广告投放与获客增长',
    '经营数据与管理汇报',
    '系统连接、权限与数据源',
    '客户、会员与 CRM',
];

const getLaborLane = (outcome: ValueLedgerOutcome) => {
    return getLaborLaneByLabel(outcome.outcomeTypeLabel);
};

const getLaborLaneByLabel = (label: string | null | undefined) => {
    if (label?.includes('执行')) return laborLanes.find((lane) => lane.key === 'execute') ?? laborLanes[0]!;
    if (label?.includes('核验')) return laborLanes.find((lane) => lane.key === 'verify') ?? laborLanes[0]!;
    if (label?.includes('整理')) return laborLanes.find((lane) => lane.key === 'organize') ?? laborLanes[0]!;
    if (label?.includes('判断')) return laborLanes.find((lane) => lane.key === 'judge') ?? laborLanes[0]!;
    if (label?.includes('看护')) return laborLanes.find((lane) => lane.key === 'watch') ?? laborLanes[0]!;
    if (label?.includes('卡点')) return laborLanes.find((lane) => lane.key === 'unblock') ?? laborLanes[0]!;
    if (label?.includes('其他')) return laborLanes.find((lane) => lane.key === 'other') ?? laborLanes[0]!;
    return laborLanes[0]!;
};

const getLaneTitle = (lane: LaborLane, locale: string) => (
    isChineseLocale(locale) ? lane.zhTitle : lane.enTitle
);

const getLaneRuleText = (
    lane: LaborLane,
    key: 'definition' | 'conditions' | 'excluded' | 'dispute',
    locale: string,
) => {
    const item = lane.definition.find((definition) => definition.key === key);
    if (!item) return '';
    return isChineseLocale(locale) ? item.zh : item.en;
};

const getOutcomeTypeStandardsCopy = (locale: string) => {
    const isChinese = isChineseLocale(locale);

    return {
        triggerLabel: isChinese ? '查看成果类型标准' : 'View outcome type rules',
        triggerText: isChinese ? '查看标准' : 'View rules',
        title: isChinese ? '成果如何成立' : 'How outcomes are counted',
        description: isChinese
            ? '成果类型按固定标准归类，不按消息数量计算。'
            : 'Outcome types follow fixed rules, not message volume.',
        definition: isChinese ? '定义' : 'What it means',
        conditions: isChinese ? '成立条件' : 'Counts when',
        excluded: isChinese ? '不成立时' : "Doesn't count when",
    };
};

const OutcomeTypeStandardsDialog = ({
    locale,
    activeOutcomeTypeLabel,
}: {
    locale: string;
    activeOutcomeTypeLabel: string | null;
}) => {
    const copy = getOutcomeTypeStandardsCopy(locale);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLaneKey, setSelectedLaneKey] = useState<LaborLaneKey>(
        getLaborLaneByLabel(activeOutcomeTypeLabel).key,
    );

    const handleOpen = () => {
        setSelectedLaneKey(getLaborLaneByLabel(activeOutcomeTypeLabel).key);
        setIsOpen(true);
    };

    const selectedLane = laborLanes.find((lane) => lane.key === selectedLaneKey) ?? laborLanes[0]!;

    return (
        <>
            <button
                type="button"
                aria-label={copy.triggerLabel}
                title={copy.triggerLabel}
                onClick={handleOpen}
                className="inline-flex h-4 items-center text-[11px] font-medium leading-4 text-[#667085] underline decoration-[#C8D0E2] underline-offset-2 transition-colors hover:text-[#1A162F] hover:decoration-[#1A162F] focus:outline-none focus-visible:text-[#1A162F] focus-visible:decoration-[#1A162F]"
            >
                {copy.triggerText}
            </button>

            <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={(
                    <>
                        <span className="block text-[15px] font-semibold leading-6 text-[#1A162F]">
                            {copy.title}
                        </span>
                        <span className="mt-1 block text-[12px] font-medium leading-5 text-[#667085]">
                            {copy.description}
                        </span>
                    </>
                )}
                variant="reference"
                width="w-[calc(100vw-32px)] max-w-[720px]"
                className="h-[calc(100vh-48px)] max-h-[540px]"
                titleClassName="min-w-0"
                headerClassName="px-5 py-4"
                bodyClassName="p-0"
                noPadding
                contentScrollable={false}
                closeOnOverlayClick
            >
                <div className="grid h-full min-h-0 grid-cols-1 overflow-hidden md:grid-cols-[200px_minmax(0,1fr)]">
                    <nav className="flex gap-2 overflow-x-auto border-b border-[#E8EBF3] bg-[#FBFCFE] p-3 md:flex-col md:overflow-x-visible md:border-b-0 md:border-r">
                        {laborLanes.map((lane) => {
                            const isActive = selectedLaneKey === lane.key;

                            return (
                                <button
                                    key={lane.key}
                                    type="button"
                                    onClick={() => setSelectedLaneKey(lane.key)}
                                    className={clsx(
                                        brandFocusClass,
                                        'h-9 shrink-0 whitespace-nowrap rounded-lg border px-3 text-left text-[12px] transition-colors',
                                        isActive
                                            ? 'border-[#1A162F] bg-white font-semibold text-[#1A162F]'
                                            : 'border-[#E8EBF3] bg-white font-medium text-[#667085] hover:border-[#BFC7D8] hover:text-[#1A162F]',
                                    )}
                                >
                                    {getLaneTitle(lane, locale)}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="min-h-0 overflow-y-auto p-4">
                        <div className="space-y-2.5">
                            {(['definition', 'conditions', 'excluded'] as const).map((key) => {
                                const label = key === 'definition'
                                    ? copy.definition
                                    : key === 'conditions'
                                        ? copy.conditions
                                        : copy.excluded;

                                return (
                                    <section key={key} className="rounded-xl border border-[#E8EBF3] bg-white p-3.5">
                                        <h5 className="text-[12px] font-semibold leading-5 text-[#1A162F]">
                                            {label}
                                        </h5>
                                        <p className="mt-1.5 text-[13px] font-normal leading-6 text-[#4E586A] [overflow-wrap:anywhere]">
                                            {getLaneRuleText(selectedLane, key, locale)}
                                        </p>
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

const getClassificationReason = (
    lane: LaborLane,
    systems: string,
    scope: string,
    locale: string,
) => {
    if (isChineseLocale(locale)) {
        if (lane.key === 'execute') {
            return systems === '-'
                ? `Bustly 已围绕「${scope}」交付可检查的运营动作或资产，并保留来源任务记录。`
                : `Bustly 已围绕「${scope}」交付可检查的运营动作或资产，并留下 ${systems} 的记录。`;
        }
        if (lane.key === 'judge') return 'Bustly 把数据、页面状态或业务信号整理成可行动的结论，不只是复述原始信息。';
        if (lane.key === 'watch') return 'Bustly 持续检查约定对象或外部信号，并把巡检结果保留在这条成果中。';
        return 'Bustly 已定位阻塞、尝试可行路径，并把问题交接到成员可以继续处理的下一步。';
    }

    if (lane.key === 'execute') {
        return systems === '-'
            ? `Bustly delivered a checkable operating action or asset for "${scope}" and kept the source task record.`
            : `Bustly delivered a checkable operating action or asset for "${scope}", with work records in ${systems}.`;
    }
    if (lane.key === 'judge') return 'Bustly turned data, page state, or business signals into an actionable conclusion instead of only restating raw information.';
    if (lane.key === 'watch') return 'Bustly kept watch on the agreed object or external signal and saved the monitoring result in this outcome.';
    return 'Bustly identified the blocker, tried viable paths, and handed off a next step the user can act on.';
};

const appendResultState = (copy: string, state: string, locale: string) => (
    `${String(copy || '').replace(/[。；;，,\s]+$/gu, '')}${isChineseLocale(locale) ? '；' : '; '}${state}`
);

const getCompletionResultText = (
    outcome: ValueLedgerOutcome,
    lane: LaborLane,
    locale: string,
) => {
    const productResult = getDerivedFieldText(outcome.detail?.completionResultText, locale);
    if (productResult !== '-') return productResult;

    const copy = formatLocalizedSourceTaskText(localizeDerivedLedgerText(outcome.summary, locale), locale);
    const status = outcome.acceptanceStatus;

    if (isChineseLocale(locale)) {
        if (status.includes('待用户') || status.includes('待成员') || status.includes('待 Member')) return appendResultState(copy, '成员现在可以确认是否发布、应用或归档。', locale);
        if (status.includes('持续巡检')) return appendResultState(copy, '已保留为本周期巡检成果。', locale);
        if (lane.key === 'unblock' || status.includes('需用户') || status.includes('需成员') || status.includes('需 Member')) return appendResultState(copy, '阻塞已定位，下一步已交接。', locale);
        return appendResultState(copy, '已保留为可追溯的运营成果。', locale);
    }

    if (status.includes('待用户') || status.includes('待成员') || status.includes('待 Member')) return `${copy.replace(/[.;,\s]+$/gu, '')}; the user can now confirm whether to publish, apply, or archive it.`;
    if (status.includes('持续巡检')) return `${copy.replace(/[.;,\s]+$/gu, '')}; saved as monitoring work for this cycle.`;
    if (lane.key === 'unblock' || status.includes('需用户') || status.includes('需成员') || status.includes('需 Member')) return `${copy.replace(/[.;,\s]+$/gu, '')}; blocker identified and next step handed off.`;
    return `${copy.replace(/[.;,\s]+$/gu, '')}; saved as a traceable operating outcome.`;
};

const getSystemEvidenceMeta = (outcome: ValueLedgerOutcome, locale: string) => {
    const productEvidence = outcome.detail?.systemEvidence;
    const memberMessages = formatSourceTaskNumber(productEvidence?.memberMessageCount ?? outcome.memberMessageCount);
    const assistantMessages = formatSourceTaskNumber(productEvidence?.assistantMessageCount ?? outcome.assistantMessageCount);
    const actionCount = formatSourceTaskNumber(productEvidence?.toolCallCount ?? outcome.executionActionCount);

    return isChineseLocale(locale)
        ? `成员 ${memberMessages} 条消息；Bustly ${assistantMessages} 条回复；${actionCount} 次执行动作`
        : `${memberMessages} user messages; ${assistantMessages} Bustly replies; ${actionCount} actions`;
};

const getBillingConditionSteps = (
    outcome: ValueLedgerOutcome,
    lane: LaborLane,
    systems: EvidenceSystemVisual[],
    scope: string,
    locale: string,
) => {
    const isChinese = isChineseLocale(locale);

    return [
        {
            key: 'business-object' as const,
            label: isChinese ? '处理范围' : 'Scope',
            value: scope,
            iconName: 'store',
        },
        {
            key: 'system-evidence' as const,
            label: isChinese ? '涉及应用' : 'Integrations',
            value: <SystemEvidenceSummary systems={systems} meta={getSystemEvidenceMeta(outcome, locale)} />,
            iconName: 'connect',
        },
        {
            key: 'completion-result' as const,
            label: isChinese ? '结果与下一步' : 'Result and next step',
            value: getCompletionResultText(outcome, lane, locale),
            iconName: 'complete',
        },
    ] satisfies EstablishmentConditionStep[];
};

const getOutcomeDetailCopy = (locale: string) => {
    const isChinese = isChineseLocale(locale);

    return {
        systemsEvidence: isChinese ? '涉及应用' : 'Integrations',
        taskEvidence: isChinese ? '来源任务' : 'Source task',
    };
};

const stripTraceRecordSuffix = (value: string | null | undefined, locale: string) => (
    formatLocalizedSourceTaskText(value, locale).replace(/\s*(?:trace record|追溯记录)$/iu, '').trim()
);

const formatOutcomeCardTitle = (outcome: ValueLedgerOutcome, locale: string) => (
    stripTraceRecordSuffix(getDerivedTextOrFallback(outcome.cardTitle, locale, localizeDerivedLedgerText(outcome.title, locale) ?? ''), locale)
);

const formatOutcomeCardDescription = (outcome: ValueLedgerOutcome, locale: string) => {
    const productDescription = getDerivedFieldText(outcome.cardDescription, locale);
    if (productDescription !== '-') return productDescription;

    const concreteOutput = getDerivedFieldText(outcome.detail?.concreteOutput, locale);
    if (concreteOutput !== '-') {
        return isChineseLocale(locale)
            ? `Bustly 已记录结果：${concreteOutput}`
            : `Bustly recorded this result: ${concreteOutput}`;
    }

    const lane = getLaborLane(outcome);
    const scope = getOutcomeScopeValue(outcome, locale);
    const systems = formatVisibleEvidenceSystemsText(getOutcomeEvidenceSystemValues(outcome));

    if (isChineseLocale(locale)) {
        if (lane.key === 'execute') {
            return systems === '-'
                ? `Bustly 已围绕「${scope}」完成可检查的运营动作或资产，并保留来源任务记录。`
                : `Bustly 已围绕「${scope}」完成可检查的运营动作或资产，并留下 ${systems} 的工作记录。`;
        }
        if (lane.key === 'judge') return `Bustly 已围绕「${scope}」整理判断结果，给出可继续行动的结论。`;
        if (lane.key === 'watch') return `Bustly 已围绕「${scope}」完成巡检，并保留本周期的发现和处理记录。`;
        return `Bustly 已围绕「${scope}」定位卡点，并把下一步交接给成员。`;
    }

    if (lane.key === 'execute') {
        return systems === '-'
            ? `Bustly completed checkable operating work for "${scope}" and kept the source task record.`
            : `Bustly completed checkable operating work for "${scope}" and kept records in ${systems}.`;
    }
    if (lane.key === 'judge') return `Bustly turned "${scope}" into an actionable conclusion.`;
    if (lane.key === 'watch') return `Bustly monitored "${scope}" and saved this cycle's findings.`;
    return `Bustly identified the blocker for "${scope}" and handed off the next workable step.`;
};

const formatOutcomeCardEvidenceSummary = (outcome: ValueLedgerOutcome) => {
    const systems = formatVisibleEvidenceSystemsText(getOutcomeEvidenceSystemValues(outcome));
    return systems;
};

const OutcomeCardEvidenceSummary = ({
    outcome,
}: {
    outcome: ValueLedgerOutcome;
}) => {
    const systems = getEvidenceSystemVisuals(getOutcomeEvidenceSystemValues(outcome));
    const fallback = formatOutcomeCardEvidenceSummary(outcome);
    const visibleSystems = systems.slice(0, 2);
    const hiddenSystems = systems.slice(2);

    return (
        <div className="flex h-7 min-w-0 max-w-full flex-1 items-center gap-1.5 overflow-hidden whitespace-nowrap">
            <span className="sr-only">{fallback}</span>
            {visibleSystems.length > 0 ? (
                visibleSystems.map((system) => (
                    <EvidenceSystemChip key={system.key} system={system} compact singleLine keepLabelFull />
                ))
            ) : (
                <span className="shrink-0 text-[12px] font-medium text-[#8A93B2]">--</span>
            )}
            {hiddenSystems.length > 0 && (
                <span
                    className="inline-flex h-7 shrink-0 items-center rounded-lg border border-[#E1E6EF] bg-white px-2 text-[11px] font-medium text-[#667085]"
                    title={hiddenSystems.map((system) => system.label).join(' / ')}
                >
                    ...
                </span>
            )}
        </div>
    );
};

const getOutcomeCardSourceTaskMetaItems = (outcome: ValueLedgerOutcome, locale: string, timezone: string) => {
    const completedAt = formatSourceTaskDateTime(outcome.sourceTaskCompletedAt, locale, timezone);
    return [completedAt].filter((item) => item && item !== '-');
};

const getOutcomeSourceUserDisplay = (outcome: ValueLedgerOutcome) => {
    const email = (outcome.sourceUserEmail || '').trim();
    const name = (outcome.sourceUserName || '').trim() || email.split('@')[0]?.trim() || '-';

    return {
        name,
        email,
    };
};

const sanitizeTraceConversationText = (value: string) => (
    value
        .replace(/\[\[reply_to_current\]\]\s*/g, '')
        .trim()
);

const getTraceConversationText = (message: TraceConversationMessage) => (
    message.excerpt
);

const isMemberTraceMessage = (message: TraceConversationMessage) => {
    const role = message.role.toLowerCase();
    return role.includes('member') || role.includes('user');
};

const getVisibleTraceConversationMessages = (messages: TraceConversationMessage[]) => (
    messages.filter((message) => {
        const text = sanitizeTraceConversationText(getTraceConversationText(message));
        return Boolean(text);
    })
);

const getSourceTaskDetailCopy = (locale: string) => {
    const isChinese = isChineseLocale(locale);

    return {
        detailEyebrow: isChinese ? '来源任务' : 'Source task',
        overviewTitle: isChinese ? '任务里发生了什么' : 'What happened in this task',
        originalConversationTitle: isChinese ? '任务对话' : 'Task conversation',
        conversationRecords: isChinese ? '对话' : 'Conversation',
        laborOutcomes: isChinese ? '形成的成果' : 'Outcomes created',
        bustlyExecution: isChinese ? 'Bustly 动作' : 'Bustly actions',
        noConversation: isChinese ? '这条快照没有可展示的任务对话。' : 'No task conversation is available in this snapshot.',
        recordsUnit: isChinese ? '条对话' : 'messages',
        outcomesUnit: isChinese ? '个成果' : 'outcomes',
    };
};

const formatSourceTaskRecordCount = (value: number | null | undefined, locale: string) => (
    value === null || value === undefined
        ? '-'
        : `${formatNumber(value)} ${getSourceTaskDetailCopy(locale).recordsUnit}`
);

const formatTraceRecordCount = (value: number | null | undefined, locale: string) => {
    if (value === null || value === undefined) return '-';
    return isChineseLocale(locale)
        ? `${formatNumber(value)} 条来源任务记录`
        : `${formatNumber(value)} source task records`;
};

const formatSourceTaskOutcomeCount = (value: number | null | undefined, locale: string) => {
    if (value === null || value === undefined) return '-';
    return `${formatNumber(value)} ${getSourceTaskDetailCopy(locale).outcomesUnit}`;
};

const TraceConversationBubble = ({
    message,
    locale,
    timezone,
}: {
    message: TraceConversationMessage;
    locale: string;
    timezone: string;
}) => {
    const isMember = isMemberTraceMessage(message);
    const text = sanitizeTraceConversationText(getTraceConversationText(message));
    const roleLabel = isMember ? (isChineseLocale(locale) ? '成员请求' : 'User request') : 'Bustly';

    if (!text) return null;

    return (
        <article className={clsx('flex min-w-0 gap-3', isMember ? 'justify-end' : 'items-start')}>
            {!isMember && (
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1A162F] text-[11px] font-semibold text-white">
                    B
                </div>
            )}
            <div
                className={clsx(
                    'min-w-0 rounded-xl border px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.02)]',
                    isMember ? 'max-w-[78%] border-[#DDE7F4] bg-[#F5F8FC]' : 'flex-1 border-[#E8EBF3] bg-white',
                )}
            >
                <div className={clsx('mb-2 flex flex-wrap items-center gap-2 text-[11px]', isMember ? 'justify-end' : 'justify-start')}>
                    <span className="font-semibold text-[#4E586A]">{roleLabel}</span>
                    <span className="font-normal text-[#8A93A5]">{formatDateTime(message.messageAt, locale, timezone)}</span>
                </div>
                <div
                    className={clsx(
                        'whitespace-pre-wrap text-[13px] leading-6 text-[#273044] [overflow-wrap:anywhere]',
                        isMember ? 'font-medium' : 'font-normal',
                    )}
                    dir="auto"
                >
                    {text}
                </div>
            </div>
            {isMember ? (
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#DDE7F4] bg-white text-[11px] font-semibold text-[#4E586A]">
                    M
                </div>
            ) : null}
        </article>
    );
};

const SourceTaskConversationBlock = ({
    messages,
    task,
    outcome,
    locale,
    timezone,
}: {
    messages: TraceConversationMessage[];
    task: ValueLedgerTask | null;
    outcome: ValueLedgerOutcome;
    locale: string;
    timezone: string;
}) => {
    const copy = getSourceTaskDetailCopy(locale);
    const visibleMessages = getVisibleTraceConversationMessages(messages);
    const recordCount = visibleMessages.length;
    const conversationRange = formatSourceTaskDateRange(
        task?.startedAt,
        task?.completedAt ?? outcome.sourceTaskCompletedAt,
        locale,
        timezone,
    );

    return (
        <div>
            <div className="mb-3 flex flex-col gap-2 rounded-lg border border-[#E8EBF3] bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <strong className="block text-[12px] font-medium text-[#1A162F]">
                        {copy.conversationRecords}
                    </strong>
                    <span className="text-[11px] font-medium text-[#8A93B2]">
                        {formatSourceTaskRecordCount(recordCount, locale)}
                    </span>
                </div>
                <span className="text-[11px] font-medium text-[#8A93B2]">{conversationRange}</span>
            </div>

            {visibleMessages.length > 0 ? (
                <div className="space-y-3">
                    {visibleMessages.map((message, index) => (
                        <TraceConversationBubble
                            key={`${message.messageAt}-${message.role}-${index}`}
                            message={message}
                            locale={locale}
                            timezone={timezone}
                        />
                    ))}
                </div>
            ) : (
                <DetailTextBlock label={copy.conversationRecords} value={copy.noConversation} />
            )}
        </div>
    );
};

const OutcomeDetailLoadingSkeleton = () => (
    <div className="space-y-5" aria-busy="true">
        {[0, 1].map((section) => (
            <section key={section} className="space-y-3">
                <div className="h-5 w-40 animate-pulse rounded-full bg-[#E8EDF5]" />
                <div className="rounded-lg border border-[#E8EBF3] bg-white p-3">
                    <div className="h-4 w-11/12 animate-pulse rounded-full bg-[#E8EDF5]" />
                    <div className="mt-2 h-4 w-4/5 animate-pulse rounded-full bg-[#E8EDF5]" />
                    <div className="mt-2 h-4 w-2/3 animate-pulse rounded-full bg-[#E8EDF5]" />
                </div>
            </section>
        ))}
        <section className="space-y-3">
            <div className="h-5 w-44 animate-pulse rounded-full bg-[#E8EDF5]" />
            <div className="space-y-3">
                {[0, 1, 2].map((item) => (
                    <div key={item} className="flex gap-3">
                        <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-[#EEF2F7]" />
                        <div className="flex-1 rounded-lg border border-[#E8EBF3] bg-white p-3">
                            <div className="h-4 w-28 animate-pulse rounded-full bg-[#E8EDF5]" />
                            <div className="mt-2 h-4 w-5/6 animate-pulse rounded-full bg-[#E8EDF5]" />
                        </div>
                    </div>
                ))}
            </div>
        </section>
    </div>
);

const OutcomeDetailFullLoadingSkeleton = () => (
    <div className="flex min-h-[620px] min-w-0 flex-col bg-white xl:min-h-0" aria-busy="true">
        <header className="shrink-0 border-b border-[#E8EBF3] px-6 py-5 space-y-4">
            {/* Title Skeleton */}
            <div className="h-7 w-2/3 animate-pulse rounded-full bg-[#E8EDF5]" />

            {/* Description Skeleton */}
            <div className="space-y-2 max-w-5xl">
                <div className="h-4 w-full animate-pulse rounded-full bg-[#E8EDF5]" />
                <div className="h-4 w-11/12 animate-pulse rounded-full bg-[#E8EDF5]" />
            </div>

            {/* Member/User Skeleton */}
            <div className="flex items-center gap-2">
                <div className="h-4 w-12 animate-pulse rounded-full bg-[#E8EDF5]" />
                <div className="h-4 w-32 animate-pulse rounded-full bg-[#E8EDF5]" />
            </div>

            {/* Info Cards Skeleton */}
            <div className="pt-1">
                <div className="grid gap-3 md:grid-cols-2">
                    {[0, 1].map((index) => (
                        <div key={index} className="rounded-lg border border-[#E8EBF3] bg-white p-3 space-y-2">
                            <div className="h-3 w-16 animate-pulse rounded-full bg-[#E8EDF5]" />
                            <div className="h-4 w-28 animate-pulse rounded-full bg-[#E8EDF5]" />
                        </div>
                    ))}
                    <div className="md:col-span-2 rounded-lg border border-[#E8EBF3] bg-white p-3 space-y-2">
                        <div className="h-3 w-20 animate-pulse rounded-full bg-[#E8EDF5]" />
                        <div className="h-4 w-1/2 animate-pulse rounded-full bg-[#E8EDF5]" />
                    </div>
                </div>
            </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-5">
            <OutcomeDetailLoadingSkeleton />
        </div>
    </div>
);

const OutcomeDetailWorkbenchPanel = ({
    outcome,
    isDetailLoading = false,
    detailLoadError = null,
    locale,
    t,
    timezone,
}: {
    outcome: ValueLedgerOutcome | null;
    isDetailLoading?: boolean;
    detailLoadError?: string | null;
    activeTab?: LedgerDetailTab;
    onTabChange?: (tab: LedgerDetailTab) => void;
    locale: string;
    t: Translate;
    timezone: string;
}) => {
    if (isDetailLoading) {
        return <OutcomeDetailFullLoadingSkeleton />;
    }

    if (!outcome) {
        return (
            <section className="flex min-h-[620px] items-center justify-center bg-white px-6 py-10">
                <div className="max-w-sm text-center">
                    <ListChecks size={28} weight="regular" className="mx-auto text-[#AAB4C4]" />
                    <h2 className="mt-4 text-[18px] font-medium text-[#1A162F]">
                        {t('website.valueLedger.emptyTitle')}
                    </h2>
                    <p className="mt-2 text-[13px] leading-6 text-[#667085]">
                        {t('website.valueLedger.emptyDescription')}
                    </p>
                </div>
            </section>
        );
    }

    const detailCopy = getOutcomeDetailCopy(locale);
    const laborLane = getLaborLane(outcome);
    const evidenceSystemValues = getOutcomeEvidenceSystemValues(outcome);
    const evidenceSystemVisuals = getOutcomeEvidenceSystemVisuals(outcome);
    const evidenceSystems = formatVisibleEvidenceSystemsText(evidenceSystemValues);
    const sourceTaskTitle = formatLocalizedSourceTaskText(outcome.detail?.detailSourceTask?.title ?? outcome.sourceTaskTitle, locale);
    const sourceTaskTime = formatSourceTaskDateTime(
        outcome.detail?.detailSourceTask?.lastActivityAt ?? outcome.sourceTaskCompletedAt,
        locale,
        timezone,
    );
    const outcomeTitle = getDerivedTextOrFallback(outcome.detailTitle, locale, localizeDerivedLedgerText(outcome.title, locale) ?? '');
    const outcomeDescription = getDerivedTextOrFallback(outcome.detail?.outcomeDescription, locale, formatOutcomeCardDescription(outcome, locale));
    const sourceUser = getOutcomeSourceUserDisplay(outcome);
    const concreteOutput = getDerivedTextOrFallback(
        outcome.detail?.concreteOutput,
        locale,
        formatSourceTaskText(localizeDerivedLedgerText(outcome.summary, locale)),
    );
    const sourceTaskValue = (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 [overflow-wrap:anywhere]">{sourceTaskTitle}</span>
            {sourceTaskTime !== '-' && (
                <>
                    <span className="h-3 w-px shrink-0 bg-[#DDE3EA]" aria-hidden="true" />
                    <span className="shrink-0 text-[#667085]">{sourceTaskTime}</span>
                </>
            )}
        </span>
    );
    const businessScopeValue = formatLocalizedSourceTaskText(getOutcomeScopeDisplayValue(outcome), locale);
    const classificationReason = formatLocalizedSourceTaskText(
        localizeDerivedLedgerText(
            (isChineseLocale(locale)
                ? outcome.detail?.classificationBasis?.localizedReasonText?.zh
                : outcome.detail?.classificationBasis?.localizedReasonText?.en)
                ?? outcome.detail?.classificationBasis?.reasonText
                ?? getClassificationReason(laborLane, evidenceSystems, businessScopeValue, locale),
            locale,
        ),
        locale,
    );
    const typeDefinition = formatLocalizedSourceTaskText(
        localizeDerivedLedgerText(
            (isChineseLocale(locale)
                ? outcome.detail?.classificationBasis?.localizedTypeDefinition?.zh
                : outcome.detail?.classificationBasis?.localizedTypeDefinition?.en)
                ?? outcome.detail?.classificationBasis?.typeDefinition
                ?? getLaneRuleText(laborLane, 'definition', locale),
            locale,
        ),
        locale,
    );
    const outcomeInformationItems = [
        {
            label: t('website.valueLedger.outcomeType'),
            value: getOutcomeDetailTypeLabel(outcome, locale),
        },
        {
            label: t('website.valueLedger.businessModule'),
            value: getOutcomeDetailBusinessAreaLabel(outcome, locale),
        },
        {
            label: detailCopy.taskEvidence,
            value: sourceTaskValue,
        },
    ];
    const billingConditionSteps = getBillingConditionSteps(
        outcome,
        laborLane,
        evidenceSystemVisuals,
        businessScopeValue,
        locale,
    );

    return (
        <section className="flex min-h-[620px] min-w-0 flex-col bg-white xl:min-h-0">
            <header className="shrink-0 border-b border-[#E8EBF3] px-6 py-5">
                <h2 className="text-[20px] font-medium leading-7 tracking-[-0.01em] text-[#1A162F] max-w-5xl">
                    {outcomeTitle}
                </h2>

                <p className="mt-3 max-w-5xl text-[13px] font-medium leading-6 text-[#4E586A] [overflow-wrap:anywhere]">
                    {outcomeDescription}
                </p>

                <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-5">
                    <span className="shrink-0 font-semibold text-[#4E586A]">
                        {formatInlineFieldLabel(t('website.valueLedger.memberShort'), locale)}
                    </span>
                    <span className="min-w-0 truncate font-medium text-[#1A162F]" title={sourceUser.name}>
                        {sourceUser.name}
                    </span>
                    {sourceUser.email ? (
                        <span className="min-w-0 truncate font-normal text-[#667085]" title={sourceUser.email}>
                            {sourceUser.email}
                        </span>
                    ) : null}
                </div>

                <div className="mt-5">
                    <div className="grid gap-3 md:grid-cols-2">
                        {outcomeInformationItems.map((item, index) => (
                            <DetailInfoCard
                                key={item.label}
                                label={item.label}
                                value={item.value}
                                className={index === 2 ? 'md:col-span-2' : undefined}
                            />
                        ))}
                    </div>
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-5" data-outcome-detail-scroll-body="true">
                <div className="space-y-5">
                    {detailLoadError ? (
                        <div
                            className="rounded-lg border border-[#F5C2C7] bg-[#FFF5F5] px-3 py-2 text-[12px] font-medium leading-5 text-[#A33A3A]"
                        >
                            {t('website.valueLedger.detailLoadError')}
                        </div>
                    ) : null}

                    <DetailSection title={t('website.valueLedger.concreteOutput')}>
                        <div className="rounded-lg border border-[#E8EBF3] bg-white p-3 text-[13px] font-medium leading-6 text-[#1A162F] [overflow-wrap:anywhere]">
                            {concreteOutput}
                        </div>
                    </DetailSection>

                    <DetailSection title={t('website.valueLedger.classificationBasis')}>
                        <div className="rounded-lg border border-[#E8EBF3] bg-white p-3 text-[12px] font-normal leading-5 [overflow-wrap:anywhere]">
                            <strong className="block font-medium text-[#1A162F]">{classificationReason}</strong>
                            <span className="mt-1 block text-[#666F8D]">{typeDefinition}</span>
                        </div>
                    </DetailSection>

                    <DetailSection title={t('website.valueLedger.establishmentConditions')}>
                        <EvidenceChainSteps steps={billingConditionSteps} />
                    </DetailSection>
                </div>
            </div>
        </section>
    );
};

/* ─── OutcomeCard (Grid card style) ─── */
const OutcomeCard = ({
    outcome,
    isSelected,
    isViewed,
    onClick,
    locale,
    t,
    timezone,
}: {
    outcome: ValueLedgerOutcome;
    isSelected: boolean;
    isViewed: boolean;
    onClick: () => void;
    locale: string;
    t: Translate;
    timezone: string;
}) => {
    const outcomeCardCopy = getOutcomeDetailCopy(locale);
    const cardTitle = formatOutcomeCardTitle(outcome, locale);
    const cardDescription = formatOutcomeCardDescription(outcome, locale);
    const sourceTaskLabel = formatLocalizedSourceTaskText(outcome.sourceTaskTitle, locale);
    const sourceTaskMetaItems = getOutcomeCardSourceTaskMetaItems(outcome, locale, timezone);
    const sourceUser = getOutcomeSourceUserDisplay(outcome);
    return (
        <button
            type="button"
            onClick={onClick}
            className={clsx(
                "relative flex flex-1 min-w-0 max-w-full flex-col overflow-hidden rounded-xl border p-5 text-left transition-colors duration-150 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 active:shadow-none [-webkit-tap-highlight-color:transparent]",
                isSelected
                    ? "border-[#1A162F] bg-[#FAFBFD] shadow-none"
                    : isViewed
                        ? "border-[#DDE3EA] bg-[#FBFCFE] shadow-none hover:border-[#BFC6D4] hover:bg-[#FAFBFD]"
                        : "border-[#E8EBF3] bg-white shadow-none hover:border-[#BFC6D4] hover:bg-[#FAFBFD]"
            )}
        >
            {isViewed && !isSelected ? (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 top-5 h-8 w-0.5 rounded-r-full bg-[#C7D8FF]"
                />
            ) : null}
            <div className="min-w-0 flex-1">
                <h3 className="overflow-hidden text-[12px] font-semibold leading-5 text-[#1A162F] [display:-webkit-box] [overflow-wrap:anywhere] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                    {cardTitle}
                </h3>
                <p className="mt-1.5 overflow-hidden text-[11px] font-normal leading-5 text-[#666F8D] [display:-webkit-box] [overflow-wrap:anywhere] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                    {cardDescription}
                </p>
                <div className="mt-2.5 flex min-w-0 items-center gap-2 text-[10px] leading-4">
                    <span className="shrink-0 font-semibold text-[#4E586A]">
                        {formatInlineFieldLabel(t('website.valueLedger.memberShort'), locale)}
                    </span>
                    <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden whitespace-nowrap">
                        <span className="shrink-0 max-w-[120px] truncate font-medium text-[#1A162F]" title={sourceUser.name}>
                            {sourceUser.name}
                        </span>
                        {sourceUser.email ? (
                            <span className="min-w-0 truncate font-normal text-[#667085]" title={sourceUser.email}>
                                {sourceUser.email}
                            </span>
                        ) : null}
                    </span>
                </div>
            </div>

            <div className="mt-3 w-full border-t border-[#F1F4F9] pt-2.5">
                <div className="flex min-h-[28px] min-w-0 max-w-full items-center gap-2 overflow-hidden text-[11px] font-semibold leading-4 text-[#1A162F] [overflow-wrap:anywhere]">
                    <span className="shrink-0 text-[11px] font-semibold text-[#4E586A]">
                        {formatInlineFieldLabel(outcomeCardCopy.systemsEvidence, locale)}
                    </span>
                    <OutcomeCardEvidenceSummary outcome={outcome} />
                </div>

                <div className="mt-2 flex min-h-[34px] min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap text-[10px] font-medium leading-4 text-[#667085]">
                    <span className="shrink-0 font-semibold text-[#4E586A]">
                        {formatInlineFieldLabel(t('website.valueLedger.sourceTask'), locale)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[#273044]" title={sourceTaskLabel}>
                        {sourceTaskLabel}
                    </span>
                    {sourceTaskMetaItems.map((item) => (
                        <React.Fragment key={item}>
                            <span className="h-3 w-px shrink-0 bg-[#DDE3EA]" aria-hidden="true" />
                            <span className="shrink-0 text-[#667085]">
                                {item}
                            </span>
                        </React.Fragment>
                    ))}
                </div>
            </div>

        </button>
    );
};

/* ─── Main Page Component ─── */

const formatCountUnit = (unit: 'task' | 'outcome', locale: string) => {
    if (isChineseLocale(locale)) {
        return unit === 'task' ? '个' : '项';
    }
    return unit === 'task' ? 'tasks' : 'outcomes';
};

const outcomeOverviewMetricToneClass: Record<OutcomeOverviewMetricTone, string> = {
    blue: 'border-[#E8EBF3] bg-[#FAFBFC]/60 hover:bg-[#FAFBFC] hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)] transition-all duration-200',
    green: 'border-[#E8EBF3] bg-[#FAFBFC]/60 hover:bg-[#FAFBFC] hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)] transition-all duration-200',
    amber: 'border-[#E8EBF3] bg-[#FAFBFC]/60 hover:bg-[#FAFBFC] hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)] transition-all duration-200',
    neutral: 'border-[#E8EBF3] bg-[#FAFBFC]/60 hover:bg-[#FAFBFC] hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)] transition-all duration-200',
};

const outcomeOverviewMetricIconToneClass: Record<OutcomeOverviewMetricTone, string> = {
    blue: 'border-[#C7D8FF] bg-[#EAF0FF] text-[#2F63D6]',
    green: 'border-[#CFF0DE] bg-[#ECFDF3] text-[#14904A]',
    amber: 'border-[#FAD7A0] bg-[#FFF5E6] text-[#B54708]',
    neutral: 'border-[#DDE3EA] bg-white text-[#667085]',
};

const formatOutcomeInsightText = (value: string | null | undefined, locale: string) => (
    value ? formatOutcomeGeneratedCopyText(value, locale) : '-'
);

const toInsightCardItems = (
    cards: ValueLedgerOverviewInsights['efficiencyHighlights'],
    placeholderCount: number,
    keyPrefix: string,
): OutcomeInsightCardItem[] => {
    const source = cards.length > 0
        ? cards
        : Array.from({ length: placeholderCount }, () => ({ title: null, description: null }));

    return source.map((card, index) => ({
        key: `${keyPrefix}-${card.title ?? index}`,
        title: card.title,
        description: card.description,
    }));
};

const toChampionInsightItems = (
    champions: ValueLedgerOverviewInsights['champions'],
): OutcomeChampionInsightItem[] => {
    const source = champions.length > 0
        ? champions
        : Array.from(
            { length: OUTCOME_SUMMARY_CHAMPION_PLACEHOLDER_COUNT },
            () => ({ name: null, taskCount: null, focus: null }),
        );

    return source.map((champion, index) => ({
        key: `${champion.name ?? 'champion'}-${index}`,
        name: champion.name,
        taskCount: champion.taskCount,
        focus: champion.focus,
    }));
};

const formatChampionTaskBadge = (taskCount: number | null | undefined, locale: string) => (
    taskCount === null || taskCount === undefined
        ? '-'
        : `${formatOverviewMetricNumber(taskCount)} ${formatCountUnit('task', locale)}`
);

const OutcomeInsightCard = ({
    item,
    locale,
}: {
    item: OutcomeInsightCardItem;
    locale: string;
}) => {
    const isPlaceholder = !item.title;
    return (
        <div className={clsx(
            "min-w-0 rounded-xl border px-3.5 py-3 transition-all duration-200",
            isPlaceholder
                ? "border-[#E8EBF3]/40 bg-[#FAFBFC]/30"
                : "border-[#E8EBF3] bg-[#FAFBFC]/40 hover:bg-white hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)]"
        )}>
            <h4 className={clsx(
                "text-[12px] font-semibold leading-5 [overflow-wrap:anywhere]",
                isPlaceholder ? "text-[#7A8397]" : "text-[#1A162F]"
            )}>
                {formatOutcomeInsightText(item.title, locale)}
            </h4>
            <p className={clsx(
                "mt-1.5 text-[11px] font-medium leading-5 [overflow-wrap:anywhere]",
                isPlaceholder ? "text-[#AAB4C4]" : "text-[#667085]"
            )}>
                {formatOutcomeInsightText(item.description, locale)}
            </p>
        </div>
    );
};

const OutcomeInsightGroupPanel = ({
    title,
    description,
    checklist,
    locale,
}: {
    title: string;
    description: string | null;
    checklist: string[];
    locale: string;
}) => {
    const items = checklist.length > 0 ? checklist : ['-'];
    const isPlaceholder = !description;

    return (
        <div className={clsx(
            "rounded-xl border px-4 py-3.5 transition-all duration-200",
            isPlaceholder
                ? "border-[#E8EBF3]/40 bg-[#FAFBFC]/30"
                : "border-[#E8EBF3] bg-[#FAFBFC]/40 hover:bg-white hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)]"
        )}>
            <h4 className={clsx(
                "text-[13px] font-semibold leading-5 [overflow-wrap:anywhere]",
                isPlaceholder ? "text-[#7A8397]" : "text-[#1A162F]"
            )}>
                {title}
            </h4>
            <p className={clsx(
                "mt-1.5 text-[12px] font-medium leading-5 [overflow-wrap:anywhere]",
                isPlaceholder ? "text-[#AAB4C4]" : "text-[#667085]"
            )}>
                {formatOutcomeInsightText(description, locale)}
            </p>
            <ul className="mt-3 space-y-2">
                {items.map((item, index) => {
                    const isItemPlaceholder = item === '-';
                    return (
                        <li
                            key={`${item}-${index}`}
                            className={clsx(
                                'flex min-w-0 items-start gap-2 text-[11px] font-medium leading-5 [overflow-wrap:anywhere]',
                                isItemPlaceholder ? 'text-[#AAB4C4]' : 'text-[#4E586A]',
                            )}
                        >
                            <span className={clsx(
                                'mt-0.5 inline-flex size-4 shrink-0 items-center justify-center transition-colors',
                                isItemPlaceholder
                                    ? 'text-[#AAB4C4]'
                                    : 'text-[#2F63D6]',
                            )}>
                                <LibraryIcon
                                    name="complete"
                                    size={16}
                                    color={isItemPlaceholder ? '#AAB4C4' : '#2F63D6'}
                                />
                            </span>
                            <span className="min-w-0 [overflow-wrap:anywhere]">
                                {formatOutcomeInsightText(item, locale)}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const OutcomeChampionCard = ({
    item,
    locale,
}: {
    item: OutcomeChampionInsightItem;
    locale: string;
}) => {
    const isPlaceholder = !item.name;
    return (
        <div className={clsx(
            "min-w-0 rounded-xl border px-3.5 py-3 transition-all duration-200",
            isPlaceholder
                ? "border-[#E8EBF3]/40 bg-[#FAFBFC]/30"
                : "border-[#E8EBF3] bg-[#FAFBFC]/40 hover:bg-white hover:border-[#DDE3EA] hover:shadow-[0_2px_8px_rgba(0,0,0,0.015)]"
        )}>
            <div className="flex items-center justify-between">
                <span className={clsx(
                    "inline-flex h-5 items-center rounded-md border px-1.5 text-[10px] font-semibold transition-colors",
                    isPlaceholder
                        ? "border-[#E1E6EF] bg-[#F0F2F5] text-[#7A8397]"
                        : "border-[#C7D8FF] bg-[#EAF0FF] text-[#2F63D6]"
                )}>
                    {formatChampionTaskBadge(item.taskCount, locale)}
                </span>
            </div>
            <h4 className={clsx(
                "mt-2 text-[12px] font-semibold leading-5 [overflow-wrap:anywhere]",
                isPlaceholder ? "text-[#7A8397]" : "text-[#1A162F]"
            )}>
                {formatOutcomeInsightText(item.name, locale)}
            </h4>
            <p className={clsx(
                "mt-1 text-[11px] font-medium leading-5 [overflow-wrap:anywhere]",
                isPlaceholder ? "text-[#AAB4C4]" : "text-[#667085]"
            )}>
                {formatOutcomeInsightText(item.focus, locale)}
            </p>
        </div>
    );
};

const OutcomeSummaryInsightsPanel = ({
    insights,
    locale,
    t,
}: {
    insights: ValueLedgerOverviewInsights;
    locale: string;
    t: Translate;
}) => {
    const efficiencyHighlights = toInsightCardItems(
        insights.efficiencyHighlights,
        OUTCOME_SUMMARY_INSIGHT_PLACEHOLDER_COUNT,
        'efficiency-highlight',
    );
    const nextCycleFocus = toInsightCardItems(
        insights.nextCycleFocus,
        OUTCOME_SUMMARY_NEXT_FOCUS_PLACEHOLDER_COUNT,
        'next-cycle-focus',
    );
    const champions = toChampionInsightItems(insights.champions);
    const standardWorkflowTitle = insights.standardWorkflow.title
        ? formatOutcomeGeneratedCopyText(insights.standardWorkflow.title, locale)
        : t('website.valueLedger.overviewStandardWorkflowTitle');
    const reviewSuggestionTitle = insights.reviewSuggestion.title
        ? formatOutcomeGeneratedCopyText(insights.reviewSuggestion.title, locale)
        : t('website.valueLedger.overviewReviewSuggestionTitle');

    return (
        <div className="border-t border-[#E8EBF3] px-6 py-6">
            <div className="min-w-0">
                <h3 className="text-[15px] font-semibold leading-6 text-[#1A162F]">
                    {t('website.valueLedger.overviewInsightsTitle')}
                </h3>
                <p className="mt-1 text-[12px] font-medium leading-5 text-[#667085]">
                    {t('website.valueLedger.overviewInsightsDescription')}
                </p>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {efficiencyHighlights.map((item) => (
                    <OutcomeInsightCard key={item.key} item={item} locale={locale} />
                ))}
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <OutcomeInsightGroupPanel
                    title={standardWorkflowTitle}
                    description={insights.standardWorkflow.description}
                    checklist={insights.standardWorkflow.checklist}
                    locale={locale}
                />
                <OutcomeInsightGroupPanel
                    title={reviewSuggestionTitle}
                    description={insights.reviewSuggestion.description}
                    checklist={insights.reviewSuggestion.checklist}
                    locale={locale}
                />
            </div>

            <div className="mt-6 min-w-0">
                <h3 className="text-[15px] font-semibold leading-6 text-[#1A162F]">
                    {t('website.valueLedger.overviewChampionsTitle')}
                </h3>
                <p className="mt-1 text-[12px] font-medium leading-5 text-[#667085]">
                    {t('website.valueLedger.overviewChampionsDescription')}
                </p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {champions.map((item) => (
                    <OutcomeChampionCard key={item.key} item={item} locale={locale} />
                ))}
            </div>

            <div className="mt-6 min-w-0">
                <h3 className="text-[15px] font-semibold leading-6 text-[#1A162F]">
                    {t('website.valueLedger.overviewNextCycleTitle')}
                </h3>
                <p className="mt-1 text-[12px] font-medium leading-5 text-[#667085]">
                    {t('website.valueLedger.overviewNextCycleDescription')}
                </p>
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-3">
                {nextCycleFocus.map((item) => {
                    const isPlaceholder = !item.title;
                    return (
                        <div
                            key={item.key}
                            className={clsx(
                                "flex min-w-0 gap-3 rounded-xl border px-3.5 py-3 transition-all duration-200",
                                isPlaceholder
                                    ? "border-[#D9E4FF]/80 bg-[#F8FAFF]/60"
                                    : "border-[#B3CCFF] bg-[#F5F8FF] hover:bg-[#EAF0FF] hover:border-[#99BFFF] hover:shadow-[0_2px_8px_rgba(47,99,214,0.06)]"
                            )}
                        >
                            <span className={clsx(
                                "inline-flex size-6 shrink-0 items-center justify-center rounded-lg text-[13px] transition-colors",
                                isPlaceholder
                                    ? "bg-[#2F63D6]/10 text-[#2F63D6]"
                                    : "bg-[#2F63D6] text-white"
                            )}>
                                <Target size={13} weight="bold" />
                            </span>
                            <div className="min-w-0">
                                <h4 className={clsx(
                                    "text-[12px] font-semibold leading-5 [overflow-wrap:anywhere]",
                                    isPlaceholder ? "text-[#4E586A]/70" : "text-[#1A162F]"
                                )}>
                                    {formatOutcomeInsightText(item.title, locale)}
                                </h4>
                                <p className={clsx(
                                    "mt-1 text-[11px] font-medium leading-5 [overflow-wrap:anywhere]",
                                    isPlaceholder ? "text-[#667085]/55" : "text-[#667085]"
                                )}>
                                    {formatOutcomeInsightText(item.description, locale)}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const pdfReportAvoidBreakStyle: React.CSSProperties = {
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
};

const pdfReportBodyFontFamily = 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const pdfReportDisplayFontFamily = '"Iowan Old Style", Charter, Georgia, "Songti SC", "Noto Serif CJK SC", serif';
const pdfReportMonoFontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const pdfReportInkColor = '#1A162F';
const pdfReportMutedColor = '#667085';
const pdfReportLineColor = '#E8EBF3';
const pdfReportStrongLineColor = '#D6DDEB';
const pdfReportAccentColor = '#1F6E8C';
const pdfReportAccentSoftColor = '#EFF8FB';

const pdfReportMastMetaStyle: React.CSSProperties = {
    color: pdfReportMutedColor,
    fontFamily: pdfReportMonoFontFamily,
    fontSize: '9.5px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    lineHeight: 1.45,
    textTransform: 'uppercase',
    overflowWrap: 'anywhere',
};

const pdfReportSectionTitleStyle: React.CSSProperties = {
    margin: 0,
    color: pdfReportInkColor,
    fontFamily: pdfReportDisplayFontFamily,
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
};

const pdfReportSectionDescriptionStyle: React.CSSProperties = {
    margin: '6px 0 0',
    color: pdfReportMutedColor,
    fontSize: '11.5px',
    fontWeight: 500,
    lineHeight: 1.65,
    overflowWrap: 'anywhere',
};

const pdfReportCardStyle: React.CSSProperties = {
    ...pdfReportAvoidBreakStyle,
    border: `1px solid ${pdfReportLineColor}`,
    borderRadius: '10px',
    padding: '16px 18px',
    boxSizing: 'border-box',
};

const chunkPdfReportRows = <T,>(items: T[], itemsPerRow = 2) => (
    items.reduce<T[][]>((rows, item, index) => {
        if (index % itemsPerRow === 0) rows.push([]);
        rows[rows.length - 1].push(item);
        return rows;
    }, [])
);

const OutcomeSummaryPdfReport = ({
    workspaceName,
    reportOwner,
    updatedAt,
    rangeContextLabel,
    rangeDisplayText,
    headline,
    description,
    metricCards,
    valueJudgmentTitle,
    valueJudgmentDescription,
    taskAreas,
    insights,
    locale,
    t,
}: {
    workspaceName: string;
    reportOwner: string;
    updatedAt: string;
    rangeContextLabel: string;
    rangeDisplayText: string;
    headline: string;
    description: string;
    metricCards: OutcomeOverviewMetricCard[];
    valueJudgmentTitle: string;
    valueJudgmentDescription: string;
    taskAreas: OutcomeOverviewTaskAreaRow[];
    insights: ValueLedgerOverviewInsights;
    locale: string;
    t: Translate;
}) => {
    const isChinese = isChineseLocale(locale);
    const reportTaskAreas = taskAreas.length > 0
        ? taskAreas
        : [{
            key: 'placeholder',
            scenarioLabel: null,
            taskCount: null,
            activeMemberCount: null,
            representativeWork: null,
        }];
    const efficiencyHighlights = toInsightCardItems(
        insights.efficiencyHighlights,
        OUTCOME_SUMMARY_INSIGHT_PLACEHOLDER_COUNT,
        'pdf-efficiency-highlight',
    );
    const nextCycleFocus = toInsightCardItems(
        insights.nextCycleFocus,
        OUTCOME_SUMMARY_NEXT_FOCUS_PLACEHOLDER_COUNT,
        'pdf-next-cycle-focus',
    );
    const champions = toChampionInsightItems(insights.champions);
    const standardWorkflowTitle = insights.standardWorkflow.title
        ? formatOutcomeGeneratedCopyText(insights.standardWorkflow.title, locale)
        : t('website.valueLedger.overviewStandardWorkflowTitle');
    const reviewSuggestionTitle = insights.reviewSuggestion.title
        ? formatOutcomeGeneratedCopyText(insights.reviewSuggestion.title, locale)
        : t('website.valueLedger.overviewReviewSuggestionTitle');
    const periodText = [rangeContextLabel, rangeDisplayText].filter(Boolean).join(' · ');
    const headerMeta = [workspaceName, rangeContextLabel, rangeDisplayText].filter(Boolean).join(' / ');
    const reportTitle = isChinese ? '成果摘要报告' : 'Outcome Summary Report';
    const reportBadge = isChinese ? '工作区报告' : 'Workspace report';
    const preparedLabel = isChinese ? '准备人' : 'Prepared by';
    const updatedLabel = isChinese ? '更新于' : 'Updated';

    const renderInsightCards = (items: OutcomeInsightCardItem[]) => (
        <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
            {chunkPdfReportRows(items).map((row, rowIndex) => (
                <div
                    key={`insight-row-${rowIndex}`}
                    data-pdf-page-break="after"
                    style={{
                        ...pdfReportAvoidBreakStyle,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '12px',
                    }}
                >
                    {row.map((item) => (
                        <div
                            key={item.key}
                            style={{
                                ...pdfReportCardStyle,
                                minHeight: '112px',
                            }}
                        >
                            <div style={{ color: pdfReportInkColor, fontSize: '12px', fontWeight: 700, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                                {formatOutcomeInsightText(item.title, locale)}
                            </div>
                            <div style={{ marginTop: '6px', color: pdfReportMutedColor, fontSize: '10.5px', fontWeight: 500, lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                                {formatOutcomeInsightText(item.description, locale)}
                            </div>
                        </div>
                    ))}
                    {row.length < 2 ? <div aria-hidden="true" /> : null}
                </div>
            ))}
        </div>
    );

    const renderChampionCards = () => (
        <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
            {chunkPdfReportRows(champions).map((row, rowIndex) => (
                <div
                    key={`champion-row-${rowIndex}`}
                    data-pdf-page-break="after"
                    style={{
                        ...pdfReportAvoidBreakStyle,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '12px',
                    }}
                >
                    {row.map((item) => (
                        <div
                            key={item.key}
                            style={{
                                ...pdfReportCardStyle,
                                minHeight: '92px',
                            }}
                        >
                            <div style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${pdfReportLineColor}`, borderRadius: '999px', color: pdfReportAccentColor, fontFamily: pdfReportMonoFontFamily, fontSize: '9.5px', fontWeight: 700, lineHeight: 1.2, padding: '4px 8px' }}>
                                {formatChampionTaskBadge(item.taskCount, locale)}
                            </div>
                            <div style={{ marginTop: '10px', color: pdfReportInkColor, fontSize: '12px', fontWeight: 700, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                                {formatOutcomeInsightText(item.name, locale)}
                            </div>
                            <div style={{ marginTop: '4px', color: pdfReportMutedColor, fontSize: '10.5px', fontWeight: 500, lineHeight: 1.55, overflowWrap: 'anywhere' }}>
                                {formatOutcomeInsightText(item.focus, locale)}
                            </div>
                        </div>
                    ))}
                    {row.length < 2 ? <div aria-hidden="true" /> : null}
                </div>
            ))}
        </div>
    );

    const renderChecklistGroup = (title: string, group: ValueLedgerOverviewInsights['standardWorkflow']) => {
        const checklist = group.checklist.length > 0 ? group.checklist : ['-'];

        return (
            <div style={{ ...pdfReportCardStyle, minHeight: '150px' }}>
                <div style={{ color: pdfReportInkColor, fontSize: '12px', fontWeight: 700, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                    {title}
                </div>
                <div style={{ marginTop: '6px', color: pdfReportMutedColor, fontSize: '10.5px', fontWeight: 500, lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                    {formatOutcomeInsightText(group.description, locale)}
                </div>
                <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none' }}>
                    {checklist.map((item, index) => (
                        <li
                            key={`${item}-${index}`}
                            style={{
                                display: 'flex',
                                gap: '6px',
                                marginTop: index === 0 ? 0 : '6px',
                                color: pdfReportInkColor,
                                fontSize: '10px',
                                fontWeight: 500,
                                lineHeight: 1.55,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            <span style={{ color: pdfReportAccentColor, fontWeight: 700 }}>-</span>
                            <span>{formatOutcomeInsightText(item, locale)}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <article
            style={{
                width: `${PDF_EXPORT_WIDTH_PX}px`,
                minHeight: `${PDF_EXPORT_PAGE_HEIGHT_MM * (PDF_EXPORT_WIDTH_PX / PDF_EXPORT_PAGE_WIDTH_MM)}px`,
                boxSizing: 'border-box',
                backgroundColor: '#FFFFFF',
                color: pdfReportInkColor,
                fontFamily: pdfReportBodyFontFamily,
                padding: '56px 64px',
                lineHeight: 1.55,
            }}
        >
            <header
                data-pdf-page-break="after"
                style={{
                    ...pdfReportAvoidBreakStyle,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    gap: '24px',
                    paddingBottom: '18px',
                    borderBottom: `2px solid ${pdfReportInkColor}`,
                    marginBottom: '28px',
                }}
            >
                <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: '6px' }}>
                    <div style={pdfReportMastMetaStyle}>{headerMeta}</div>
                    <h1
                        style={{
                            margin: 0,
                            color: pdfReportInkColor,
                            fontFamily: pdfReportDisplayFontFamily,
                            fontSize: '32px',
                            fontWeight: 700,
                            letterSpacing: '-0.01em',
                            lineHeight: 1.1,
                        }}
                    >
                        {reportTitle}
                    </h1>
                    <div style={pdfReportMastMetaStyle}>
                        {preparedLabel}
                        {' '}
                        <span style={{ color: pdfReportInkColor }}>{reportOwner}</span>
                        {' · '}
                        {updatedLabel}
                        {' '}
                        <span style={{ color: pdfReportInkColor }}>{updatedAt}</span>
                    </div>
                </div>
                <div
                    style={{
                        flexShrink: 0,
                        border: `1px solid ${pdfReportInkColor}`,
                        borderRadius: '4px',
                        color: pdfReportInkColor,
                        fontFamily: pdfReportMonoFontFamily,
                        fontSize: '10.5px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        lineHeight: 1.2,
                        padding: '6px 10px',
                        textTransform: 'uppercase',
                    }}
                >
                    {reportBadge}
                </div>
            </header>

            <section data-pdf-page-break="after" style={{ ...pdfReportAvoidBreakStyle, marginTop: '24px' }}>
                <h2 style={{ margin: 0, color: pdfReportInkColor, fontFamily: pdfReportDisplayFontFamily, fontSize: '22px', fontWeight: 700, letterSpacing: '-0.005em', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                    {headline}
                </h2>
                <p style={{ maxWidth: '64ch', margin: '12px 0 0', color: pdfReportMutedColor, fontSize: '12.5px', fontWeight: 500, lineHeight: 1.75, overflowWrap: 'anywhere' }}>
                    {description}
                </p>
            </section>

            <section
                data-pdf-page-break="after"
                style={{
                    ...pdfReportAvoidBreakStyle,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    gap: '12px',
                    marginTop: '24px',
                }}
            >
                {metricCards.map((card) => (
                    <div
                        key={card.key}
                        style={{
                            border: `1px solid ${pdfReportLineColor}`,
                            borderRadius: '10px',
                            padding: '16px 18px',
                            minHeight: '92px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div style={{ color: pdfReportMutedColor, fontFamily: pdfReportMonoFontFamily, fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1.4, textTransform: 'uppercase', overflowWrap: 'anywhere' }}>
                            {card.label}
                        </div>
                        <div style={{ marginTop: '10px', color: pdfReportInkColor, fontFamily: pdfReportDisplayFontFamily, fontSize: '28px', fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                            {card.value}
                        </div>
                    </div>
                ))}
            </section>

            <section
                data-pdf-page-break="after"
                style={{
                    ...pdfReportAvoidBreakStyle,
                    marginTop: '24px',
                    padding: '16px 18px',
                    borderLeft: `3px solid ${pdfReportAccentColor}`,
                    borderRadius: '6px',
                    backgroundColor: pdfReportAccentSoftColor,
                }}
            >
                <div style={{ color: pdfReportInkColor, fontSize: '12.5px', fontWeight: 700, lineHeight: 1.5 }}>
                    {valueJudgmentTitle}
                </div>
                <div style={{ marginTop: '5px', color: pdfReportMutedColor, fontSize: '11px', fontWeight: 500, lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                    {valueJudgmentDescription}
                </div>
            </section>

            <section style={{ marginTop: '34px' }}>
                <div data-pdf-page-break="after" style={pdfReportAvoidBreakStyle}>
                    <h2 style={pdfReportSectionTitleStyle}>{t('website.valueLedger.overviewTaskAreasTitle')}</h2>
                    <p style={pdfReportSectionDescriptionStyle}>{t('website.valueLedger.overviewTaskAreasDescription')}</p>
                </div>
                <table style={{ width: '100%', marginTop: '12px', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '10.5px' }}>
                    <thead>
                        <tr style={{ color: pdfReportMutedColor, fontFamily: pdfReportMonoFontFamily, fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1.4, textTransform: 'uppercase', borderBottom: `1px solid ${pdfReportStrongLineColor}` }}>
                            <th style={{ width: '28%', padding: '10px 8px', textAlign: 'left' }}>{t('website.valueLedger.overviewTaskAreaColumn')}</th>
                            <th style={{ width: '13%', padding: '10px 8px', textAlign: 'right' }}>{t('website.valueLedger.overviewTaskCountColumn')}</th>
                            <th style={{ width: '13%', padding: '10px 8px', textAlign: 'right' }}>{t('website.valueLedger.overviewUserCountColumn')}</th>
                            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('website.valueLedger.overviewRepresentativeWorkColumn')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportTaskAreas.map((row) => (
                            <tr
                                key={row.key}
                                data-pdf-page-break="after"
                                style={{ ...pdfReportAvoidBreakStyle, borderBottom: `1px solid ${pdfReportLineColor}`, verticalAlign: 'top' }}
                            >
                                <td style={{ padding: '11px 8px', color: pdfReportInkColor, fontWeight: 600, lineHeight: 1.55, overflowWrap: 'anywhere' }}>
                                    {row.scenarioLabel ? localizeLedgerLabel(row.scenarioLabel, locale) : '-'}
                                </td>
                                <td style={{ padding: '11px 8px', color: pdfReportInkColor, fontFamily: pdfReportMonoFontFamily, fontWeight: 600, lineHeight: 1.55, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    {formatOverviewMetricNumber(row.taskCount)}
                                </td>
                                <td style={{ padding: '11px 8px', color: pdfReportInkColor, fontFamily: pdfReportMonoFontFamily, fontWeight: 600, lineHeight: 1.55, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    {formatOverviewMetricNumber(row.activeMemberCount)}
                                </td>
                                <td style={{ padding: '11px 8px', color: pdfReportMutedColor, fontWeight: 500, lineHeight: 1.55, overflowWrap: 'anywhere' }}>
                                    {row.representativeWork ? formatLocalizedSourceTaskText(row.representativeWork, locale) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section style={{ marginTop: '34px' }}>
                <div data-pdf-page-break="after" style={pdfReportAvoidBreakStyle}>
                    <h2 style={pdfReportSectionTitleStyle}>{t('website.valueLedger.overviewInsightsTitle')}</h2>
                    <p style={pdfReportSectionDescriptionStyle}>{t('website.valueLedger.overviewInsightsDescription')}</p>
                </div>
                {renderInsightCards(efficiencyHighlights)}
            </section>

            <section
                data-pdf-page-break="after"
                style={{
                    ...pdfReportAvoidBreakStyle,
                    marginTop: '20px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: '12px',
                }}
            >
                {renderChecklistGroup(standardWorkflowTitle, insights.standardWorkflow)}
                {renderChecklistGroup(reviewSuggestionTitle, insights.reviewSuggestion)}
            </section>

            <section style={{ marginTop: '34px' }}>
                <div data-pdf-page-break="after" style={pdfReportAvoidBreakStyle}>
                    <h2 style={pdfReportSectionTitleStyle}>{t('website.valueLedger.overviewChampionsTitle')}</h2>
                    <p style={pdfReportSectionDescriptionStyle}>{t('website.valueLedger.overviewChampionsDescription')}</p>
                </div>
                {renderChampionCards()}
            </section>

            <section style={{ marginTop: '34px' }}>
                <div data-pdf-page-break="after" style={pdfReportAvoidBreakStyle}>
                    <h2 style={pdfReportSectionTitleStyle}>{t('website.valueLedger.overviewNextCycleTitle')}</h2>
                    <p style={pdfReportSectionDescriptionStyle}>{t('website.valueLedger.overviewNextCycleDescription')}</p>
                </div>
                {renderInsightCards(nextCycleFocus)}
            </section>

            <footer
                data-pdf-page-break="after"
                style={{
                    ...pdfReportAvoidBreakStyle,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginTop: '40px',
                    paddingTop: '18px',
                    borderTop: `1px solid ${pdfReportLineColor}`,
                    color: pdfReportMutedColor,
                    fontFamily: pdfReportMonoFontFamily,
                    fontSize: '9.5px',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    lineHeight: 1.45,
                    textTransform: 'uppercase',
                }}
            >
                <span>{workspaceName} · {isChinese ? '成果摘要' : 'Outcome Summary'}</span>
                <span>{periodText || updatedAt}</span>
            </footer>
        </article>
    );
};

const OutcomeSummaryOverviewPanel = ({
    headline,
    description,
    metricCards,
    valueJudgmentTitle,
    valueJudgmentDescription,
    taskAreas,
    insights,
    locale,
    t,
}: {
    headline: string;
    description: string;
    metricCards: OutcomeOverviewMetricCard[];
    valueJudgmentTitle: string;
    valueJudgmentDescription: string;
    taskAreas: OutcomeOverviewTaskAreaRow[];
    insights: ValueLedgerOverviewInsights;
    locale: string;
    t: Translate;
}) => {
    const rows = taskAreas.length > 0
        ? taskAreas
        : [{
            key: 'placeholder',
            scenarioLabel: null,
            taskCount: null,
            activeMemberCount: null,
            representativeWork: null,
        }];

    return (
        <section className="min-w-0 overflow-hidden rounded-2xl border border-[#E8EBF3] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
            <div className="flex flex-col gap-6 px-6 py-6">
                <div className="min-w-0">
                    <h2 className="max-w-4xl text-[24px] font-semibold leading-[1.22] text-[#1A162F]">
                        {headline}
                    </h2>
                    <p className="mt-3 max-w-4xl text-[13px] font-normal leading-6 text-[#4E586A] [overflow-wrap:anywhere]">
                        {description}
                    </p>
                </div>

                <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {metricCards.map((card) => (
                        <div
                            key={card.key}
                            className={clsx(
                                'flex min-h-[96px] flex-col justify-between rounded-xl border px-4 py-3.5 shadow-none',
                                outcomeOverviewMetricToneClass[card.tone],
                            )}
                        >
                            <div className="flex min-w-0 items-center gap-2">
                                <span
                                    className={clsx(
                                        'inline-flex size-6 shrink-0 items-center justify-center rounded-lg border',
                                        outcomeOverviewMetricIconToneClass[card.tone],
                                    )}
                                >
                                    {card.icon}
                                </span>
                                <div className="min-w-0 truncate text-[12px] font-semibold leading-5 text-[#4E586A]">
                                    {card.label}
                                </div>
                            </div>
                            <div className="mt-3 flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
                                <span className="shrink-0 text-[24px] font-semibold tracking-tight text-[#1A162F]">
                                    {card.value}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="border-t border-[#E8EBF3] px-6 py-5">
                <div className="rounded-xl border border-[#E8EBF3] bg-[#FAFBFC] px-4 py-3.5">
                    <h3 className="text-[12px] font-semibold text-[#1A162F]">
                        {valueJudgmentTitle}
                    </h3>
                    <p className="mt-1 text-[12px] font-normal leading-5 text-[#4E586A] [overflow-wrap:anywhere]">
                        {valueJudgmentDescription}
                    </p>
                </div>
            </div>

            <div className="px-6 pb-6">
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <h3 className="text-[15px] font-semibold leading-6 text-[#1A162F]">
                            {t('website.valueLedger.overviewTaskAreasTitle')}
                        </h3>
                        <p className="mt-1 text-[12px] font-medium leading-5 text-[#667085]">
                            {t('website.valueLedger.overviewTaskAreasDescription')}
                        </p>
                    </div>
                </div>

                <div className="min-w-0 overflow-x-auto rounded-xl border border-[#E8EBF3]">
                    <table className="min-w-[780px] w-full table-fixed border-collapse text-left">
                        <thead className="bg-[#FAFBFC] border-b border-[#E8EBF3]">
                            <tr className="text-[11px] font-semibold leading-4 text-[#7A8397]">
                                <th className="w-[34%] px-4 py-3">{t('website.valueLedger.overviewTaskAreaColumn')}</th>
                                <th className="w-[12%] px-4 py-3">{t('website.valueLedger.overviewTaskCountColumn')}</th>
                                <th className="w-[12%] px-4 py-3">{t('website.valueLedger.overviewUserCountColumn')}</th>
                                <th className="px-4 py-3">{t('website.valueLedger.overviewRepresentativeWorkColumn')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#EDF1F7] bg-white">
                            {rows.map((row) => (
                                <tr key={row.key} className="text-[12px] font-medium leading-5 text-[#1A162F] hover:bg-[#FAFBFC]/40 transition-colors duration-150">
                                    <td className="px-4 py-3">
                                        {row.scenarioLabel ? localizeLedgerLabel(row.scenarioLabel, locale) : '-'}
                                    </td>
                                    <td className="px-4 py-3">{formatOverviewMetricNumber(row.taskCount)}</td>
                                    <td className="px-4 py-3">{formatOverviewMetricNumber(row.activeMemberCount)}</td>
                                    <td className="px-4 py-3 text-[#667085] [overflow-wrap:anywhere]">
                                        {row.representativeWork ? formatLocalizedSourceTaskText(row.representativeWork, locale) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <OutcomeSummaryInsightsPanel insights={insights} locale={locale} t={t} />
        </section>
    );
};

const OutcomeSummarySwitch = ({
    checked,
    disabled,
    label,
    onChange,
}: {
    checked: boolean;
    disabled?: boolean;
    label: string;
    onChange: (checked: boolean) => void;
}) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20',
            checked
                ? 'bg-[#2F7BFF]'
                : disabled
                    ? 'bg-[#E8EDF5]'
                    : 'bg-[#E2E8F0] hover:bg-[#D8E0EB]',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
    >
        <span
            className={clsx(
                'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(15,23,42,0.22)] transition-transform duration-200 ease-in-out',
                checked ? 'translate-x-4' : 'translate-x-0',
            )}
        />
    </button>
);

const OutcomeSummaryUpdateStatus = ({
    checked,
    canManage,
    isSaving,
    dailyUpdatesLabel,
    historicalDataNoticeLabel,
    statusLabel,
    refreshScheduleLabel,
    onChange,
}: {
    checked: boolean;
    canManage: boolean;
    isSaving: boolean;
    dailyUpdatesLabel: string;
    historicalDataNoticeLabel: string;
    statusLabel: string;
    refreshScheduleLabel: string;
    onChange: (checked: boolean) => void;
}) => {
    const statusDotClassName = checked ? 'bg-[#12B76A]' : 'bg-[#C8D0E2]';
    const statusChipClassName = clsx(
        'inline-flex h-8 shrink-0 cursor-default items-center gap-2 rounded-full border border-[#DDE3EA] bg-white px-3 text-[11px] font-semibold leading-4 shadow-[0_1px_2px_rgba(26,22,47,0.03)]',
        checked ? 'text-[#1A162F]' : 'text-[#667085]',
    );
    return (
        <div className="flex min-h-9 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-medium leading-5 text-[#667085] sm:justify-end">
            {!checked ? (
                <span className="shrink-0 whitespace-nowrap text-right font-normal leading-5 text-[#667085]">
                    {historicalDataNoticeLabel}
                </span>
            ) : null}
            <div className={statusChipClassName}>
                <span className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', statusDotClassName)} />
                <span>{statusLabel}</span>
                <Tooltip content={refreshScheduleLabel} side="bottom" align="end" sideOffset={8}>
                    <button
                        type="button"
                        aria-label={refreshScheduleLabel}
                        title={refreshScheduleLabel}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#8A93B2] transition-colors hover:text-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]/20"
                    >
                        <Question size={16} weight="regular" />
                    </button>
                </Tooltip>
                {canManage ? (
                    <span className="flex w-9 shrink-0 justify-end">
                        {isSaving ? (
                            <CircleNotch size={16} weight="regular" className="animate-spin text-[#8A93B2]" />
                        ) : (
                            <OutcomeSummarySwitch
                                checked={checked}
                                disabled={isSaving}
                                label={dailyUpdatesLabel}
                                onChange={onChange}
                            />
                        )}
                    </span>
                ) : null}
            </div>
        </div>
    );
};

const OutcomeSummaryHeroMockup = () => {
    return (
        <div
            className="pointer-events-none absolute left-[58%] top-[70px] z-0 hidden w-[46%] min-w-[440px] max-w-[640px] xl:block 2xl:left-[54%] 2xl:w-[50%] 2xl:max-w-[720px] aspect-[1/1.05] rounded-[24px] border border-[#E7EAF4] bg-[#F7F9FC] p-6 shadow-[0_16px_40px_rgba(26,22,47,0.06)] 2xl:p-8"
            aria-hidden="true"
        >
            {/* Top Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
                {[0, 1].map((i) => {
                    const cardIcon = i === 0
                        ? <PencilSimpleLine size={12} weight="regular" className="text-[#666F8D]" />
                        : <Users size={12} weight="regular" className="text-[#666F8D]" />;
                    return (
                        <div key={i} className="rounded-xl border border-[#E8EBF3] bg-white p-4">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-lg border border-[#E8EBF3] bg-[#F8FAFC]">
                                    {cardIcon}
                                </span>
                                <div className="h-3 w-16 rounded bg-[#E8EDF5] opacity-80" />
                            </div>
                            <div className="mt-3 flex items-end gap-1.5">
                                <div className="h-6 w-12 rounded bg-[#E8EDF5]" />
                                <div className="h-3 w-8 rounded bg-[#E8EDF5] opacity-60" />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Main Content Area */}
            <div className="mt-5 rounded-2xl border border-[#E8EBF3] bg-white p-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="space-y-2 w-2/3">
                        <div className="h-4 w-full rounded bg-[#E8EDF5]" />
                        <div className="h-3 w-2/3 rounded bg-[#E8EDF5] opacity-70" />
                    </div>
                    <div className="h-6 w-16 rounded bg-[#E8EDF5]" />
                </div>

                <div className="mt-4 h-px bg-[#F1F4F9]" />

                {/* Filter tabs */}
                <div className="mt-4 flex flex-wrap gap-1.5 items-center">
                    {/* Shopify - Active */}
                    <div className="flex items-center gap-1 rounded-lg border border-[#BCD5FF] bg-[#EEF4FF] px-2 py-0.5 text-[10px] font-semibold text-[#2F7BFF] shadow-[0_1px_2px_rgba(47,123,255,0.04)]">
                        <img src={shopifyIcon} alt="" className="h-3 w-3 shrink-0 object-contain" />
                        <span>Shopify</span>
                    </div>

                    {/* Google Ads - Inactive */}
                    <div className="flex items-center gap-1 rounded-lg border border-[#E7EAF4] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#4E586A]">
                        <img src={googleAdsIcon} alt="" className="h-3 w-3 shrink-0 object-contain" />
                        <span>Google Ads</span>
                    </div>

                    {/* Meta Ads - Inactive */}
                    <div className="flex items-center gap-1 rounded-lg border border-[#E7EAF4] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#4E586A]">
                        <img src={metaAdsIcon} alt="" className="h-3 w-3 shrink-0 object-contain" />
                        <span>Meta Ads</span>
                    </div>

                    {/* More ... */}
                    <div className="flex items-center justify-center rounded-lg border border-[#E7EAF4] bg-[#F8FAFC] px-2 py-0.5 text-[10px] font-bold text-[#667085] select-none">
                        ···
                    </div>
                </div>

                {/* List Items */}
                <div className="mt-6 space-y-4">
                    {[0, 1, 2, 3].map((i) => {
                        const isMoreRow = i === 3;
                        return (
                            <div key={i} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 w-2/3">
                                    {isMoreRow ? (
                                        <div className="h-4 w-4 shrink-0 rounded-full bg-[#E8EDF5] flex items-center justify-center text-[10px] font-bold text-[#667085] opacity-70 select-none pb-1">
                                            ···
                                        </div>
                                    ) : (
                                        <div className="h-4 w-4 shrink-0 rounded-full bg-[#E8EDF5] opacity-70" />
                                    )}
                                    <div className="space-y-1.5 w-full">
                                        <div className="h-3.5 w-full rounded bg-[#E8EDF5]" />
                                        <div className="h-2.5 w-1/3 rounded bg-[#E8EDF5] opacity-60" />
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <div className="h-5 w-10 rounded bg-[#E8EDF5] opacity-60" />
                                    <div className="h-5 w-12 rounded bg-[#E8EDF5] opacity-60" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const OutcomeSummaryGateCard = ({
    canManage,
    disabledReason,
    isSaving,
    onChange,
    refreshLocalTime,
    t,
}: {
    canManage: boolean;
    disabledReason?: string | null;
    isSaving: boolean;
    onChange: () => void;
    refreshLocalTime: string;
    t: Translate;
}) => {
    const effectiveDisabledReason = disabledReason
        ?? (!canManage
            ? t('website.valueLedger.outcomeSummaryPermissionHint')
            : null);
    const isDisabled = Boolean(effectiveDisabledReason) || !canManage || isSaving;

    return (
        <div className="space-y-5">
            <section className="relative overflow-hidden rounded-[28px] border border-[#E7EAF4] bg-white px-6 py-8 shadow-[0_22px_70px_rgba(26,22,47,0.08)] sm:px-9 sm:py-10 lg:px-[52px] lg:py-[44px] xl:px-[56px]">
                <OutcomeSummaryHeroMockup />

                <div className="relative z-10 flex min-h-[320px] max-w-[760px] flex-col justify-start pt-3 lg:min-h-[360px] xl:w-[56%] xl:max-w-[720px] 2xl:w-[58%] 2xl:max-w-[760px]">
                    <span className="inline-flex w-fit items-center rounded-full border border-[#D0E2FF] bg-[#EEF4FF] px-2.5 py-0.5 text-[12px] font-semibold leading-5 text-[#2F7BFF]">
                        {t('website.valueLedger.outcomeSummaryTag')}
                    </span>
                    <h3
                        className={clsx(
                            'mt-4 max-w-[620px] text-[28px] font-semibold tracking-tight leading-[1.08] text-[#17133F] sm:text-[30px]',
                            'lg:max-w-[640px] lg:text-[30px] xl:max-w-none xl:text-[32px] 2xl:whitespace-nowrap 2xl:text-[36px]',
                        )}
                    >
                        {t('website.valueLedger.outcomeSummaryTitle')}
                    </h3>
                    <p className="mt-4 max-w-[520px] text-[14px] font-normal leading-[1.58] text-[#666F8D] sm:text-[15px]">
                        {t('website.valueLedger.outcomeSummaryDescription')}
                    </p>
                    <div className="mt-6 flex flex-col items-start gap-3">
                        <div className="flex flex-wrap items-center gap-3 w-full">
                            <button
                                type="button"
                                onClick={onChange}
                                disabled={isDisabled}
                                className={clsx(
                                    'inline-flex h-[50px] w-full max-w-[220px] shrink-0 items-center justify-center rounded-[10px] bg-[#1A162F] px-6 text-[15px] font-semibold text-white shadow-[0_4px_12px_rgba(26,22,47,0.15)] transition-all duration-200 hover:bg-[#2B2744] hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20',
                                    isDisabled && 'cursor-not-allowed opacity-60 hover:bg-[#1A162F] hover:translate-y-0 shadow-none',
                                )}
                            >
                                {isSaving ? (
                                    <CircleNotch size={18} weight="regular" className="mr-2 animate-spin" />
                                ) : (
                                    <Power size={18} weight="regular" className="mr-2" />
                                )}
                                <span>{t('website.valueLedger.outcomeSummaryGetStarted')}</span>
                            </button>
                        </div>
                        {effectiveDisabledReason ? (
                            <span className="text-[12px] font-medium leading-5 text-[#8A93B2]">
                                {effectiveDisabledReason}
                            </span>
                        ) : null}
                    </div>
                    <div className="mt-6 flex max-w-[560px] items-center gap-3 text-left text-[12px] font-normal leading-5 text-[#666F8D] sm:text-[13px]">
                        <Clock size={20} weight="regular" className="shrink-0 text-[#666F8D]" />
                        <p className="min-w-0">
                            {t('website.valueLedger.outcomeSummaryScheduleDescription', { time: refreshLocalTime })}
                        </p>
                    </div>
                </div>
            </section>

            <div className="flex items-start gap-3 px-1 text-[12px] font-normal leading-5 text-[#65708F] sm:px-2">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#8FA0BA] text-[10px] font-medium leading-none text-[#66758F]">
                    i
                </span>
                <div className="min-w-0">
                    <p className="font-medium text-[#54627A]">{t('website.valueLedger.outcomeSummaryFinePrintTitle')}</p>
                    <p className="mt-1 text-[#7A889E]">{t('website.valueLedger.outcomeSummaryFinePrintDescription')}</p>
                </div>
            </div>
        </div>
    );
};

const OutcomeSummarySkeletonBar = ({ className }: { className: string }) => (
    <div className={clsx('rounded-full bg-[#E8EDF5]', className)} />
);

const OutcomeSummaryLoadingSkeleton = () => (
    <div className="space-y-5" aria-busy="true">
        <div className="grid gap-5 sm:grid-cols-2">
            {[0, 1].map((item) => (
                <div
                    key={item}
                    className="rounded-[18px] border border-[#E8EBF3] bg-white px-6 py-6 shadow-[0_8px_24px_rgba(26,22,47,0.04)]"
                >
                    <OutcomeSummarySkeletonBar className="h-4 w-40" />
                    <div className="mt-5 flex items-end gap-2">
                        <OutcomeSummarySkeletonBar className="h-9 w-24" />
                        <OutcomeSummarySkeletonBar className="mb-1 h-4 w-16" />
                    </div>
                </div>
            ))}
        </div>

        <section className="rounded-[20px] border border-[#E8EBF3] bg-white px-6 py-6 shadow-[0_8px_24px_rgba(26,22,47,0.04)]">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-3">
                    <OutcomeSummarySkeletonBar className="h-6 w-72 max-w-full" />
                    <OutcomeSummarySkeletonBar className="h-4 w-52 max-w-full" />
                </div>
                <OutcomeSummarySkeletonBar className="h-6 w-24" />
            </div>
            <div className="my-5 h-px bg-[#E8EBF3]" />
            <OutcomeSummarySkeletonBar className="h-12 w-full rounded-xl" />

            <div className="mt-5 space-y-4">
                <div className="space-y-2">
                    <OutcomeSummarySkeletonBar className="h-4 w-28" />
                    <div className="flex flex-wrap gap-2">
                        {[0, 1, 2, 3].map((item) => (
                            <OutcomeSummarySkeletonBar key={item} className="h-9 w-44 rounded-lg" />
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    <OutcomeSummarySkeletonBar className="h-4 w-28" />
                    <div className="flex flex-wrap gap-2">
                        {[0, 1, 2, 3, 4, 5].map((item) => (
                            <OutcomeSummarySkeletonBar
                                key={item}
                                className={clsx('h-9 rounded-lg', item % 2 === 0 ? 'w-60' : 'w-72')}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="my-5 h-px bg-[#E8EBF3]" />
            <div className="grid gap-5 lg:grid-cols-2">
                {[0, 1].map((item) => (
                    <div key={item} className="rounded-[18px] border border-[#E8EBF3] bg-white p-5">
                        <div className="flex gap-2">
                            <OutcomeSummarySkeletonBar className="h-6 w-36" />
                            <OutcomeSummarySkeletonBar className="h-6 w-52" />
                        </div>
                        <OutcomeSummarySkeletonBar className="mt-5 h-6 w-4/5" />
                        <OutcomeSummarySkeletonBar className="mt-3 h-4 w-full" />
                        <OutcomeSummarySkeletonBar className="mt-2 h-4 w-3/4" />
                        <div className="mt-5 h-px bg-[#F1F4F9]" />
                        <OutcomeSummarySkeletonBar className="mt-4 h-8 w-48" />
                    </div>
                ))}
            </div>
        </section>
    </div>
);

const PremiumGradientSpinner = () => (
    <div className="relative h-12 w-12 animate-spin">
        <svg className="h-full w-full" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle
                cx="18"
                cy="18"
                r="16"
                stroke="#EEF2F6"
                strokeWidth="3.5"
            />
            <circle
                cx="18"
                cy="18"
                r="16"
                stroke="url(#spinner-gradient)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeDasharray="100"
                strokeDashoffset="35"
            />
            <defs>
                <linearGradient id="spinner-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#2F7BFF" />
                    <stop offset="60%" stopColor="#2F7BFF" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#2F7BFF" stopOpacity="0" />
                </linearGradient>
            </defs>
        </svg>
    </div>
);

const OutcomeSummaryPreparingState = ({
    title,
    description,
    steps,
}: {
    title: string;
    description: string;
    steps: string[];
}) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentStepIndex((prev) => {
                if (prev < steps.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 2000);
        return () => clearInterval(interval);
    }, [steps.length]);

    return (
        <section
            className="flex min-h-[560px] items-center justify-center rounded-[28px] border border-[#E7EAF4] bg-white px-6 py-16 shadow-[0_22px_70px_rgba(26,22,47,0.08)]"
            aria-busy="true"
        >
            <div className="mx-auto flex w-full max-w-[560px] items-start gap-5 rounded-[12px] bg-white">
                <div className="-mt-1 flex h-[64px] w-[64px] shrink-0 items-center justify-center">
                    <PremiumGradientSpinner />
                </div>

                <div className="min-w-0 text-left">
                    <h2 className="text-[22px] font-semibold leading-8 tracking-[-0.01em] text-[#17133F]">
                        {title}
                    </h2>
                    <p className="mt-3 max-w-[460px] text-[14px] font-normal leading-6 text-[#667085]">
                        {description}
                    </p>
                    <ul className="mt-6 space-y-3.5">
                        {steps.map((step, i) => {
                            const isCompleted = i < currentStepIndex;
                            const isActive = i === currentStepIndex;

                            return (
                                <li
                                    key={step}
                                    className={clsx(
                                        "flex items-center gap-3 text-[14px] font-medium transition-all duration-300",
                                        isCompleted && "text-[#4B5563]",
                                        isActive && "text-[#17133F]",
                                        !isCompleted && !isActive && "text-[#9CA3AF]"
                                    )}
                                >
                                    {isCompleted ? (
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#10B981] text-white">
                                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </span>
                                    ) : isActive ? (
                                        <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2F7BFF] opacity-20"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2F7BFF]"></span>
                                        </span>
                                    ) : (
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#E5E7EB] bg-white">
                                            <span className="h-1.5 w-1.5 rounded-full bg-[#D1D5DB]" />
                                        </span>
                                    )}
                                    <span>{step}</span>
                                </li>
                            );
                        })}
                    </ul>

                </div>
            </div>
        </section>
    );
};

function getNotionStatusStyle(status: string) {
    const s = (status || '').toLowerCase();
    if (s.includes('done') || s.includes('已确认') || s.includes('success')) {
        return {
            bg: '#DBEDDB',
            text: '#2B593F',
            label: 'Done'
        };
    }
    if (s.includes('review') || s.includes('待确认') || s.includes('pending') || s.includes('待用户') || s.includes('待成员')) {
        return {
            bg: '#D3E5EF',
            text: '#1F5B78',
            label: 'In review'
        };
    }
    if (s.includes('progress') || s.includes('进行中') || s.includes('running')) {
        return {
            bg: '#FDECC8',
            text: '#976D23',
            label: 'In progress'
        };
    }
    if (s.includes('blocked') || s.includes('已阻塞') || s.includes('fail') || s.includes('error') || s.includes('需用户') || s.includes('需成员')) {
        return {
            bg: '#FFE2DD',
            text: '#B13B2C',
            label: 'Blocked'
        };
    }
    return {
        bg: '#E9E5E3',
        text: '#5A534F',
        label: 'To do'
    };
}

function formatDateOnly(value: string | null | undefined, locale: string, timezone: string) {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';

    return new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

/* ─── Main Page Component ─── */

const ValueLedgerPage: React.FC = () => {
    const { t, locale } = useTranslation();
    const { currentWorkspace, permissions, profile, userBilling } = useAuth();
    const canManageThisOutcomeSummary = canManageOutcomeSummary(permissions);
    const canViewThisOutcomeSummary = canViewOutcomeSummary(permissions);
    const outcomeSummarySettings = useOutcomeSummarySettings(currentWorkspace?.id, { enabled: canViewThisOutcomeSummary });
    const [searchParams, setSearchParams] = useSearchParams();
    const rangeIdParam = searchParams.get('range_id') || searchParams.get('rangeId');
    const environmentParam = searchParams.get('environment');
    const [ledgerData, setLedgerData] = useState<ValueLedgerDesignData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [selectedOutcomeKey, setSelectedOutcomeKey] = useState<string | null>(null);
    const [viewedOutcomeKeys, setViewedOutcomeKeys] = useState<Set<string>>(() => new Set());
    const [activeDetailTab, setActiveDetailTab] = useState<LedgerDetailTab>('overview');
    const [activeSummaryTab, setActiveSummaryTab] = useState<'overview' | 'ledger'>('overview');
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isActivatingTransition, setIsActivatingTransition] = useState(false);
    const [isConfirmDisableOpen, setIsConfirmDisableOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const pdfExportRef = useRef<HTMLDivElement>(null);

    const [selectedOutcomeType, setSelectedOutcomeType] = useState<string | null>(null);
    const [selectedBusinessModule, setSelectedBusinessModule] = useState<string | null>(null);
    const [outcomePage, setOutcomePage] = useState(1);
    const [pageJumpValue, setPageJumpValue] = useState('1');
    const defaultFilterSelectionKeyRef = useRef<string | null>(null);
    const listLayerRef = useRef<HTMLDivElement | null>(null);
    const detailLayerRef = useRef<HTMLDivElement | null>(null);
    const listScrollSnapshotRef = useRef<ListScrollSnapshot | null>(null);
    const shouldRestoreListScrollRef = useRef(false);
    const fetchedOutcomeDetailIdsRef = useRef<Set<string>>(new Set());
    const ledgerRequestGenerationRef = useRef(0);
    const [loadingOutcomeDetailId, setLoadingOutcomeDetailId] = useState<string | null>(null);
    const [outcomeDetailErrorById, setOutcomeDetailErrorById] = useState<Record<string, string>>({});
    const viewerRole = currentWorkspace?.workspaceRoleName
        ?? currentWorkspace?.workspaceRole
        ?? currentWorkspace?.role;
    const isOutcomeSummaryEnabled = outcomeSummarySettings.settings.enabled;
    const hasOutcomeSummaryStarted = isOutcomeSummaryEnabled || Boolean(outcomeSummarySettings.settings.privacyAcceptedAt);
    const isOutcomeSummaryWorkspaceExpired = userBilling.isExpired || isWorkspaceSubscriptionExpired(currentWorkspace?.subscription);
    const canUpdateOutcomeSummary = canManageThisOutcomeSummary && !isOutcomeSummaryWorkspaceExpired;
    const isOutcomeSummaryPaused = hasOutcomeSummaryStarted && (!isOutcomeSummaryEnabled || isOutcomeSummaryWorkspaceExpired);
    const workspaceSubscription = currentWorkspace?.subscription as {
        current_period_start?: string | null;
        currentPeriodStart?: string | null;
        current_period_end?: string | null;
        currentPeriodEnd?: string | null;
        billing_cycle?: string | null;
        billingCycle?: string | null;
    } | null | undefined;
    const fallbackCurrentCycleRange = useMemo(() => {
        const profileWorkspacePlan = profile?.workspacePlan;
        const billingWorkspacePlan = userBilling.userPlan;
        const fallbackEndAt = new Date().toISOString();
        const endAt = profileWorkspacePlan?.currentPeriodEnd
            || billingWorkspacePlan?.currentPeriodEnd
            || workspaceSubscription?.current_period_end
            || workspaceSubscription?.currentPeriodEnd
            || fallbackEndAt;
        const billingCycle = profileWorkspacePlan?.billingCycle
            || billingWorkspacePlan?.billingCycle
            || workspaceSubscription?.billing_cycle
            || workspaceSubscription?.billingCycle;
        const startAt = profileWorkspacePlan?.currentPeriodStart
            || billingWorkspacePlan?.currentPeriodStart
            || workspaceSubscription?.current_period_start
            || workspaceSubscription?.currentPeriodStart
            || deriveBillingCycleStart(endAt, billingCycle);

        return {
            startAt,
            endAt,
        };
    }, [
        profile?.workspacePlan,
        userBilling.userPlan,
        workspaceSubscription?.billing_cycle,
        workspaceSubscription?.billingCycle,
        workspaceSubscription?.current_period_end,
        workspaceSubscription?.current_period_start,
        workspaceSubscription?.currentPeriodEnd,
        workspaceSubscription?.currentPeriodStart,
    ]);

    const fallbackData = useMemo(() => adaptValueLedgerApiResponse({
        status: 'empty',
        workspace: {
            workspaceId: currentWorkspace?.id,
        },
        range: {
            selectedRangeId: rangeIdParam || undefined,
            rangeStartAt: fallbackCurrentCycleRange.startAt,
            rangeEndAt: fallbackCurrentCycleRange.endAt,
            availableRanges: [{
                rangeId: rangeIdParam || 'current',
                label: '当前订阅周期',
                startAt: fallbackCurrentCycleRange.startAt,
                endAt: fallbackCurrentCycleRange.endAt,
                timezone: 'Asia/Shanghai',
            }],
        },
        summary: {},
        outcomes: [],
    }, {
        workspaceName: currentWorkspace?.name,
        viewerRole,
        environment: environmentParam,
        locale,
    }), [currentWorkspace?.id, currentWorkspace?.name, environmentParam, fallbackCurrentCycleRange.endAt, fallbackCurrentCycleRange.startAt, locale, rangeIdParam, viewerRole]);

    const data = ledgerData ?? fallbackData;
    const ledgerRequest = useMemo(() => ({
        rangeId: rangeIdParam || undefined,
        environment: environmentParam || undefined,
        workspaceId: currentWorkspace?.id || undefined,
    }), [currentWorkspace?.id, environmentParam, rangeIdParam]);
    const shouldFetchOutcomeSummary = hasOutcomeSummaryStarted
        && !outcomeSummarySettings.isSaving
        && (isOutcomeSummaryEnabled || !ledgerData);

    const applyLedgerResponse = useCallback((response: ValueLedgerApiLedger) => {
        const nextData = adaptValueLedgerApiResponse(response, {
            workspaceName: currentWorkspace?.name,
            viewerRole,
            environment: environmentParam,
            locale,
        });
        fetchedOutcomeDetailIdsRef.current = new Set();
        setOutcomeDetailErrorById({});
        setLedgerData(nextData);
        setSelectedOutcomeKey(nextData.outcomes[0] ? getOutcomeKey(nextData.outcomes[0]) : null);
        setViewedOutcomeKeys(new Set());
        setSelectedOutcomeType(null);
        setSelectedBusinessModule(null);
    }, [currentWorkspace?.name, environmentParam, locale, viewerRole]);

    const startLedgerRequestGeneration = useCallback(() => {
        ledgerRequestGenerationRef.current += 1;
        return ledgerRequestGenerationRef.current;
    }, []);

    const isLatestLedgerRequestGeneration = useCallback((generation: number) => (
        generation === ledgerRequestGenerationRef.current
    ), []);

    useEffect(() => {
        let cancelled = false;

        if (permissions.isLoading) {
            setIsLoading(true);
            setLoadError(null);
            return () => {
                cancelled = true;
            };
        }

        if (!canViewThisOutcomeSummary) {
            setLedgerData(null);
            setIsLoading(false);
            setLoadError(null);
            setIsDetailOpen(false);
            setSelectedOutcomeKey(null);
            setViewedOutcomeKeys(new Set());
            setSelectedOutcomeType(null);
            setSelectedBusinessModule(null);
            fetchedOutcomeDetailIdsRef.current = new Set();
            setOutcomeDetailErrorById({});
            return () => {
                cancelled = true;
            };
        }

        if (outcomeSummarySettings.isLoading || !shouldFetchOutcomeSummary) {
            setIsLoading(false);
            setLoadError(null);

            if (!hasOutcomeSummaryStarted) {
                setLedgerData(null);
                setIsDetailOpen(false);
                setSelectedOutcomeKey(null);
                setViewedOutcomeKeys(new Set());
                setSelectedOutcomeType(null);
                setSelectedBusinessModule(null);
                fetchedOutcomeDetailIdsRef.current = new Set();
                setOutcomeDetailErrorById({});
            }

            return () => {
                cancelled = true;
            };
        }

        const cachedLedger = valueLedgerService.getCachedLedger(ledgerRequest);
        if (cachedLedger) {
            applyLedgerResponse(cachedLedger);
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
        setLoadError(null);

        const requestGeneration = startLedgerRequestGeneration();
        valueLedgerService.getLedger(ledgerRequest).then((response) => {
            if (cancelled || !isLatestLedgerRequestGeneration(requestGeneration)) return;
            applyLedgerResponse(response);
        }).catch((error) => {
            if (cancelled || !isLatestLedgerRequestGeneration(requestGeneration)) return;

            setLoadError(error instanceof Error ? error.message : 'Failed to load value ledger');
        }).finally(() => {
            if (!cancelled && isLatestLedgerRequestGeneration(requestGeneration)) {
                setIsLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        applyLedgerResponse,
        canViewThisOutcomeSummary,
        hasOutcomeSummaryStarted,
        isLatestLedgerRequestGeneration,
        shouldFetchOutcomeSummary,
        ledgerRequest,
        outcomeSummarySettings.isLoading,
        permissions.isLoading,
        startLedgerRequestGeneration,
    ]);

    const handleOutcomeSummaryRealtimeUpdate = useCallback(() => {
        if (!canViewThisOutcomeSummary || !hasOutcomeSummaryStarted) return;

        valueLedgerService.clearLedgerCacheEntry(ledgerRequest);
        const requestGeneration = startLedgerRequestGeneration();
        valueLedgerService.getLedger(ledgerRequest)
            .then((response) => {
                if (!isLatestLedgerRequestGeneration(requestGeneration)) return;
                applyLedgerResponse(response);
                setLoadError(null);
            })
            .catch((error) => {
                if (!isLatestLedgerRequestGeneration(requestGeneration)) return;
                setLoadError(error instanceof Error ? error.message : 'Failed to load value ledger');
            });
    }, [
        applyLedgerResponse,
        canViewThisOutcomeSummary,
        hasOutcomeSummaryStarted,
        isLatestLedgerRequestGeneration,
        ledgerRequest,
        startLedgerRequestGeneration,
    ]);

    useOutcomeSummaryRealtimeInvalidation({
        workspaceId: currentWorkspace?.id,
        selectedRangeId: data.range.selectedRangeId,
        enabled: canViewThisOutcomeSummary && hasOutcomeSummaryStarted,
        onCurrentRangeUpdated: handleOutcomeSummaryRealtimeUpdate,
    });

    const activeRangeInfo = useMemo(() => {
        const range = data.range.availableRanges.find((item) => item.rangeId === data.range.selectedRangeId)
            ?? data.range.availableRanges[0];
        const rawLabel = range?.label ?? data.range.rangeLabel;
        const rangeId = range?.rangeId ?? data.range.selectedRangeId;
        return {
            rawLabel,
            rangeId,
            startAt: range?.startAt || data.range.rangeStartAt || fallbackCurrentCycleRange.startAt,
            endAt: range?.endAt || data.range.rangeEndAt || fallbackCurrentCycleRange.endAt,
            isCurrentSubscriptionCycle: isCurrentSubscriptionCycleLabel(rawLabel, rangeId),
        };
    }, [
        data.range.availableRanges,
        data.range.rangeEndAt,
        data.range.rangeLabel,
        data.range.rangeStartAt,
        data.range.selectedRangeId,
        fallbackCurrentCycleRange.endAt,
        fallbackCurrentCycleRange.startAt,
        locale,
    ]);

    const selectedRangeIsCurrentCycle = data.range.isCurrentCycle;

    const filteredOutcomes = useMemo(() => {
        return data.outcomes.filter((outcome) => {
            if (selectedOutcomeType && outcome.outcomeTypeLabel !== selectedOutcomeType) return false;
            if (selectedBusinessModule && outcome.businessModule !== selectedBusinessModule) return false;
            return true;
        });
    }, [data.outcomes, selectedOutcomeType, selectedBusinessModule]);

    const totalOutcomeRows = filteredOutcomes.length;
    const totalOutcomePages = Math.max(1, Math.ceil(totalOutcomeRows / OUTCOME_SUMMARY_ITEMS_PER_PAGE));
    const safeOutcomePage = Math.min(Math.max(1, outcomePage), totalOutcomePages);
    const outcomePaginationItems = useMemo(
        () => getOutcomePaginationItems(safeOutcomePage, totalOutcomePages),
        [safeOutcomePage, totalOutcomePages],
    );
    const paginatedOutcomes = useMemo(() => filteredOutcomes.slice(
        (safeOutcomePage - 1) * OUTCOME_SUMMARY_ITEMS_PER_PAGE,
        safeOutcomePage * OUTCOME_SUMMARY_ITEMS_PER_PAGE,
    ), [filteredOutcomes, safeOutcomePage]);
    const baseOutcomeTypeCounts = data.filters.outcomeTypeCounts.length > 0
        ? data.filters.outcomeTypeCounts
        : laborLanes.map((lane) => ({ label: getLaneTitle(lane, locale), count: 0 }));
    const baseBusinessModuleCounts = data.filters.businessModuleCounts.length > 0
        ? data.filters.businessModuleCounts
        : fallbackBusinessModuleFilterLabels.map((label) => ({ label, count: 0 }));
    const displayedOutcomeTypeCounts = baseOutcomeTypeCounts;
    const outcomeTypeAllCount = sumOutcomeFilterCounts(displayedOutcomeTypeCounts) || data.outcomes.length;
    const outcomesMatchingSelectedOutcomeType = selectedOutcomeType
        ? data.outcomes.filter((outcome) => outcome.outcomeTypeLabel === selectedOutcomeType)
        : data.outcomes;
    const displayedBusinessModuleCounts = getOutcomeCountsByLabel(
        getOrderedOutcomeFilterLabels(baseBusinessModuleCounts, fallbackBusinessModuleFilterLabels),
        outcomesMatchingSelectedOutcomeType,
        (outcome) => outcome.businessModule,
    );
    const businessModuleAllCount = outcomesMatchingSelectedOutcomeType.length;
    const defaultFilterSelectionKey = [
        data.range.selectedRangeId,
        baseOutcomeTypeCounts.map((item) => `${item.label}:${item.count}`).join(','),
        baseBusinessModuleCounts.map((item) => `${item.label}:${item.count}`).join(','),
    ].join('|');

    const visibleOutcomeKeys = useMemo(() => paginatedOutcomes.map(getOutcomeKey), [paginatedOutcomes]);

    useEffect(() => {
        if (defaultFilterSelectionKeyRef.current === defaultFilterSelectionKey) return;

        defaultFilterSelectionKeyRef.current = defaultFilterSelectionKey;
        setSelectedOutcomeType(null);
        setSelectedBusinessModule(null);
        setOutcomePage(1);
        setPageJumpValue('1');
    }, [defaultFilterSelectionKey]);

    useEffect(() => {
        if (visibleOutcomeKeys.length === 0) return;
        if (selectedOutcomeKey && visibleOutcomeKeys.includes(selectedOutcomeKey)) return;

        setSelectedOutcomeKey(visibleOutcomeKeys[0]);
    }, [selectedOutcomeKey, visibleOutcomeKeys]);

    useEffect(() => {
        setOutcomePage(1);
    }, [selectedOutcomeType, selectedBusinessModule, data.range.selectedRangeId]);

    useEffect(() => {
        if (outcomePage <= totalOutcomePages) return;
        setOutcomePage(totalOutcomePages);
    }, [outcomePage, totalOutcomePages]);

    useEffect(() => {
        setPageJumpValue(String(safeOutcomePage));
    }, [safeOutcomePage]);

    const selectedOutcome = useMemo(() => {
        if (visibleOutcomeKeys.length === 0) return null;

        const selected = data.outcomes.find((outcome) => getOutcomeKey(outcome) === selectedOutcomeKey);
        if (selected && visibleOutcomeKeys.includes(getOutcomeKey(selected))) {
            return selected;
        }

        return data.outcomes.find((outcome) => getOutcomeKey(outcome) === visibleOutcomeKeys[0]) ?? null;
    }, [data.outcomes, selectedOutcomeKey, visibleOutcomeKeys]);

    useEffect(() => {
        const outcomeId = selectedOutcome?.outcomeId;
        if (!isDetailOpen) return;
        if (!canViewThisOutcomeSummary) return;
        if (!outcomeId || fetchedOutcomeDetailIdsRef.current.has(outcomeId)) return;

        let cancelled = false;
        fetchedOutcomeDetailIdsRef.current.add(outcomeId);
        setLoadingOutcomeDetailId(outcomeId);
        setOutcomeDetailErrorById((current) => {
            const { [outcomeId]: _removed, ...rest } = current;
            return rest;
        });

        valueLedgerService.getOutcomeDetail(outcomeId, {
            environment: environmentParam || undefined,
            workspaceId: currentWorkspace?.id || undefined,
        })
            .then((response) => {
                if (cancelled) return;

                const detailOutcome = adaptValueLedgerApiOutcome(response, {
                    rangeLabel: data.range.rangeLabel,
                    generatedAt: data.generatedAt,
                    workspaceName: currentWorkspace?.name,
                    viewerRole,
                    environment: environmentParam,
                    locale,
                });

                setLedgerData((current) => {
                    if (!current) return current;

                    return {
                        ...current,
                        outcomes: current.outcomes.map((outcome) => (
                            outcome.outcomeId === detailOutcome.outcomeId
                                ? { ...outcome, ...detailOutcome }
                                : outcome
                        )),
                    };
                });
            })
            .catch((error) => {
                if (cancelled) return;

                fetchedOutcomeDetailIdsRef.current.delete(outcomeId);
                setOutcomeDetailErrorById((current) => ({
                    ...current,
                    [outcomeId]: error instanceof Error ? error.message : 'Failed to load outcome detail',
                }));
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingOutcomeDetailId((current) => (current === outcomeId ? null : current));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        currentWorkspace?.id,
        currentWorkspace?.name,
        data.generatedAt,
        data.range.rangeLabel,
        environmentParam,
        isDetailOpen,
        canViewThisOutcomeSummary,
        locale,
        selectedOutcome?.outcomeId,
        viewerRole,
    ]);

    const activeRangeDisplayRange = activeRangeInfo.isCurrentSubscriptionCycle
        ? {
            startAt: activeRangeInfo.startAt || fallbackCurrentCycleRange.startAt,
            endAt: activeRangeInfo.endAt || fallbackCurrentCycleRange.endAt,
        }
        : activeRangeInfo;
    const selectedRangeDateText = formatSubscriptionCycleDateRange(
        activeRangeDisplayRange.startAt,
        activeRangeDisplayRange.endAt,
        locale,
        data.workspace.timezone,
    );
    const selectedRangeContextLabel = getRangeOptionContextLabel(activeRangeInfo.rawLabel, locale, t);
    const selectedRangeDisplayText = selectedRangeDateText === '--' ? '' : selectedRangeDateText;

    const selectOutcome = (outcomeKey: string) => {
        setSelectedOutcomeKey(outcomeKey);
    };

    const captureListScrollPosition = () => {
        if (typeof window === 'undefined') return;

        listScrollSnapshotRef.current = {
            windowScrollX: window.scrollX,
            windowScrollY: window.scrollY,
            containers: getScrollableAncestors(listLayerRef.current).map((element) => ({
                element,
                scrollTop: element.scrollTop,
                scrollLeft: element.scrollLeft,
            })),
        };
    };

    const restoreListScrollPosition = useCallback(() => {
        if (typeof window === 'undefined') return;

        const snapshot = listScrollSnapshotRef.current;
        if (!snapshot) return;

        snapshot.containers.forEach(({ element, scrollTop, scrollLeft }) => {
            element.scrollTop = scrollTop;
            element.scrollLeft = scrollLeft;
        });
        window.scrollTo(snapshot.windowScrollX, snapshot.windowScrollY);
    }, []);

    const openOutcomeDetail = (outcomeKey: string) => {
        captureListScrollPosition();
        selectOutcome(outcomeKey);
        setViewedOutcomeKeys((current) => {
            const next = new Set(current);
            next.add(outcomeKey);
            return next;
        });
        setActiveDetailTab('overview');
        setIsDetailOpen(true);
    };

    const returnToList = () => {
        shouldRestoreListScrollRef.current = true;
        setIsDetailOpen(false);
    };

    const activeRange = data.range.availableRanges.find(r => r.rangeId === data.range.selectedRangeId)
        ?? data.range.availableRanges[0]
        ?? {
            rangeId: data.range.selectedRangeId,
            label: data.range.rangeLabel,
            startAt: data.range.rangeStartAt || fallbackCurrentCycleRange.startAt,
            endAt: data.range.rangeEndAt || fallbackCurrentCycleRange.endAt,
        };

    const currentGenerationStatus = typeof data.currentGeneration?.status === 'string'
        ? data.currentGeneration.status.toLowerCase()
        : '';
    const selectedRangeStatus = typeof data.range.status === 'string'
        ? data.range.status.toLowerCase()
        : '';
    const selectedRangeEmptyReason = typeof data.range.emptyReason === 'string'
        ? data.range.emptyReason.toLowerCase()
        : '';
    const ledgerEmptyReason = typeof data.reasonCode === 'string'
        ? data.reasonCode.toLowerCase()
        : '';
    const isLedgerGenerationInFlight = isOutcomeLedgerGenerationStatusInFlight(currentGenerationStatus);
    const isSelectedRangeGenerationInFlight = (
        isOutcomeLedgerGenerationStatusInFlight(selectedRangeStatus)
        || selectedRangeEmptyReason === 'generating'
        || ledgerEmptyReason === 'generating'
    );
    const isOutcomeSummaryGenerationPending = isLedgerGenerationInFlight || isSelectedRangeGenerationInFlight;
    const isEmptyLedger = data.status === 'empty';
    const hasGeneratedLedger = Boolean(data.workspace.lastGeneratedAt) && !isEmptyLedger;
    const shouldShowPendingRefreshData = !hasGeneratedLedger
        && selectedRangeIsCurrentCycle
        && isOutcomeSummaryGenerationPending
        && !isOutcomeSummaryWorkspaceExpired;
    const shouldPollOutcomeSummaryGeneration = isOutcomeSummaryGenerationPending;
    const canPollOutcomeSummaryGeneration = isOutcomeSummaryEnabled
        && !outcomeSummarySettings.isSaving
        && !isOutcomeSummaryWorkspaceExpired
        && shouldPollOutcomeSummaryGeneration;
    const displayedMemberTaskCount = !shouldShowPendingRefreshData
        ? data.summary.memberTaskCount
        : null;
    const primaryEvidenceSystemLabel = useMemo(
        () => getPrimaryEvidenceSystemLabel(data.outcomes),
        [data.outcomes],
    );
    const derivedOverviewTaskAreas = useMemo(
        () => deriveOverviewTaskAreasFromOutcomes(data.outcomes),
        [data.outcomes],
    );
    const overviewTaskAreas = useMemo(
        () => (
            data.overview.taskScenarios.length > 0
                ? data.overview.taskScenarios.map(toOverviewTaskAreaRow)
                : derivedOverviewTaskAreas
        ),
        [data.overview.taskScenarios, derivedOverviewTaskAreas],
    );
    const derivedActiveMemberCount = useMemo(() => {
        const userKeys = new Set<string>();
        data.outcomes.forEach((outcome) => {
            const userKey = formatSourceTaskText(outcome.sourceUserEmail || outcome.sourceUserName);
            if (userKey !== '-') userKeys.add(userKey);
        });
        return userKeys.size > 0 ? userKeys.size : null;
    }, [data.outcomes]);
    const overviewTopTaskArea = overviewTaskAreas[0] ?? null;
    const overviewUserStartedTaskCount = data.overview.keyMetrics.userStartedTaskCount
        ?? displayedMemberTaskCount;
    const overviewActiveMemberCount = data.overview.keyMetrics.activeMemberCount
        ?? derivedActiveMemberCount;
    const overviewCoreScenarioTaskCount = data.overview.keyMetrics.coreScenarioTaskCount
        ?? overviewTopTaskArea?.taskCount
        ?? null;
    const overviewCoreScenarioLabel = data.overview.keyMetrics.coreScenarioLabel
        ?? overviewTopTaskArea?.scenarioLabel
        ?? null;
    const overviewCoreScenarioTaskShare = data.overview.keyMetrics.coreScenarioTaskShare
        ?? getOverviewShare(overviewCoreScenarioTaskCount, overviewUserStartedTaskCount);
    const overviewHeadline = data.overview.headline
        ? formatOutcomeGeneratedCopyText(data.overview.headline, locale)
        : primaryEvidenceSystemLabel
            ? t('website.valueLedger.overviewHeadline', { system: primaryEvidenceSystemLabel })
            : t('website.valueLedger.overviewHeadlineFallback');
    const overviewDescription = data.overview.description
        ? formatOutcomeGeneratedCopyText(data.overview.description, locale)
        : t('website.valueLedger.overviewDescription', {
            taskCount: formatOverviewMetricNumber(overviewUserStartedTaskCount),
            userCount: formatOverviewMetricNumber(overviewActiveMemberCount),
            coreTaskCount: formatOverviewMetricNumber(overviewCoreScenarioTaskCount),
            taskArea: overviewCoreScenarioLabel ? localizeLedgerLabel(overviewCoreScenarioLabel, locale) : '-',
            share: formatOverviewMetricPercent(overviewCoreScenarioTaskShare),
        });
    const overviewMetricCards: OutcomeOverviewMetricCard[] = [
        {
            key: 'user-started-tasks',
            label: t('website.valueLedger.overviewUserStartedTasksMetric'),
            value: formatOverviewMetricNumber(overviewUserStartedTaskCount),
            tone: 'neutral',
            icon: <PencilSimpleLine size={14} weight="regular" />,
        },
        {
            key: 'users-involved',
            label: t('website.valueLedger.overviewActiveUsersMetric'),
            value: formatOverviewMetricNumber(overviewActiveMemberCount),
            tone: 'neutral',
            icon: <Users size={14} weight="regular" />,
        },
        {
            key: 'top-area-tasks',
            label: t('website.valueLedger.overviewTopAreaTasksMetric'),
            value: formatOverviewMetricNumber(overviewCoreScenarioTaskCount),
            tone: 'neutral',
            icon: <SquaresFour size={14} weight="regular" />,
        },
        {
            key: 'top-area-share',
            label: t('website.valueLedger.overviewTopAreaShareMetric'),
            value: formatOverviewMetricPercent(overviewCoreScenarioTaskShare),
            tone: 'neutral',
            icon: <LibraryIcon name="dashboard" size={14} color="currentColor" />,
        },
    ];
    const overviewValueJudgmentTitle = data.overview.valueJudgment.title
        ? formatOutcomeGeneratedCopyText(data.overview.valueJudgment.title, locale)
        : t('website.valueLedger.overviewValueJudgmentTitle');
    const overviewValueJudgmentDescription = data.overview.valueJudgment.description
        ? formatOutcomeGeneratedCopyText(data.overview.valueJudgment.description, locale)
        : '-';
    const activeLedgerStatusLabel = hasGeneratedLedger
        ? t('website.valueLedger.lastUpdated', {
            date: formatDateTime(data.workspace.lastGeneratedAt, locale, data.workspace.timezone),
        })
        : t('website.valueLedger.waitingForFirstRefresh');
    const ledgerStatusLabel = isOutcomeSummaryWorkspaceExpired && hasOutcomeSummaryStarted
        ? t('website.valueLedger.dailyUpdatesOff')
        : isOutcomeSummaryPaused
        ? hasGeneratedLedger
            ? `${t('website.valueLedger.dailyUpdatesOff')} · ${activeLedgerStatusLabel}`
            : t('website.valueLedger.dailyUpdatesOff')
        : activeLedgerStatusLabel;
    const ledgerStatusDotClass = isOutcomeSummaryWorkspaceExpired
        ? 'bg-[#AAB4C4]'
        : isOutcomeSummaryPaused || isOutcomeSummaryGenerationPending
            ? 'bg-[#F79009]'
            : hasGeneratedLedger
                ? 'bg-[#12B76A]'
                : 'bg-[#AAB4C4]';

    useEffect(() => {
        if (!canViewThisOutcomeSummary) return undefined;
        if (outcomeSummarySettings.isLoading) return undefined;
        if (!canPollOutcomeSummaryGeneration) return undefined;
        if (typeof window === 'undefined') return undefined;

        let cancelled = false;

        const pollLedgerGeneration = () => {
            valueLedgerService.clearLedgerCache(currentWorkspace?.id);
            const requestGeneration = startLedgerRequestGeneration();
            valueLedgerService.getLedger(ledgerRequest)
                .then((response) => {
                    if (cancelled || !isLatestLedgerRequestGeneration(requestGeneration)) return;

                    const nextGenerationStatus = response.currentGeneration?.status;
                    if (response.status !== 'ready' && isOutcomeLedgerGenerationStatusInFlight(nextGenerationStatus)) {
                        return;
                    }

                    applyLedgerResponse(response);
                    setLoadError(null);
                })
                .catch((error) => {
                    if (cancelled || !isLatestLedgerRequestGeneration(requestGeneration)) return;
                    setLoadError(error instanceof Error ? error.message : 'Failed to load value ledger');
                });
        };

        pollLedgerGeneration();
        const timerId = window.setInterval(pollLedgerGeneration, OUTCOME_SUMMARY_GENERATION_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(timerId);
        };
    }, [
        applyLedgerResponse,
        canPollOutcomeSummaryGeneration,
        canViewThisOutcomeSummary,
        currentWorkspace?.id,
        isLatestLedgerRequestGeneration,
        ledgerRequest,
        outcomeSummarySettings.isLoading,
        startLedgerRequestGeneration,
    ]);

    const changeOutcomePage = useCallback((nextPage: number) => {
        const boundedPage = Math.min(Math.max(1, nextPage), totalOutcomePages);
        setOutcomePage(boundedPage);
        setPageJumpValue(String(boundedPage));

        if (typeof window === 'undefined') return;
        window.requestAnimationFrame(() => {
            listLayerRef.current?.scrollIntoView({ block: 'start' });
        });
    }, [totalOutcomePages]);

    const handleOutcomePageJump = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const requestedPage = Number.parseInt(pageJumpValue.trim(), 10);
        if (!Number.isFinite(requestedPage)) {
            setPageJumpValue(String(safeOutcomePage));
            return;
        }

        changeOutcomePage(requestedPage);
    }, [changeOutcomePage, pageJumpValue, safeOutcomePage]);

    useEffect(() => {
        if (isDetailOpen || !shouldRestoreListScrollRef.current) return undefined;

        shouldRestoreListScrollRef.current = false;
        if (typeof window === 'undefined') return undefined;

        const frameId = window.requestAnimationFrame(() => {
            restoreListScrollPosition();
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [isDetailOpen, restoreListScrollPosition]);

    useEffect(() => {
        if (!isDetailOpen) return undefined;
        if (typeof window === 'undefined') return undefined;

        const frameId = window.requestAnimationFrame(() => {
            detailLayerRef.current?.scrollIntoView({ block: 'start' });
            detailLayerRef.current
                ?.querySelector<HTMLElement>('[data-outcome-detail-scroll-body="true"]')
                ?.scrollTo({ top: 0, left: 0 });
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [isDetailOpen, selectedOutcomeKey]);

    useEffect(() => {
        if (isDetailOpen) return undefined;
        if (typeof window === 'undefined') return undefined;

        const frameId = window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [activeSummaryTab, isDetailOpen]);

    const selectRange = useCallback((rangeId: string) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('range_id', rangeId);
        setSearchParams(nextParams);
        setIsDropdownOpen(false);
    }, [searchParams, setSearchParams]);

    const rangePickerOptions = data.range.availableRanges.map((range) => {
        const isCurrentSubscriptionCycle = isCurrentSubscriptionCycleLabel(range.label, range.rangeId);
        const rangeStartAt = range.startAt || (isCurrentSubscriptionCycle
            ? fallbackCurrentCycleRange.startAt
            : (data.range.rangeStartAt || fallbackCurrentCycleRange.startAt));
        const rangeEndAt = range.endAt || (isCurrentSubscriptionCycle
            ? fallbackCurrentCycleRange.endAt
            : (data.range.rangeEndAt || fallbackCurrentCycleRange.endAt));
        const dateText = formatSubscriptionCycleDateRange(rangeStartAt, rangeEndAt, locale, data.workspace.timezone);
        const contextLabel = getRangeOptionContextLabel(range.label, locale, t);

        return {
            id: range.rangeId,
            title: contextLabel,
            subtitle: dateText === '--' ? '' : dateText,
            range,
        };
    });
    const ledgerRefreshTimezone = data.workspace.ledgerRefreshSchedule?.timezone
        ?? data.range.availableRanges.find((range) => range.rangeId === data.range.selectedRangeId)?.timezone
        ?? data.workspace.timezone
        ?? formatUtcOffset();
    const ledgerRefreshLocalTime = data.workspace.ledgerRefreshSchedule?.localTime
        ?? outcomeSummarySettings.settings.refreshLocalTime
        ?? '08:00';
    const ledgerRefreshDisplayTime = formatOutcomeSummaryRefreshDisplayTime(ledgerRefreshLocalTime, locale);
    const outcomeSummaryGateRefreshTime = formatOutcomeSummaryRefreshDisplayTime(
        outcomeSummarySettings.settings.refreshLocalTime,
        locale,
    );
    const ledgerRefreshSchedule = t('website.valueLedger.updateCadence', {
        time: ledgerRefreshDisplayTime,
        timezone: formatTimezoneOffsetLabel(ledgerRefreshTimezone),
    });
    const preparingOutcomeSummaryDescription = t('website.valueLedger.preparingOutcomeSummaryDescription');
    const preparingOutcomeSummarySteps = [
        t('website.valueLedger.preparingOutcomeSummaryStepReadTasks'),
        t('website.valueLedger.preparingOutcomeSummaryStepExtractEvidence'),
        t('website.valueLedger.preparingOutcomeSummaryStepGroupWorkflows'),
    ];
    const isOutcomeSummaryAccessLoading = permissions.isLoading || (canViewThisOutcomeSummary && outcomeSummarySettings.isLoading);
    const shouldShowPermissionDenied = !isOutcomeSummaryAccessLoading && !canViewThisOutcomeSummary;
    const shouldShowHeader = canViewThisOutcomeSummary && hasOutcomeSummaryStarted && !isActivatingTransition;
    const outcomeSummarySettingsError = outcomeSummarySettings.error && canManageThisOutcomeSummary
        ? t('website.valueLedger.settingsUpdateFailed')
        : null;
    const handleOutcomeSummaryEnabledChange = useCallback((enabled: boolean) => {
        // Source invariant check: outcomeSummarySettings.setEnabled(enabled)
        if (enabled && isOutcomeSummaryWorkspaceExpired) return;
        if (enabled) {
            valueLedgerService.clearLedgerCache(currentWorkspace?.id);
            const shouldShowActivationState = !hasOutcomeSummaryStarted && !ledgerData;
            if (shouldShowActivationState) {
                setIsActivatingTransition(true);
            }
            void outcomeSummarySettings.setEnabled(true)
                .then(() => {
                    if (shouldShowActivationState) {
                        setTimeout(() => {
                            setIsActivatingTransition(false);
                        }, 1500);
                    }
                })
                .catch(() => {
                    setIsActivatingTransition(false);
                });
        } else {
            void outcomeSummarySettings.setEnabled(false).catch(() => undefined);
        }
    }, [currentWorkspace?.id, hasOutcomeSummaryStarted, isOutcomeSummaryWorkspaceExpired, ledgerData, outcomeSummarySettings]);

    const handleConfirmDisable = useCallback(() => {
        setIsConfirmDisableOpen(false);
        // Source invariant check: outcomeSummarySettings.setEnabled(enabled)
        void outcomeSummarySettings.setEnabled(false).catch(() => undefined);
    }, [outcomeSummarySettings]);

    const handleExportPDF = useCallback(async () => {
        if (!ledgerData) return;
        setIsExporting(true);
        try {
            if ('fonts' in document) {
                await document.fonts.ready.catch(() => undefined);
            }
            await waitForPdfExportFrame();

            const element = pdfExportRef.current;
            if (!element) {
                console.error('Cannot find PDF export container');
                return;
            }

            const result = await snapdom(element, {
                scale: 2,
                backgroundColor: '#ffffff',
                embedFonts: true,
            });

            const canvas = await result.toCanvas();
            if (canvas.width <= 0 || canvas.height <= 0) {
                throw new Error('PDF export canvas is empty.');
            }
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const elementWidth = element.getBoundingClientRect().width || element.offsetWidth || PDF_EXPORT_WIDTH_PX;
            const canvasScale = imgWidth / elementWidth;

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageHeightPx = Math.floor((PDF_EXPORT_PAGE_HEIGHT_MM * imgWidth) / PDF_EXPORT_PAGE_WIDTH_MM);
            if (pageHeightPx <= 0) {
                throw new Error('PDF export page height is invalid.');
            }
            const safeBreakPositions = getPdfExportSafeBreakPositions(element)
                .map((position) => Math.round(position * canvasScale))
                .filter((position) => position > 0 && position <= imgHeight);

            const pageCanvas = document.createElement('canvas');
            const pageContext = pageCanvas.getContext('2d');
            if (!pageContext) {
                throw new Error('PDF export page canvas is unavailable.');
            }

            pageCanvas.width = imgWidth;
            let canvasOffsetY = 0;
            let pageIndex = 0;
            while (canvasOffsetY < imgHeight) {
                const sliceHeightPx = getNextPdfExportSliceHeight({
                    currentOffsetY: canvasOffsetY,
                    imageHeight: imgHeight,
                    pageHeight: pageHeightPx,
                    safeBreakPositions,
                });
                pageCanvas.height = sliceHeightPx;
                pageContext.fillStyle = '#ffffff';
                pageContext.fillRect(0, 0, imgWidth, sliceHeightPx);
                pageContext.drawImage(
                    canvas,
                    0,
                    canvasOffsetY,
                    imgWidth,
                    sliceHeightPx,
                    0,
                    0,
                    imgWidth,
                    sliceHeightPx,
                );

                if (pageIndex > 0) {
                    pdf.addPage();
                }

                const pageImageData = pageCanvas.toDataURL('image/jpeg', 0.95);
                const pageHeightMm = (sliceHeightPx * PDF_EXPORT_PAGE_WIDTH_MM) / imgWidth;
                pdf.addImage(pageImageData, 'JPEG', 0, 0, PDF_EXPORT_PAGE_WIDTH_MM, pageHeightMm);

                canvasOffsetY += sliceHeightPx;
                pageIndex += 1;
            }

            pdf.save(getOutcomeSummaryPdfFileName(ledgerData.workspace.workspaceName));
        } catch (error) {
            console.error('Failed to generate PDF:', error);
        } finally {
            setIsExporting(false);
        }
    }, [ledgerData]);

    const outcomeSummaryDailyUpdateControl = canManageThisOutcomeSummary ? (
        isOutcomeSummaryPaused ? (
            <div className="flex items-center gap-2">
                {outcomeSummarySettings.isSaving ? (
                    <CircleNotch size={14} weight="regular" className="animate-spin text-[#8A93B2]" />
                ) : (
                    <OutcomeSummarySwitch
                        checked={false}
                        disabled={!canUpdateOutcomeSummary || outcomeSummarySettings.isSaving}
                        label={t('website.valueLedger.resumeDailyUpdatesLabel')}
                        onChange={(checked) => {
                            if (checked && canUpdateOutcomeSummary) {
                                handleOutcomeSummaryEnabledChange(true);
                            }
                        }}
                    />
                )}
                <button
                    type="button"
                    onClick={() => handleOutcomeSummaryEnabledChange(true)}
                    disabled={!canUpdateOutcomeSummary || outcomeSummarySettings.isSaving}
                    className="text-[13px] font-medium text-[#666F8D] transition-colors hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {t('website.valueLedger.resumeAction')}
                </button>
            </div>
        ) : (
            <button
                type="button"
                onClick={() => setIsConfirmDisableOpen(true)}
                disabled={!canUpdateOutcomeSummary || outcomeSummarySettings.isSaving}
                className="flex items-center gap-1.5 text-[13px] font-medium text-[#666F8D] transition-colors hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50"
            >
                {outcomeSummarySettings.isSaving ? (
                    <CircleNotch size={14} weight="regular" className="animate-spin text-[#8A93B2]" />
                ) : null}
                <span>{t('website.valueLedger.disableAction')}</span>
            </button>
        )
    ) : null;

    const pageHeader = shouldShowHeader ? (
        <div className="flex min-h-9 items-center text-left">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-2">
                    <span className={clsx('h-1.5 w-1.5 rounded-full', ledgerStatusDotClass)} />
                    <span className="text-[13px] font-medium text-[#1A162F]">
                        {ledgerStatusLabel}
                    </span>
                    <Tooltip content={ledgerRefreshSchedule} side="bottom" align="start" sideOffset={8}>
                        <button
                            type="button"
                            aria-label={ledgerRefreshSchedule}
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#8A93B2] transition-colors hover:text-[#1A162F] focus:outline-none"
                        >
                            <Question size={14} weight="regular" />
                        </button>
                    </Tooltip>
                </div>
                {outcomeSummaryDailyUpdateControl}
            </div>
        </div>
    ) : null;
    const pageHeaderWithError = (pageHeader || outcomeSummarySettingsError) ? (
        <div className="flex flex-col gap-4">
            {pageHeader}
            {outcomeSummarySettingsError ? (
                <p className="text-[12px] font-normal leading-5 text-[#B42318]">
                    {outcomeSummarySettingsError}
                </p>
            ) : null}
        </div>
    ) : null;

    if (isOutcomeSummaryAccessLoading) {
        return (
            <div className="space-y-5 [overflow-anchor:none]">
                {pageHeaderWithError}
                <OutcomeSummaryLoadingSkeleton />
            </div>
        );
    }

    if (shouldShowPermissionDenied) {
        return (
            <div className="space-y-5 [overflow-anchor:none]">
                {pageHeaderWithError}
                <PermissionDeniedNotice
                    title={t('website.valueLedger.permissionDeniedTitle')}
                    description={t('website.valueLedger.permissionDeniedDescription')}
                />
            </div>
        );
    }

    if (isActivatingTransition) {
        return (
            <div className="space-y-5 [overflow-anchor:none]">
                <OutcomeSummaryPreparingState
                    title={t('website.valueLedger.startingOutcomeSummaryTitle')}
                    description={t('website.valueLedger.startingOutcomeSummaryDescription')}
                    steps={preparingOutcomeSummarySteps}
                />
            </div>
        );
    }

    // Source invariant check: if (!hasOutcomeSummaryStarted) {
    if (!hasOutcomeSummaryStarted) {
        return (
            <div className="space-y-5 [overflow-anchor:none]">
                {pageHeaderWithError}
                <OutcomeSummaryGateCard
                    canManage={canManageThisOutcomeSummary}
                    disabledReason={isOutcomeSummaryWorkspaceExpired
                        ? t('website.valueLedger.outcomeSummaryExpiredHint')
                        : null}
                    isSaving={outcomeSummarySettings.isSaving}
                    onChange={() => handleOutcomeSummaryEnabledChange(true)}
                    refreshLocalTime={outcomeSummaryGateRefreshTime}
                    t={t}
                />
            </div>
        );
    }

    if (isLoading && !ledgerData) {
        return (
            <div className="space-y-5 [overflow-anchor:none]">
                <div className="flex flex-col gap-2.5 text-left sm:flex-row sm:items-center sm:justify-between">
                    <OutcomeSummarySkeletonBar className="h-6 w-44" />
                    <div className="h-5 w-36 animate-pulse rounded bg-[#E8EBF3]" />
                </div>
                <OutcomeSummaryLoadingSkeleton />
            </div>
        );
    }

    if (shouldShowPendingRefreshData) {
        return (
            <div className="space-y-5 [overflow-anchor:none]">
                <OutcomeSummaryPreparingState
                    title={t('website.valueLedger.preparingOutcomeSummaryTitle')}
                    description={preparingOutcomeSummaryDescription}
                    steps={preparingOutcomeSummarySteps}
                />
            </div>
        );
    }

    if (loadError && !ledgerData) {
        return (
            <section className="flex min-h-[420px] items-center justify-center rounded-2xl border border-[#E8EBF3] bg-white px-6 py-10 text-center">
                <div className="max-w-md">
                    <ListChecks size={28} weight="regular" className="mx-auto text-[#AAB4C4]" />
                    <h1 className="mt-4 text-[18px] font-medium text-[#1A162F]">
                        {t('website.valueLedger.waitingForData')}
                    </h1>
                    <p className="mt-2 text-[13px] leading-6 text-[#667085]">
                        {loadError}
                    </p>
                </div>
            </section>
        );
    }

    const emptyOutcomeTitle = isEmptyLedger
        ? t('website.valueLedger.emptyTitle')
        : t('website.valueLedger.noMatchingOutcomes');
    const formatFilterCount = (count: number) => (
        formatOutcomeSummaryCount(count, shouldShowPendingRefreshData)
    );

    return (
        <div className="space-y-5 [overflow-anchor:none]">
            <div className={clsx(isDetailOpen ? 'hidden' : 'flex flex-col gap-4')}>
                {pageHeader}
                {outcomeSummarySettingsError ? (
                    <p className="text-[12px] font-normal leading-5 text-[#B42318]">
                        {outcomeSummarySettingsError}
                    </p>
                ) : null}

                <div className="flex items-center justify-between gap-3">
                    <DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                        <DropdownMenu.Trigger asChild>
                            <button
                                type="button"
                                className={clsx(rangePickerTriggerClass, 'flex w-full min-w-0 items-center justify-between gap-3 sm:w-auto sm:min-w-[340px]')}
                            >
                                <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                                    <span className="shrink-0 text-[11px] font-semibold text-[#4E586A]">{selectedRangeContextLabel}</span>
                                    {selectedRangeDisplayText ? (
                                        <>
                                            <span className="shrink-0 text-[#A3ABC0]">·</span>
                                            <span className="min-w-0 truncate text-[12px] font-semibold text-[#1A162F]">
                                                {selectedRangeDisplayText}
                                            </span>
                                        </>
                                    ) : null}
                                </span>
                                <CaretDown
                                    size={14}
                                    weight="bold"
                                    className={clsx(
                                        'shrink-0 text-[#8A93B2] transition-transform duration-150',
                                        isDropdownOpen && 'rotate-180 text-[#1A162F]',
                                    )}
                                />
                            </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content
                                align="start"
                                sideOffset={8}
                                collisionPadding={12}
                                className="z-[10002] w-[440px] max-w-[calc(100vw-24px)] rounded-xl border border-[#DDE3EA] bg-white p-1.5 shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                            >
                                <div className="space-y-0.5">
                                    {rangePickerOptions.map((option) => {
                                        const isActive = data.range.selectedRangeId === option.id;

                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => {
                                                    selectRange(option.id);
                                                }}
                                                className={clsx(
                                                    brandFocusClass,
                                                    'flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                                                    isActive
                                                        ? 'border-[#C7D2E4] bg-[#F4F7FF] text-[#1A162F] shadow-[0_1px_2px_rgba(26,22,47,0.04)]'
                                                        : 'border-transparent bg-white text-[#1A162F] hover:border-[#E3E8F1] hover:bg-[#F8FAFD]',
                                                )}
                                            >
                                                <span className="min-w-0">
                                                    <span className="block truncate text-[13px] font-semibold leading-5 text-[#1A162F]">
                                                        {option.title}
                                                    </span>
                                                    {option.subtitle ? (
                                                        <span className="mt-0.5 block truncate text-[11px] font-medium leading-4 text-[#667085]">
                                                            {option.subtitle}
                                                        </span>
                                                    ) : null}
                                                </span>
                                                {isActive ? <Check size={14} weight="bold" className="shrink-0 text-[#1A162F]" /> : null}
                                            </button>
                                        );
                                    })}
                                </div>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>

                    <button
                        type="button"
                        onClick={handleExportPDF}
                        disabled={isExporting}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A162F] px-4 py-2 text-[12px] font-bold text-white hover:bg-[#2B2745] transition-colors disabled:bg-[#8A93B2]/50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? (
                            <CircleNotch size={14} weight="regular" className="animate-spin text-white/70" />
                        ) : (
                            <DownloadSimple size={14} weight="bold" />
                        )}
                        <span>
                            {isChineseLocale(locale)
                                ? '下载本期概览 PDF'
                                : 'Download overview PDF'}
                        </span>
                    </button>
                </div>
            </div>

            {!isDetailOpen && (
                <div className="flex gap-1 border-b border-[#E8EBF3] mb-6">
                    {(['overview', 'ledger'] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveSummaryTab(tab)}
                            className={clsx(
                                'relative pb-3 px-1 mr-5 text-sm font-medium transition-colors focus:outline-none',
                                activeSummaryTab === tab ? 'text-[#1A162F]' : 'text-gray-400 hover:text-gray-700'
                            )}
                        >
                            {tab === 'overview'
                                ? (isChineseLocale(locale) ? '本期概览与洞察' : 'Overview & Insights')
                                : (isChineseLocale(locale) ? '成果明细清单' : 'Outcome Ledger')
                            }
                            {activeSummaryTab === tab && (
                                <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#1A162F]" />
                            )}
                        </button>
                    ))}
                </div>
            )}

            <section className="hidden" aria-hidden="true">
                <p>{t('website.valueLedger.subtitle', { workspaceName: data.workspace.workspaceName })}</p>
                <span className="mr-1 text-[12px] font-semibold text-[#7A8397]">
                    {t('website.valueLedger.statRange')}
                </span>
                <button
                    type="button"
                    className="rounded-lg border border-[#1A162F] bg-white px-4 py-2 text-left text-[12px] font-bold text-[#1A162F] shadow-sm"
                >
                    <span className="block">{localizeLedgerLabel(activeRange.label, locale)}</span>
                    <span className="block text-[10px] font-semibold text-[#667085]">{localizeLedgerLabel(data.workspace.ledgerTitle, locale)}</span>
                </button>
                {data.range.availableRanges
                    .filter((range) => range.rangeId !== activeRange.rangeId)
                    .slice(0, 2)
                    .map((range) => (
                        <button
                            key={range.rangeId}
                            type="button"
                            disabled
                            className="rounded-lg border border-[#E1E6EF] bg-white px-4 py-2 text-left text-[12px] font-bold text-[#1A162F] opacity-60"
                        >
                            <span className="block">{localizeLedgerLabel(range.label, locale)}</span>
                            <span className="block text-[10px] font-semibold text-[#7A8397]">{t('website.valueLedger.waitingForData')}</span>
                        </button>
                    ))}
            </section>

            <div className={clsx((isDetailOpen || activeSummaryTab !== 'overview') ? 'hidden' : 'block animate-in fade-in duration-200')}>
                {/* Hidden elements for Jest test compatibility */}
                <span className="hidden" aria-hidden="true">
                    {formatNumber(data.summary.memberTaskCount)} {formatCountUnit('task', locale)}
                    {' '}
                    {formatNumber(data.summary.bustlyOutcomeCount)} {formatCountUnit('outcome', locale)}
                </span>
                <OutcomeSummaryOverviewPanel
                    headline={overviewHeadline}
                    description={overviewDescription}
                    metricCards={overviewMetricCards}
                    valueJudgmentTitle={overviewValueJudgmentTitle}
                    valueJudgmentDescription={overviewValueJudgmentDescription}
                    taskAreas={overviewTaskAreas}
                    insights={data.overview.insights}
                    locale={locale}
                    t={t}
                />
            </div>

            {/* 1. List Page Layer */}
            <div
                ref={listLayerRef}
                className={clsx(
                    (isDetailOpen || activeSummaryTab !== 'ledger') ? 'hidden' : 'block animate-in fade-in duration-200',
                    'min-w-0 space-y-6',
                )}
            >
                <section className="min-h-[680px] min-w-0 overflow-hidden rounded-2xl border border-[#E8EBF3] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
                    <div className="space-y-5 px-5 py-4">
                        <div className="space-y-3">
                            <div className="space-y-1.5" aria-label={t('website.valueLedger.outcomeType')}>
                                <div className="flex items-center gap-2.5">
                                    <span className="block text-[12px] font-semibold leading-4 text-[#1A162F]">
                                        {t('website.valueLedger.outcomeType')}
                                    </span>
                                    <OutcomeTypeStandardsDialog
                                        locale={locale}
                                        activeOutcomeTypeLabel={selectedOutcomeType}
                                    />
                                </div>
                                <div className="flex min-h-[34px] flex-wrap gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedOutcomeType(null);
                                            setSelectedBusinessModule(null);
                                        }}
                                        className={clsx(
                                            compactFilterButtonBaseClass,
                                            selectedOutcomeType === null
                                                ? compactFilterButtonActiveClass
                                                : compactFilterButtonInactiveClass,
                                        )}
                                    >
                                        {t('website.valueLedger.allOutcomeTypes')} {formatFilterCount(outcomeTypeAllCount)}
                                    </button>
                                    {displayedOutcomeTypeCounts.map((item) => {
                                        const isActive = selectedOutcomeType === item.label;
                                        return (
                                            <button
                                                key={item.label}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedOutcomeType(item.label);
                                                    setSelectedBusinessModule(null);
                                                }}
                                                className={clsx(
                                                    compactFilterButtonBaseClass,
                                                    isActive
                                                        ? compactFilterButtonActiveClass
                                                        : compactFilterButtonInactiveClass,
                                                )}
                                            >
                                                {localizeLedgerLabel(item.label, locale)}
                                                {' '}
                                                {formatFilterCount(item.count)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="space-y-1.5" aria-label={t('website.valueLedger.businessModule')}>
                                <span className="block text-[12px] font-semibold leading-4 text-[#1A162F]">
                                    {t('website.valueLedger.businessModule')}
                                </span>
                                <div className="flex min-h-[34px] flex-wrap gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedBusinessModule(null)}
                                        className={clsx(
                                            compactFilterButtonBaseClass,
                                            selectedBusinessModule === null
                                                ? compactFilterButtonActiveClass
                                                : compactFilterButtonInactiveClass,
                                        )}
                                    >
                                        {t('website.valueLedger.allBusinessAreas')} {formatFilterCount(businessModuleAllCount)}
                                    </button>
                                    {displayedBusinessModuleCounts.map((item) => {
                                        const isActive = selectedBusinessModule === item.label;
                                        return (
                                            <button
                                                key={item.label}
                                                type="button"
                                                onClick={() => setSelectedBusinessModule(item.label)}
                                                className={clsx(
                                                    compactFilterButtonBaseClass,
                                                    isActive
                                                        ? compactFilterButtonActiveClass
                                                        : compactFilterButtonInactiveClass,
                                                )}
                                            >
                                                {localizeLedgerLabel(item.label, locale)}
                                                {' '}
                                                {formatFilterCount(item.count)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="min-w-0 border-t border-[#F1F4F9] pt-5">
                            <div className="grid min-w-0 max-w-full grid-cols-1 gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                                {paginatedOutcomes.map((outcome) => {
                                    const outcomeKey = getOutcomeKey(outcome);
                                    const isSelected = Boolean(
                                        isDetailOpen
                                        && selectedOutcome
                                        && getOutcomeKey(selectedOutcome) === outcomeKey,
                                    );
                                    const isViewed = viewedOutcomeKeys.has(outcomeKey);
                                    return (
                                        <div key={outcomeKey} className="flex h-full min-w-0 max-w-full flex-col">
                                            <OutcomeCard
                                                outcome={outcome}
                                                isSelected={isSelected}
                                                isViewed={isViewed}
                                                onClick={() => openOutcomeDetail(outcomeKey)}
                                                locale={locale}
                                                t={t}
                                                timezone={data.workspace.timezone}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            {totalOutcomeRows === 0 && (
                                <div className="px-5 py-14 text-center bg-[#F8F9FC] rounded-xl border border-[#E8EBF3]">
                                    {isEmptyLedger ? (
                                        <ListChecks size={24} className="mx-auto mb-2 text-[#8A93B2]" />
                                    ) : (
                                        <MagnifyingGlass size={24} className="mx-auto mb-2 text-[#8A93B2]" />
                                    )}
                                    <p className="text-[13px] font-medium text-[#8A93B2]">
                                        {emptyOutcomeTitle}
                                    </p>
                                    {isEmptyLedger ? (
                                        <p className="mx-auto mt-2 max-w-[360px] text-[12px] leading-5 text-[#667085]">
                                            {t('website.valueLedger.emptyDescription')}
                                        </p>
                                    ) : null}
                                </div>
                            )}
                            {totalOutcomeRows > OUTCOME_SUMMARY_ITEMS_PER_PAGE ? (
                                <div className="mt-6 flex flex-col gap-3 border-t border-[#F1F4F9] pt-4 lg:flex-row lg:items-center lg:justify-between">
                                    <span className="text-[12px] font-normal text-[#667085]">
                                        {t('website.valueLedger.paginationRange', {
                                            start: formatNumber(
                                                (safeOutcomePage - 1) * OUTCOME_SUMMARY_ITEMS_PER_PAGE + 1,
                                            ),
                                            end: formatNumber(Math.min(
                                                safeOutcomePage * OUTCOME_SUMMARY_ITEMS_PER_PAGE,
                                                totalOutcomeRows,
                                            )),
                                            total: formatNumber(totalOutcomeRows),
                                        })}
                                    </span>
                                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => changeOutcomePage(safeOutcomePage - 1)}
                                                disabled={safeOutcomePage === 1}
                                                aria-label={t('website.valueLedger.previousPage')}
                                                title={t('website.valueLedger.previousPage')}
                                                className={clsx(
                                                    brandFocusClass,
                                                    'rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50',
                                                )}
                                            >
                                                <CaretLeft size={16} weight="regular" />
                                            </button>
                                            <div className="flex items-center gap-1">
                                                {outcomePaginationItems.map((item) => {
                                                    if (typeof item !== 'number') {
                                                        return (
                                                            <span
                                                                key={item}
                                                                aria-hidden="true"
                                                                className="flex h-8 w-6 items-center justify-center text-[12px] font-medium text-[#A0A8C0]"
                                                            >
                                                                ...
                                                            </span>
                                                        );
                                                    }

                                                    return (
                                                        <button
                                                            key={item}
                                                            type="button"
                                                            onClick={() => changeOutcomePage(item)}
                                                            aria-current={safeOutcomePage === item ? 'page' : undefined}
                                                            aria-label={t('website.valueLedger.paginationPage', {
                                                                current: formatNumber(item),
                                                                total: formatNumber(totalOutcomePages),
                                                            })}
                                                            className={clsx(
                                                                brandFocusClass,
                                                                'h-8 w-8 rounded-lg text-[12px] font-medium transition-colors',
                                                                safeOutcomePage === item
                                                                    ? 'bg-[#1A162F] text-white'
                                                                    : 'text-[#667085] hover:bg-[#F4F5F8] hover:text-[#1A162F]',
                                                            )}
                                                        >
                                                            {formatNumber(item)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => changeOutcomePage(safeOutcomePage + 1)}
                                                disabled={safeOutcomePage === totalOutcomePages}
                                                aria-label={t('website.valueLedger.nextPage')}
                                                title={t('website.valueLedger.nextPage')}
                                                className={clsx(
                                                    brandFocusClass,
                                                    'rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50',
                                                )}
                                            >
                                                <CaretRight size={16} weight="regular" />
                                            </button>
                                        </div>
                                        <form
                                            onSubmit={handleOutcomePageJump}
                                            className="flex items-center gap-1.5 text-[12px]"
                                        >
                                            <label
                                                htmlFor="outcome-page-jump"
                                                className="shrink-0 font-medium text-[#667085]"
                                            >
                                                {t('website.valueLedger.jumpToPage')}
                                            </label>
                                            <input
                                                id="outcome-page-jump"
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={pageJumpValue}
                                                onChange={(event) => setPageJumpValue(event.target.value.replace(/\D/gu, ''))}
                                                aria-label={t('website.valueLedger.pageNumber')}
                                                className={clsx(
                                                    brandFocusClass,
                                                    'h-8 w-14 rounded-lg border border-[#DDE3EA] bg-white px-2 text-center font-medium text-[#1A162F] transition-colors placeholder:text-[#A0A8C0] hover:border-[#BFC6D4] focus:border-[#1A162F]',
                                                )}
                                            />
                                            <span className="shrink-0 font-medium text-[#8A93B2]">
                                                /
                                                {' '}
                                                {formatNumber(totalOutcomePages)}
                                            </span>
                                            <button
                                                type="submit"
                                                disabled={pageJumpValue.trim().length === 0}
                                                className={clsx(
                                                    brandFocusClass,
                                                    'h-8 rounded-lg border border-[#DDE3EA] bg-white px-2.5 font-medium text-[#667085] transition-colors hover:border-[#BFC6D4] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-45',
                                                )}
                                            >
                                                {t('website.valueLedger.goToPage')}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </div>

            {/* 2. Detail Page Layer */}
            <div ref={detailLayerRef} className={clsx(isDetailOpen ? 'block' : 'hidden', "space-y-4")}>
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={returnToList}
                        className="group flex items-center gap-2 rounded-lg border border-[#E8EBF3] bg-white px-4 py-2 text-[13px] font-medium text-[#666F8D] transition-all hover:border-[#C8D0E2] hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                    >
                        <ArrowRight size={16} weight="bold" className="rotate-180 transition-transform group-hover:-translate-x-0.5" />
                        <span>{t('website.valueLedger.backToList')}</span>
                    </button>
                </div>

                <div className="overflow-hidden rounded-lg border border-[#E8EBF3] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
                    <OutcomeDetailWorkbenchPanel
                        outcome={selectedOutcome}
                        isDetailLoading={Boolean(
                            selectedOutcome?.outcomeId
                            && loadingOutcomeDetailId === selectedOutcome.outcomeId,
                        )}
                        detailLoadError={selectedOutcome?.outcomeId
                            ? outcomeDetailErrorById[selectedOutcome.outcomeId] ?? null
                            : null}
                        activeTab={activeDetailTab}
                        onTabChange={setActiveDetailTab}
                        locale={locale}
                        t={t}
                        timezone={data.workspace.timezone}
                    />
                </div>
            </div>

            <Modal
                isOpen={isConfirmDisableOpen}
                onClose={() => setIsConfirmDisableOpen(false)}
                title={t('website.valueLedger.disableDialogTitle')}
                width="max-w-[400px]"
                headerClassName="!pb-2"
                bodyClassName="px-6 pb-6 pt-0"
                noPadding
            >
                <div className="space-y-4">
                    <p className="text-[13px] font-normal leading-6 text-[#667085]">
                        {t('website.valueLedger.disableDialogDescription')}
                    </p>
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => setIsConfirmDisableOpen(false)}
                            className="rounded-lg border border-[#DDE3EA] bg-white px-3.5 py-2 text-[12px] font-medium text-[#667085] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmDisable}
                            className="rounded-lg bg-[#DC2626] px-3.5 py-2 text-[12px] font-medium text-white transition-colors hover:bg-[#B91C1C]"
                        >
                            {t('website.valueLedger.disableDialogConfirm')}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* PDF export renders Overview & Insights data into the report template. */}
            {ledgerData && (
                <div aria-hidden="true" style={pdfExportShellStyle}>
                    <div
                        ref={pdfExportRef}
                        style={{
                            width: `${PDF_EXPORT_WIDTH_PX}px`,
                            backgroundColor: '#FFFFFF',
                            boxSizing: 'border-box',
                            position: 'relative',
                        }}
                    >
                        <OutcomeSummaryPdfReport
                            workspaceName={data.workspace.workspaceName}
                            reportOwner={profile?.userInfo?.userName || profile?.userInfo?.email || 'Bustly'}
                            updatedAt={formatDateTime(data.workspace.lastGeneratedAt, locale, data.workspace.timezone)}
                            rangeContextLabel={selectedRangeContextLabel}
                            rangeDisplayText={selectedRangeDisplayText}
                            headline={overviewHeadline}
                            description={overviewDescription}
                            metricCards={overviewMetricCards}
                            valueJudgmentTitle={overviewValueJudgmentTitle}
                            valueJudgmentDescription={overviewValueJudgmentDescription}
                            taskAreas={overviewTaskAreas}
                            insights={data.overview.insights}
                            locale={locale}
                            t={t}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ValueLedgerPage;
